import { useState, useEffect, useCallback } from "react";
import {
  Users, DollarSign, Zap, Link2, Plus, Copy, Trash2, RefreshCw,
  Settings, BarChart3, LogOut, ChevronDown, ChevronRight, Loader2,
  AlertCircle, AlertTriangle, Check, Shield, CreditCard, Clock, TrendingUp,
  Package, Pencil, X, Save, Receipt, FileText, ArrowUpDown,
  MessageSquare, Send, ArrowLeft, Filter, Search, Ban, RotateCcw,
  UserX, UserCheck, Globe, Eye, Newspaper, Repeat2, Activity,
  TrendingDown, UserPlus, Layers
} from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";

interface SaasAdminDashboardProps {
  admin: { id: number; username: string; displayName: string | null; role: string };
  onLogout: () => void;
}

type Tab = "overview" | "packages" | "billing" | "users" | "support" | "waitlist" | "ai-controls" | "api-connections" | "staff" | "website" | "chaos-pools" | "scheduler" | "audit" | "content" | "budget-burn";

export default function SaasAdminDashboard({ admin, onLogout }: SaasAdminDashboardProps) {
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  const baseTabs = [
    { id: "overview" as Tab, label: "Overview", icon: BarChart3 },
    { id: "packages" as Tab, label: "Packages", icon: Package },
    { id: "billing" as Tab, label: "Billing", icon: Receipt },
    { id: "users" as Tab, label: "Users", icon: Users },
    { id: "support" as Tab, label: "Support", icon: MessageSquare },
    { id: "waitlist" as Tab, label: "Waitlist", icon: Clock },
    { id: "ai-controls" as Tab, label: "AI Controls", icon: Settings },
    { id: "api-connections" as Tab, label: "API Connections", icon: Link2 },
    { id: "website" as Tab, label: "Website", icon: Globe },
    // Chaos Pools is archived (2026-11-02): its tab shows only in a VITE_POOLS_ENABLED=true build
    ...(import.meta.env.VITE_POOLS_ENABLED === "true" ? [{ id: "chaos-pools" as Tab, label: "Chaos Pools", icon: Activity }] : []),
    { id: "scheduler" as Tab, label: "Scheduler", icon: Activity },
    { id: "audit" as Tab, label: "Audit Log", icon: Shield },
    { id: "content" as Tab, label: "Pipeline", icon: Layers },
    { id: "budget-burn" as Tab, label: "Budget Burn", icon: TrendingUp },
  ];
  const tabs = (admin.role === "owner"
    ? [...baseTabs, { id: "staff" as Tab, label: "Staff", icon: Users }]
    : baseTabs).filter(tab => {
      if (admin.role === "owner") return true;
      const staff = (admin as any).staffRecord;
      if (!staff || !staff.permissions) return true;
      return staff.permissions[tab.id] !== false;
    });

  const isOwner = admin.role === "owner";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="saas-admin-dashboard">
      <header className="border-b border-foreground/[0.06] bg-background/50 backdrop-blur-2xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <Zap size={18} className="text-foreground" />
            </div>
            <div className="flex items-baseline gap-0.5">
              <span className="text-xl font-display font-black tracking-tight text-foreground">Drpr</span>
              <span className="text-xl font-display font-black tracking-tight text-blue-400">.ai</span>
            </div>
            <span className="text-[10px] font-bold text-foreground/20 uppercase tracking-[0.15em] ml-2 hidden sm:block">SaaS Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground/40" data-testid="text-admin-name">{admin.displayName || admin.username}</span>
              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-400" data-testid="text-admin-role">{admin.role}</span>
            </div>
            <button
              onPointerDown={onLogout}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-foreground/30 hover:text-red-400 hover:bg-red-500/[0.06] transition-all text-sm"
              data-testid="button-admin-logout"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onPointerDown={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? "bg-blue-500/[0.1] text-blue-400 border border-blue-500/20"
                  : "text-foreground/35 hover:text-foreground/60 hover:bg-foreground/[0.03] border border-transparent"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
          {isOwner && (
            <Link
              href="/qa"
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all text-foreground/35 hover:text-blue-400 hover:bg-blue-500/[0.05] border border-transparent hover:border-blue-500/20"
              data-testid="tab-qa-dashboard"
            >
              <Shield size={16} />
              QA Dashboard
            </Link>
          )}
        </div>

        {activeTab === "overview" && <OverviewTab />}
        {activeTab === "packages" && <PackagesTab />}
        {activeTab === "billing" && <BillingTab />}
        {activeTab === "users" && <UsersTab />}
        {activeTab === "support" && <SupportTab />}
        {activeTab === "waitlist" && <WaitlistTab />}
        {activeTab === "ai-controls" && <AiControlsTab />}
        {activeTab === "api-connections" && <ApiConnectionsTab />}
        {activeTab === "website" && <WebsiteTab />}
        {activeTab === "chaos-pools" && <ChaosPoolsTab />}
        {activeTab === "scheduler" && <SchedulerTab />}
        {activeTab === "audit" && <AuditLogTab />}
        {activeTab === "content" && <ContentPipelineTab />}
        {activeTab === "budget-burn" && <BudgetBurnTab />}
        {activeTab === "staff" && admin.role === "owner" && <StaffTab />}
      </div>
    </div>
  );
}

function TrendBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="flex-1 flex flex-col items-center gap-1">
      <div className="w-full bg-foreground/[0.04] rounded-sm h-16 flex items-end overflow-hidden">
        <div className={`w-full ${color} rounded-sm transition-all duration-300`} style={{ height: `${Math.max(2, pct)}%` }} />
      </div>
    </div>
  );
}

function MiniLineChart({ data, dataKey, color, label, formatter }: { data: any[]; dataKey: string; color: string; label: string; formatter?: (v: number) => string }) {
  if (!data || data.length === 0) return null;
  const values = data.map((d) => Number(d[dataKey]) || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const w = 280;
  const h = 80;
  const points = values.map((v, i) => ({
    x: (i / (values.length - 1)) * w,
    y: h - ((v - min) / range) * (h - 10) - 5,
  }));
  const pathD = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(" ");
  const areaD = `${pathD} L ${w} ${h} L 0 ${h} Z`;
  const total = values.reduce((s, v) => s + v, 0);
  const latest = values[values.length - 1];
  const fmt = formatter || ((v: number) => String(v));

  return (
    <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid={`chart-${dataKey}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">{label}</span>
        <span className="text-lg font-display font-black text-foreground">{fmt(total)}</span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
        <defs>
          <linearGradient id={`grad-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.2" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaD} fill={`url(#grad-${dataKey})`} />
        <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.length > 0 && (
          <circle cx={points[points.length - 1].x} cy={points[points.length - 1].y} r="3" fill={color} />
        )}
      </svg>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[10px] text-foreground/20">{data[0]?.label || ""}</span>
        <span className="text-[10px] text-foreground/30">Latest: {fmt(latest)}</span>
        <span className="text-[10px] text-foreground/20">{data[data.length - 1]?.label || ""}</span>
      </div>
    </div>
  );
}

function OverviewTab() {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState<any[]>([]);
  const [trendsLoading, setTrendsLoading] = useState(true);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [activeMetric, setActiveMetric] = useState<string>("newUsers");
  const [triggeringBrief, setTriggeringBrief] = useState(false);
  const [briefResult, setBriefResult] = useState<string | null>(null);
  const [purgingQa, setPurgingQa] = useState(false);
  const [purgeResult, setPurgeResult] = useState<string | null>(null);
  const [workspaceHealth, setWorkspaceHealth] = useState<any[]>([]);
  const [kpi, setKpi] = useState<any>(null);
  const [signupTrend, setSignupTrend] = useState<any[]>([]);
  const [contentTrend, setContentTrend] = useState<any[]>([]);
  const [leadTrend, setLeadTrend] = useState<any[]>([]);
  const [aiCostTrend, setAiCostTrend] = useState<any[]>([]);
  const [revenueTrend, setRevenueTrend] = useState<any[]>([]);
  const [activeWorkspacesTrend, setActiveWorkspacesTrend] = useState<any[]>([]);
  const [conversionTrend, setConversionTrend] = useState<any[]>([]);
  const [churnTrend, setChurnTrend] = useState<any[]>([]);
  const [arpuTrend, setArpuTrend] = useState<any[]>([]);
  const [agentRunTrend, setAgentRunTrend] = useState<any[]>([]);
  const [widgetConvTrend, setWidgetConvTrend] = useState<any[]>([]);
  const [contentCreationTrend, setContentCreationTrend] = useState<any[]>([]);
  const [nurtureActivityTrend, setNurtureActivityTrend] = useState<any[]>([]);
  const [harryIntelligenceTrend, setHarryIntelligenceTrend] = useState<any[]>([]);
  const [chatActivityTrend, setChatActivityTrend] = useState<any[]>([]);
  const [emailOutboxTrend, setEmailOutboxTrend] = useState<any[]>([]);
  const [campaignLaunchTrend, setCampaignLaunchTrend] = useState<any[]>([]);
  const [memoryFormationTrend, setMemoryFormationTrend] = useState<any[]>([]);
  const [loginActivityTrend, setLoginActivityTrend] = useState<any[]>([]);
  const [platformHealth, setPlatformHealth] = useState<any>(null);

  const triggerDailyBrief = async () => {
    setTriggeringBrief(true);
    setBriefResult(null);
    try {
      const res = await fetch("/api/saas-admin/trigger-daily-brief", { method: "POST", credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        setBriefResult("Brief generation started — check the Website tab in ~60 seconds.");
      } else {
        setBriefResult(`Failed: ${data.error || data.message || "Unknown error"}`);
      }
    } catch (e: any) {
      setBriefResult(`Error: ${e.message}`);
    } finally {
      setTriggeringBrief(false);
    }
  };

  const purgeQaArtifacts = async () => {
    if (!confirm("Delete QA test artifacts (HACKED content, Attack Campaign $99,999) and regenerate API keys for workspaces 1 & 4?")) return;
    setPurgingQa(true);
    setPurgeResult(null);
    try {
      const res = await fetch("/api/saas-admin/purge-qa-artifacts", { method: "POST", credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        setPurgeResult(`Done — ${data.message || "QA artifacts removed, API keys regenerated."}`);
      } else {
        setPurgeResult(`Failed: ${data.error || data.message || "Unknown error"}`);
      }
    } catch (e: any) {
      setPurgeResult(`Error: ${e.message}`);
    } finally {
      setPurgingQa(false);
    }
  };

  useEffect(() => {
    fetch("/api/saas-admin/stats", { credentials: "include" })
      .then((r) => r.json())
      .then(setStats)
      .finally(() => setLoading(false));

    fetch("/api/saas-admin/analytics/trends", { credentials: "include" })
      .then((r) => r.json())
      .then(setTrends)
      .finally(() => setTrendsLoading(false));

    fetch("/api/saas-admin/workspace-health", { credentials: "include" })
      .then((r) => r.json())
      .then(setWorkspaceHealth)
      .catch(() => {});

    fetch('/api/saas-admin/kpi-summary', { credentials: 'include' })
      .then((r) => r.json())
      .then(setKpi)
      .catch(() => {});

    fetch('/api/saas-admin/signup-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setSignupTrend)
      .catch(() => {});

    fetch('/api/saas-admin/content-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setContentTrend)
      .catch(() => {});

    fetch('/api/saas-admin/lead-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setLeadTrend)
      .catch(() => {});

    fetch('/api/saas-admin/ai-cost-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setAiCostTrend)
      .catch(() => {});

    fetch('/api/saas-admin/revenue-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setRevenueTrend)
      .catch(() => {});

    fetch('/api/saas-admin/active-workspaces-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setActiveWorkspacesTrend)
      .catch(() => {});

    fetch('/api/saas-admin/conversion-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setConversionTrend)
      .catch(() => {});

    fetch('/api/saas-admin/churn-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setChurnTrend)
      .catch(() => {});
    fetch('/api/saas-admin/arpu-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setArpuTrend)
      .catch(() => {});
    fetch('/api/saas-admin/agent-run-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setAgentRunTrend)
      .catch(() => {});
    fetch('/api/saas-admin/widget-conv-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setWidgetConvTrend)
      .catch(() => {});
    fetch('/api/saas-admin/content-creation-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setContentCreationTrend)
      .catch(() => {});
    fetch('/api/saas-admin/nurture-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setNurtureActivityTrend)
      .catch(() => {});
    fetch('/api/saas-admin/harry-intelligence-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setHarryIntelligenceTrend)
      .catch(() => {});
    fetch('/api/saas-admin/chat-activity-trend', { credentials: 'include' })
      .then((r) => r.json())
      .then(setChatActivityTrend)
      .catch(() => {});
    fetch('/api/saas-admin/email-outbox-trend', { credentials: 'include' })
      .then(r => r.json())
      .then(setEmailOutboxTrend)
      .catch(() => {});
    fetch('/api/saas-admin/campaign-launch-trend', { credentials: 'include' })
      .then(r => r.json())
      .then(setCampaignLaunchTrend)
      .catch(() => {});
    fetch('/api/saas-admin/memory-formation-trend', { credentials: 'include' })
      .then(r => r.json())
      .then(setMemoryFormationTrend)
      .catch(() => {});
    fetch('/api/saas-admin/login-activity-trend', { credentials: 'include' }).then(r => r.json()).then(setLoginActivityTrend).catch(() => {});
    fetch('/api/saas-admin/platform-health-score', { credentials: 'include' })
      .then(r => r.json()).then(setPlatformHealth).catch(() => {});
  }, []);

  const loadRecommendations = () => {
    setRecsLoading(true);
    fetch("/api/saas-admin/analytics/recommendations", { credentials: "include" })
      .then((r) => r.json())
      .then(setRecommendations)
      .finally(() => setRecsLoading(false));
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>;

  const cards = [
    { label: "Total Users", value: stats?.totalUsers ?? 0, icon: Users, color: "blue" },
    { label: "Total Workspaces", value: stats?.totalWorkspaces ?? 0, icon: Shield, color: "purple" },
    { label: "Content Items", value: stats?.totalContentItems ?? 0, icon: BarChart3, color: "emerald" },
    { label: "AI Calls", value: stats?.totalAiCalls ?? 0, icon: Zap, color: "amber" },
    { label: "AI Base Cost", value: `$${(stats?.totalBaseCost ?? 0).toFixed(4)}`, icon: DollarSign, color: "red" },
    { label: "AI Billed (w/ margin)", value: `$${(stats?.totalAiCost ?? 0).toFixed(4)}`, icon: TrendingUp, color: "emerald" },
    { label: "Repurposed Posts", value: stats?.totalRepurposedPosts ?? 0, icon: Repeat2, color: "purple" },
  ];

  const colorMap: Record<string, string> = {
    blue: "from-blue-500/10 to-transparent border-blue-500/10 text-blue-400",
    purple: "from-purple-500/10 to-transparent border-purple-500/10 text-purple-400",
    emerald: "from-emerald-500/10 to-transparent border-emerald-500/10 text-emerald-400",
    amber: "from-amber-500/10 to-transparent border-amber-500/10 text-amber-400",
    red: "from-red-500/10 to-transparent border-red-500/10 text-red-400",
  };

  const metricOptions = [
    { key: "newUsers", label: "New Users", color: "bg-blue-500" },
    { key: "contentCreated", label: "Content", color: "bg-emerald-500" },
    { key: "aiCalls", label: "AI Calls", color: "bg-amber-500" },
    { key: "aiCost", label: "AI Cost", color: "bg-red-500" },
    { key: "revenue", label: "Revenue", color: "bg-purple-500" },
  ];

  const maxVal = Math.max(...trends.map((t) => Number(t[activeMetric]) || 0), 1);
  const currentMetricColor = metricOptions.find((m) => m.key === activeMetric)?.color || "bg-blue-500";

  const priorityColor: Record<string, string> = {
    high: "bg-red-500/10 text-red-400 border-red-500/15",
    medium: "bg-amber-500/10 text-amber-400 border-amber-500/15",
    low: "bg-emerald-500/10 text-emerald-400 border-emerald-500/15",
  };

  return (
    <div data-testid="overview-tab">
      {platformHealth && !platformHealth.timedOut && (
        <div className='mb-4 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>Platform Health Score</h3>
          <div className='flex items-center gap-5'>
            <div className={`text-5xl font-black tabular-nums ${
              platformHealth.score >= 70 ? 'text-emerald-400' :
              platformHealth.score >= 40 ? 'text-amber-400' : 'text-red-400'
            }`}>{platformHealth.score}<span className='text-lg font-normal text-foreground/30'>/100</span></div>
            <div className='flex-1 grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-foreground/50'>
              <span>Active workspaces: <b className='text-foreground/70'>{platformHealth.activeRatio}%</b></span>
              <span>Content output: <b className='text-foreground/70'>{platformHealth.contentScore}/100</b></span>
              <span>Lead capture: <b className='text-foreground/70'>{platformHealth.leadScore}/100</b></span>
              <span>Cost efficiency: <b className='text-foreground/70'>{platformHealth.costEfficiency}/100</b></span>
            </div>
          </div>
          <div className='mt-3 h-2 rounded-full bg-foreground/10 overflow-hidden'>
            <div className={`h-full rounded-full ${
              platformHealth.score >= 70 ? 'bg-emerald-400' :
              platformHealth.score >= 40 ? 'bg-amber-400' : 'bg-red-400'
            }`} style={{ width: platformHealth.score + '%' }} />
          </div>
        </div>
      )}
      <h2 className="text-2xl font-display font-bold mb-6">Platform Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card) => (
          <div key={card.label} className={`bg-gradient-to-br ${colorMap[card.color]} border rounded-2xl p-5`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="text-[11px] font-semibold text-foreground/35 uppercase tracking-wider">{card.label}</span>
              <card.icon size={18} className={colorMap[card.color].split(" ").pop()} />
            </div>
            <span className="text-2xl font-bold text-foreground">{card.value}</span>
          </div>
        ))}
      </div>

      {kpi && (
        <div className="mt-4 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4">
          <h3 className="text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3">Business Pulse</h3>
          <div className="flex flex-wrap gap-3">
            {([
              { label: 'Paid', value: kpi.paidWorkspaces, color: 'text-emerald-400' },
              { label: 'Free', value: kpi.freeWorkspaces, color: 'text-foreground/60' },
              { label: 'Active 30d', value: kpi.activeWorkspaces30d, color: 'text-blue-400' },
              { label: 'Leads 30d', value: kpi.totalLeads30d, color: 'text-purple-400' },
              { label: 'Churn Risk', value: kpi.churnRiskCount, color: kpi.churnRiskCount > 0 ? 'text-red-400' : 'text-foreground/40' },
            ] as { label: string; value: number; color: string }[]).map((m) => (
              <div key={m.label} className="flex flex-col items-center bg-foreground/[0.03] border border-foreground/[0.06] rounded-xl px-4 py-3 min-w-[90px]">
                <span className={`text-xl font-bold ${m.color}`}>{m.value}</span>
                <span className="text-[10px] text-foreground/35 uppercase tracking-wider mt-1">{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {signupTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>14-Day Signup Trend</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxS = Math.max(...signupTrend.map((x: any) => x.signups), 1);
              return signupTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.signups / maxS) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-blue-400/60 rounded-sm hover:bg-blue-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.signups + ' signup' + (d.signups !== 1 ? 's' : '')}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 14 days</p>
        </div>
      )}

      {contentTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Content Output</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxC = Math.max(...contentTrend.map((x: any) => x.published), 1);
              return contentTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.published / maxC) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-emerald-400/60 rounded-sm hover:bg-emerald-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.published + ' post' + (d.published !== 1 ? 's' : '')}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days</p>
        </div>
      )}

      {leadTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Lead Capture</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxL = Math.max(...leadTrend.map((x: any) => x.leads), 1);
              return leadTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.leads / maxL) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-purple-400/60 rounded-sm hover:bg-purple-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.leads + ' lead' + (d.leads !== 1 ? 's' : '')}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days</p>
        </div>
      )}

      {aiCostTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day AI Cost</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxA = Math.max(...aiCostTrend.map((x: any) => x.cost), 0.0001);
              return aiCostTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.cost / maxA) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-amber-400/60 rounded-sm hover:bg-amber-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': $' + d.cost.toFixed(4)}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (USD)</p>
        </div>
      )}

      {revenueTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Revenue</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxR = Math.max(...revenueTrend.map((x: any) => x.revenue), 0.0001);
              return revenueTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.revenue / maxR) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-green-400/60 rounded-sm hover:bg-green-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': $' + d.revenue.toFixed(2)}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (USD)</p>
        </div>
      )}

      {activeWorkspacesTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Active Workspaces</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxW = Math.max(...activeWorkspacesTrend.map((x: any) => x.activeWorkspaces), 1);
              return activeWorkspacesTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.activeWorkspaces / maxW) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-cyan-400/60 rounded-sm hover:bg-cyan-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.activeWorkspaces + ' workspace' + (d.activeWorkspaces !== 1 ? 's' : '')}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (distinct workspaces with agent runs)</p>
        </div>
      )}

      {conversionTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Paid Conversions</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxP = Math.max(...conversionTrend.map((x: any) => x.conversions), 1);
              return conversionTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.conversions / maxP) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-orange-400/60 rounded-sm hover:bg-orange-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.conversions + ' conversion' + (d.conversions !== 1 ? 's' : '')}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (paid invoices)</p>
        </div>
      )}

      {churnTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Workspace Churn</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxC = Math.max(...churnTrend.map((x: any) => x.churned), 1);
              return churnTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.churned / maxC) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-red-400/60 rounded-sm hover:bg-red-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.churned + ' churned'}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (soft-deleted workspaces)</p>
        </div>
      )}

      {arpuTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Avg Revenue Per User</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxA = Math.max(...arpuTrend.map((x: any) => x.arpu), 0.0001);
              return arpuTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.arpu / maxA) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-violet-400/60 rounded-sm hover:bg-violet-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': $' + d.arpu.toFixed(2) + ' ARPU'}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (avg revenue / paying workspace)</p>
        </div>
      )}

      {agentRunTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Agent Run Volume</h3>
          <div className='flex items-end gap-[3px] h-10'>
            {(() => {
              const maxR = Math.max(...agentRunTrend.map((x: any) => x.runs), 1);
              return agentRunTrend.map((d: any) => {
                const pct = Math.max(4, Math.round((d.runs / maxR) * 100));
                return (
                  <div
                    key={d.date}
                    className='flex-1 bg-teal-400/60 rounded-sm hover:bg-teal-400 transition-colors'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.runs + ' runs'}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (agent runs across all workspaces)</p>
        </div>
      )}

      {widgetConvTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Widget Conversations</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...widgetConvTrend.map((d: any) => d.convs), 1);
              return widgetConvTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.convs / maxVal) * 100));
                return (
                  <div
                    key={i}
                    className='flex-1 bg-indigo-400/60 hover:bg-indigo-400 rounded-sm transition-colors cursor-default'
                    style={{ height: pct + '%' }}
                    title={d.date + ': ' + d.convs + ' convs'}
                  />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (widget visitor conversations)</p>
        </div>
      )}

      {contentCreationTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Content Creation</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...contentCreationTrend.map((d: any) => d.items), 1);
              return contentCreationTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.items / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-rose-400/60 hover:bg-rose-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.items + ' item' + (d.items !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (all content items created)</p>
        </div>
      )}

      {nurtureActivityTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Nurture Activity</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...nurtureActivityTrend.map((d: any) => d.nurtures), 1);
              return nurtureActivityTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.nurtures / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-lime-400/60 hover:bg-lime-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.nurtures + ' nurture' + (d.nurtures !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (leads receiving nurture emails)</p>
        </div>
      )}

      {harryIntelligenceTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Harry Intelligence</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...harryIntelligenceTrend.map((d: any) => d.briefings), 1);
              return harryIntelligenceTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.briefings / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-sky-400/60 hover:bg-sky-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.briefings + ' briefing' + (d.briefings !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (Harry intelligence briefings generated)</p>
        </div>
      )}

      {chatActivityTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Draper Chat Activity</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...chatActivityTrend.map((d: any) => d.sessions), 1);
              return chatActivityTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.sessions / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-pink-400/60 hover:bg-pink-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.sessions + ' session' + (d.sessions !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (active Draper chat sessions per day)</p>
        </div>
      )}

      {emailOutboxTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Email Activity</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...emailOutboxTrend.map((d: any) => d.emails), 1);
              return emailOutboxTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.emails / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-emerald-400/60 hover:bg-emerald-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.emails + ' email' + (d.emails !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (emails sent per day via outbox)</p>
        </div>
      )}

      {campaignLaunchTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Pete Ad Campaign Activity</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...campaignLaunchTrend.map((d: any) => d.campaigns), 1);
              return campaignLaunchTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.campaigns / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-yellow-400/60 hover:bg-yellow-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.campaigns + ' campaign' + (d.campaigns !== 1 ? 's' : '')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (ad campaigns launched per day)</p>
        </div>
      )}

      {memoryFormationTrend.length > 0 && (
        <div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'>
          <h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day Draper Memory Formation</h3>
          <div className='flex items-end gap-[2px] h-16'>
            {(() => {
              const maxVal = Math.max(...memoryFormationTrend.map((d: any) => d.memories), 1);
              return memoryFormationTrend.map((d: any, i: number) => {
                const pct = Math.max(4, Math.round((d.memories / maxVal) * 100));
                return (
                  <div key={i} className='flex-1 bg-fuchsia-400/60 hover:bg-fuchsia-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.memories + ' memor' + (d.memories !== 1 ? 'ies' : 'y')} />
                );
              });
            })()}
          </div>
          <p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (AI memories formed per day)</p>
        </div>
      )}

      {loginActivityTrend.length > 0 && (<div className='mt-3 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4'><h3 className='text-xs font-bold text-foreground/40 uppercase tracking-widest mb-3'>30-Day User Login Activity</h3><div className='flex items-end gap-[2px] h-16'>{(() => { const maxVal = Math.max(...loginActivityTrend.map((d: any) => d.logins), 1); return loginActivityTrend.map((d: any, i: number) => { const pct = Math.max(4, Math.round((d.logins / maxVal) * 100)); return (<div key={i} className='flex-1 bg-slate-400/60 hover:bg-slate-400 rounded-sm transition-colors cursor-default' style={{ height: pct + '%' }} title={d.date + ': ' + d.logins + ' login' + (d.logins !== 1 ? 's' : '')} />); }); })()}</div><p className='text-[10px] text-foreground/30 mt-2'>Last 30 days (successful logins per day)</p></div>)}

      {/* Quick Actions */}
      <div className="mt-6 bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5" data-testid="quick-actions-section">
        <h3 className="text-xs font-bold text-foreground/40 uppercase tracking-widest mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button
            onPointerDown={triggerDailyBrief}
            disabled={triggeringBrief}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600/20 border border-emerald-500/30 hover:bg-emerald-600/30 text-emerald-400 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            data-testid="button-trigger-daily-brief-overview"
          >
            {triggeringBrief ? <Loader2 size={15} className="animate-spin" /> : <Newspaper size={15} />}
            {triggeringBrief ? "Generating..." : "Draper's Daily Brief"}
          </button>
          <button
            onPointerDown={purgeQaArtifacts}
            disabled={purgingQa}
            className="flex items-center gap-2 px-4 py-2.5 bg-red-600/15 border border-red-500/25 hover:bg-red-600/25 text-red-400 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            data-testid="button-purge-qa-artifacts"
          >
            {purgingQa ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
            {purgingQa ? "Purging..." : "Purge QA Artifacts"}
          </button>
        </div>
        {briefResult && (
          <p className={`mt-3 text-xs px-3 py-2 rounded-lg border ${briefResult.startsWith("Failed") || briefResult.startsWith("Error") ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"}`} data-testid="overview-brief-result">
            {briefResult}
          </p>
        )}
        {purgeResult && (
          <p className={`mt-3 text-xs px-3 py-2 rounded-lg border ${purgeResult.startsWith("Failed") || purgeResult.startsWith("Error") ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"}`} data-testid="purge-qa-result">
            {purgeResult}
          </p>
        )}
      </div>

      {/* Line Charts: User Growth, Revenue, AI Usage */}
      {!trendsLoading && trends.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6" data-testid="overview-line-charts">
          <MiniLineChart data={trends} dataKey="newUsers" color="#3b82f6" label="User Growth" />
          <MiniLineChart data={trends} dataKey="revenue" color="#a855f7" label="Revenue" formatter={(v) => `$${v.toFixed(2)}`} />
          <MiniLineChart data={trends} dataKey="aiCalls" color="#f59e0b" label="AI Usage Trend" />
        </div>
      )}

      {/* 12-Week Trends */}
      <div className="mt-8" data-testid="analytics-trends">
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          <h3 className="text-lg font-display font-bold text-foreground/80">12-Week Trends</h3>
          <div className="flex gap-1.5 flex-wrap">
            {metricOptions.map((m) => (
              <button
                key={m.key}
                onPointerDown={() => setActiveMetric(m.key)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                  activeMetric === m.key
                    ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                    : "bg-foreground/[0.03] text-foreground/30 border border-foreground/[0.06] hover:text-foreground/60"
                }`}
                data-testid={`button-metric-${m.key}`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {trendsLoading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-foreground/20" size={20} /></div>
        ) : (
          <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4">
            <div className="flex gap-1 items-end">
              {trends.map((week, i) => (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group relative">
                  <div className="invisible group-hover:visible absolute -top-8 bg-background/90 border border-foreground/10 px-2 py-1 rounded text-[10px] text-foreground whitespace-nowrap z-10">
                    {activeMetric === "aiCost" || activeMetric === "revenue"
                      ? `$${Number(week[activeMetric]).toFixed(2)}`
                      : week[activeMetric]}
                  </div>
                  <TrendBar value={Number(week[activeMetric]) || 0} max={maxVal} color={currentMetricColor} />
                  <span className="text-[9px] text-foreground/20 font-mono">{week.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* AI Recommendations */}
      <div className="mt-8" data-testid="ai-recommendations">
        <div className="flex items-center justify-between gap-4 mb-4">
          <h3 className="text-lg font-display font-bold text-foreground/80">AI Recommendations</h3>
          <button
            onPointerDown={loadRecommendations}
            disabled={recsLoading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors disabled:opacity-50"
            data-testid="button-get-recommendations"
          >
            {recsLoading ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} />}
            {recsLoading ? "Analyzing..." : "Get Insights"}
          </button>
        </div>

        {recommendations.length > 0 && (
          <div className="space-y-2">
            {recommendations.map((rec: any, i: number) => (
              <div key={i} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-foreground/90">{rec.title}</span>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wide border ${priorityColor[rec.priority] || priorityColor.medium}`}>
                        {rec.priority}
                      </span>
                      <span className="text-[10px] text-foreground/25 uppercase tracking-wider">{rec.category}</span>
                    </div>
                    <p className="text-xs text-foreground/40 leading-relaxed">{rec.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {recommendations.length === 0 && !recsLoading && (
          <p className="text-xs text-foreground/20 italic">Click "Get Insights" to generate AI-powered recommendations based on your platform data.</p>
        )}
      </div>

      {/* Workspace Health Scorecard */}
      <div className="mt-8" data-testid="workspace-health">
        <h3 className="text-lg font-display font-bold text-foreground/80 mb-4">Workspace Health</h3>
        {workspaceHealth.length === 0 ? (
          <p className="text-xs text-foreground/20 italic">No workspace data available.</p>
        ) : (
          <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-foreground/[0.06]">
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Workspace</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Owner</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Plan</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Last Agent Run</th>
                    <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Content 7d</th>
                    <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Leads 7d</th>
                    <th className="text-center px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Telegram</th>
                    <th className="text-center px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Social</th>
                    <th className="text-center px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {workspaceHealth.map((ws: any, i: number) => {
                    const lastRun = ws.last_agent_run_at ? new Date(ws.last_agent_run_at) : null;
                    const now = Date.now();
                    const daysSinceRun = lastRun ? (now - lastRun.getTime()) / (1000 * 60 * 60 * 24) : Infinity;
                    const content7d = Number(ws.content_7d ?? 0);
                    let statusLabel = "Dormant";
                    let statusClass = "bg-red-500/10 text-red-400 border-red-500/20";
                    if (daysSinceRun <= 7 && content7d > 0) {
                      statusLabel = "Active";
                      statusClass = "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
                    } else if (daysSinceRun <= 30) {
                      statusLabel = "Idle";
                      statusClass = "bg-amber-500/10 text-amber-400 border-amber-500/20";
                    }
                    return (
                      <tr key={ws.workspace_id ?? i} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors">
                        <td className="px-4 py-3 font-medium text-foreground/80">{ws.name ?? "—"}</td>
                        <td className="px-4 py-3 text-foreground/40">{ws.owner_email ?? "—"}</td>
                        <td className="px-4 py-3 text-foreground/40 capitalize">{ws.plan ?? "free"}</td>
                        <td className="px-4 py-3 text-foreground/40">
                          {lastRun ? lastRun.toLocaleDateString() : <span className="text-foreground/20">Never</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-foreground/60">{content7d}</td>
                        <td className="px-4 py-3 text-right text-foreground/60">{Number(ws.leads_7d ?? 0)}</td>
                        <td className="px-4 py-3 text-center">
                          {ws.telegram_connected ? <span className="text-emerald-400">✓</span> : <span className="text-foreground/20">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {ws.social_connected ? <span className="text-emerald-400">✓</span> : <span className="text-foreground/20">—</span>}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${statusClass}`}>{statusLabel}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SchedulerTab() {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState<string | null>(null);

  const fetchJobs = () => {
    setLoading(true);
    fetch('/api/saas-admin/scheduler-status', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setJobs(d.jobs ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchJobs(); }, []);

  const forceRun = async (jobName: string) => {
    setRunning(jobName);
    await fetch(`/api/saas-admin/scheduler/force-run/${jobName}`, { method: 'POST', credentials: 'include' });
    await new Promise(r => setTimeout(r, 400));
    fetchJobs();
    setRunning(null);
  };

  const fmtInterval = (ms: number) => {
    if (ms >= 30 * 24 * 60 * 60 * 1000) return '30d';
    if (ms >= 7 * 24 * 60 * 60 * 1000) return '7d';
    if (ms >= 24 * 60 * 60 * 1000) return '24h';
    if (ms >= 60 * 60 * 1000) return `${Math.round(ms / 3600000)}h`;
    return `${Math.round(ms / 60000)}m`;
  };

  const fmtRelative = (ts: number) => {
    if (!ts) return 'never';
    const diff = Date.now() - ts;
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`;
    return `${Math.round(diff / 86400000)}d ago`;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground/80">Scheduler Jobs</h2>
          <p className="text-xs text-foreground/40 mt-0.5">{jobs.length} jobs tracked. Force-run resets the clock so the next 2-minute tick picks it up.</p>
        </div>
        <button onPointerDown={fetchJobs} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/60">Refresh</button>
      </div>
      {loading ? (
        <p className="text-xs text-foreground/30">Loading...</p>
      ) : (
        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Job</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Interval</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Last Run</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Next Expected</th>
                  <th className="text-center px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-center px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j: any) => (
                  <tr key={j.name} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors">
                    <td className="px-4 py-2.5 font-mono text-foreground/70">{j.name}</td>
                    <td className="px-4 py-2.5 text-foreground/40">{fmtInterval(j.intervalMs)}</td>
                    <td className="px-4 py-2.5 text-foreground/40">{fmtRelative(j.lastRunAt)}</td>
                    <td className="px-4 py-2.5 text-foreground/40">{j.lastRunAt ? fmtRelative(j.nextExpectedAt) : '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      {j.overdue ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-red-500/10 text-red-400 border-red-500/20">OVERDUE</span>
                      ) : j.lastRunAt === 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-amber-500/10 text-amber-400 border-amber-500/20">NEVER</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md border bg-emerald-500/10 text-emerald-400 border-emerald-500/20">OK</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onPointerDown={() => forceRun(j.name)}
                        disabled={running === j.name}
                        className="text-[10px] px-2 py-1 rounded bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/50 disabled:opacity-40"
                      >{running === j.name ? '...' : 'Force Run'}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function AuditLogTab() {
  const [entries, setEntries] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [actionFilter, setActionFilter] = useState('');
  const [wsFilter, setWsFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');

  const fetchEntries = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ limit: '50', offset: String(page * 50) });
    if (actionFilter.trim()) params.set('action', actionFilter.trim());
    if (wsFilter.trim()) params.set('workspaceId', wsFilter.trim());
    if (userFilter.trim()) params.set('userId', userFilter.trim());
    fetch(`/api/saas-admin/audit-log?${params}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setEntries(d.entries ?? []); setTotal(d.total ?? 0); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, actionFilter, wsFilter, userFilter]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const reset = () => { setActionFilter(''); setWsFilter(''); setUserFilter(''); setPage(0); };

  const fmtTime = (ts: string) => ts ? ts.slice(0, 19).replace('T', ' ') : '—';

  const badgeClass = (action: string) => {
    if (action.startsWith('login') || action.startsWith('logout') || action.startsWith('auth')) return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
    if (action.includes('delete') || action.includes('remove')) return 'bg-red-500/10 text-red-400 border-red-500/20';
    return 'bg-foreground/[0.06] text-foreground/50 border-foreground/[0.1]';
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground/80">Audit Log</h2>
          <p className="text-xs text-foreground/40 mt-0.5">{total} total entries across all workspaces.</p>
        </div>
        <button onPointerDown={fetchEntries} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/60">Refresh</button>
      </div>
      <div className="flex gap-2 mb-4">
        <input value={actionFilter} onChange={e => { setActionFilter(e.target.value); setPage(0); }} placeholder="Filter action..." className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.04] border border-foreground/[0.08] text-foreground/70 placeholder:text-foreground/25 focus:outline-none w-44" />
        <input value={wsFilter} onChange={e => { setWsFilter(e.target.value); setPage(0); }} placeholder="Workspace ID..." className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.04] border border-foreground/[0.08] text-foreground/70 placeholder:text-foreground/25 focus:outline-none w-36" />
        <input value={userFilter} onChange={e => { setUserFilter(e.target.value); setPage(0); }} placeholder="User ID..." className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.04] border border-foreground/[0.08] text-foreground/70 placeholder:text-foreground/25 focus:outline-none w-36" />
        <button onPointerDown={reset} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/50">Reset</button>
      </div>
      {loading ? (
        <p className="text-xs text-foreground/30">Loading...</p>
      ) : (
        <>
          <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-foreground/[0.06]">
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Time</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Action</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Resource</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">User ID</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Workspace</th>
                    <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">IP</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-foreground/25">No entries found.</td></tr>
                  ) : entries.map((e: any) => (
                    <tr key={e.id} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors">
                      <td className="px-4 py-2.5 font-mono text-foreground/40 whitespace-nowrap">{fmtTime(e.createdAt)}</td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${badgeClass(e.action)}`}>{e.action}</span>
                      </td>
                      <td className="px-4 py-2.5 text-foreground/50">{e.resourceType}{e.resourceId ? ` #${e.resourceId}` : ''}</td>
                      <td className="px-4 py-2.5 font-mono text-foreground/40 max-w-[140px] truncate">{e.userId}</td>
                      <td className="px-4 py-2.5 text-foreground/40">{e.workspaceId ?? '—'}</td>
                      <td className="px-4 py-2.5 font-mono text-foreground/30">{e.ipAddress ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-between mt-3">
            <span className="text-xs text-foreground/30">Showing {page * 50 + 1}–{Math.min((page + 1) * 50, total)} of {total}</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onPointerDown={() => setPage(p => p - 1)} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/50 disabled:opacity-40">Prev</button>
              <button disabled={(page + 1) * 50 >= total} onPointerDown={() => setPage(p => p + 1)} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/50 disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ContentPipelineTab() {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetch('/api/saas-admin/content-pipeline', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const fmtDate = (ts: string | null) => {
    if (!ts) return 'Never';
    const d = new Date(ts);
    const diffDays = Math.floor((Date.now() - d.getTime()) / (86400 * 1000));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    return `${diffDays}d ago`;
  };

  const isStalled = (r: any) => {
    if (!r.pending_approval) return false;
    if (!r.last_published_at) return true;
    const diffDays = (Date.now() - new Date(r.last_published_at).getTime()) / (86400 * 1000);
    return diffDays > 7;
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground/80">Content Pipeline</h2>
          <p className="text-xs text-foreground/40 mt-0.5">Content status across all workspaces.</p>
        </div>
        <button onPointerDown={fetchData} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/60">Refresh</button>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-foreground/[0.04] animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground/30 py-8 text-center">No content data yet</p>
      ) : (
        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Workspace</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Owner Email</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Pending</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Scheduled</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Failed</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Published Total</th>
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Last Published</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r: any) => (
                  <tr key={r.workspace_id} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors">
                    <td className="px-4 py-2.5 font-medium text-foreground/70 flex items-center gap-2">
                      {r.name}
                      {isStalled(r) && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">Stalled</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-foreground/50">{r.owner_email}</td>
                    <td className="px-4 py-2.5">
                      {r.pending_approval > 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-400">{r.pending_approval}</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-green-500/10 border border-green-500/20 text-green-400">0</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-blue-400">{r.scheduled}</td>
                    <td className="px-4 py-2.5">
                      {r.failed > 0 ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-red-500/10 border border-red-500/20 text-red-400">{r.failed}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-foreground/50">{r.published_total}</td>
                    <td className="px-4 py-2.5 text-foreground/40">{fmtDate(r.last_published_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BudgetBurnTab() {
  const [rows, setRows] = useState<Array<{
    workspaceId: number;
    workspaceName: string | null;
    monthlyBudgetCents: number;
    currentMonthSpendCents: number;
    budgetResetAt: string | null;
    burnPct: number;
  }>>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    setLoading(true);
    fetch('/api/saas-admin/budget-burn-rate', { credentials: 'include' })
      .then(r => r.json())
      .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const burnColor = (pct: number) => {
    if (pct >= 80) return 'text-red-400';
    if (pct >= 50) return 'text-amber-400';
    return 'text-emerald-400';
  };

  const burnBg = (pct: number) => {
    if (pct >= 80) return 'bg-red-500/10 border-red-500/20';
    if (pct >= 50) return 'bg-amber-500/10 border-amber-500/20';
    return '';
  };

  const fmt = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const criticalCount = rows.filter(r => r.burnPct >= 80).length;
  const warningCount = rows.filter(r => r.burnPct >= 50 && r.burnPct < 80).length;

  return (
    <div className="p-6" data-testid="budget-burn-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground/80">Budget Burn Rate</h2>
          <p className="text-xs text-foreground/40 mt-0.5">Monthly AI budget utilization per workspace. Red = &gt;80%, Yellow = &gt;50%.</p>
        </div>
        <div className="flex items-center gap-3">
          {criticalCount > 0 && (
            <span className="text-xs font-bold px-2 py-1 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">{criticalCount} Critical</span>
          )}
          {warningCount > 0 && (
            <span className="text-xs font-bold px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400">{warningCount} Warning</span>
          )}
          <button onPointerDown={fetchData} className="text-xs px-3 py-1.5 rounded-lg bg-foreground/[0.06] hover:bg-foreground/10 transition-colors text-foreground/60">Refresh</button>
        </div>
      </div>
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-10 rounded-lg bg-foreground/[0.04] animate-pulse" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground/30 py-8 text-center">No workspace budget data found</p>
      ) : (
        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Workspace</th>
                  <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Monthly Budget</th>
                  <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Spent</th>
                  <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Burn %</th>
                  <th className="text-right px-4 py-3 text-foreground/40 font-semibold uppercase tracking-wider">Resets</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.workspaceId} className={`border-b border-foreground/[0.04] ${burnBg(r.burnPct)} ${i % 2 === 0 ? '' : 'bg-foreground/[0.01]'}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{r.workspaceName ?? `Workspace #${r.workspaceId}`}</div>
                      <div className="text-foreground/40">ID {r.workspaceId}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-foreground/70 font-mono">{fmt(r.monthlyBudgetCents)}</td>
                    <td className="px-4 py-3 text-right text-foreground/70 font-mono">{fmt(r.currentMonthSpendCents)}</td>
                    <td className={`px-4 py-3 text-right font-bold font-mono ${burnColor(r.burnPct)}`}>{Number(r.burnPct).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right text-foreground/50">{r.budgetResetAt ? new Date(r.budgetResetAt).toLocaleDateString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterPlan, setFilterPlan] = useState("all");
  const [filterSuspended, setFilterSuspended] = useState("all");
  const [userStats, setUserStats] = useState<any>(null);

  const loadUsers = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set("q", searchQuery.trim());
    if (filterPlan !== "all") params.set("subscription", filterPlan);
    if (filterSuspended !== "all") params.set("suspended", filterSuspended);
    const url = params.toString()
      ? `/api/saas-admin/users/search?${params}`
      : "/api/saas-admin/users";
    fetch(url, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => setUsers(data.users || data))
      .finally(() => setLoading(false));
  }, [searchQuery, filterPlan, filterSuspended]);

  const loadStats = useCallback(() => {
    fetch("/api/saas-admin/users/stats", { credentials: "include" })
      .then((r) => r.json())
      .then(setUserStats);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { loadStats(); }, [loadStats]);

  const updateSubscription = async (userId: string, subscription: string) => {
    await fetch(`/api/saas-admin/users/${userId}/subscription`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ subscription }),
      credentials: "include",
    });
    loadUsers();
    loadStats();
  };

  const removeUser = async (userId: string, userEmail: string) => {
    if (!confirm(`Permanently delete user ${userEmail}? This also removes their workspaces and cannot be undone.`)) return;
    await fetch(`/api/saas-admin/users/${userId}`, { method: "DELETE" });
    loadUsers();
    loadStats();
  };

  const toggleSuspend = async (userId: string, currentSuspended: boolean) => {
    await fetch(`/api/saas-admin/users/${userId}/suspend`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ suspended: !currentSuspended }),
      credentials: "include",
    });
    loadUsers();
    loadStats();
  };

  const resetAiUsage = async (userId: string) => {
    await fetch(`/api/saas-admin/users/${userId}/reset-ai-usage`, {
      method: "POST",
      credentials: "include",
    });
    loadUsers();
  };

  return (
    <div data-testid="users-tab">
      {/* User Stats Summary */}
      {userStats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {[
            { label: "Total", value: userStats.total, color: "text-foreground/60" },
            { label: "Free", value: userStats.free, color: "text-foreground/40" },
            { label: "Solo", value: userStats.solo, color: "text-blue-400" },
            { label: "Growth", value: userStats.growth, color: "text-purple-400" },
            { label: "Suspended", value: userStats.suspended, color: "text-red-400" },
            { label: "New (7d)", value: userStats.newThisWeek, color: "text-emerald-400" },
          ].map((s) => (
            <div key={s.label} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-3 text-center">
              <span className="text-[10px] text-foreground/25 uppercase tracking-wider block mb-1">{s.label}</span>
              <span className={`text-lg font-bold ${s.color}`}>{s.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <h2 className="text-2xl font-display font-bold">Users ({users.length})</h2>
        <button onPointerDown={() => { loadUsers(); loadStats(); }} className="p-2 rounded-xl hover:bg-foreground/[0.05] text-foreground/30 hover:text-foreground/60 transition-colors" data-testid="button-refresh-users">
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/20" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-foreground/[0.03] border border-foreground/[0.08] rounded-xl text-sm text-foreground/80 placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
            data-testid="input-user-search"
          />
        </div>
        <select
          value={filterPlan}
          onChange={(e) => setFilterPlan(e.target.value)}
          className="px-3 py-2 bg-foreground/[0.03] border border-foreground/[0.08] rounded-xl text-sm text-foreground/60 focus:outline-none focus:border-blue-500/40 appearance-none cursor-pointer"
          data-testid="select-filter-plan"
        >
          <option value="all">All Plans</option>
          <option value="free">Free</option>
          <option value="solo">Solo</option>
          <option value="growth">Growth</option>
        </select>
        <select
          value={filterSuspended}
          onChange={(e) => setFilterSuspended(e.target.value)}
          className="px-3 py-2 bg-foreground/[0.03] border border-foreground/[0.08] rounded-xl text-sm text-foreground/60 focus:outline-none focus:border-blue-500/40 appearance-none cursor-pointer"
          data-testid="select-filter-suspended"
        >
          <option value="all">All Status</option>
          <option value="true">Suspended</option>
          <option value="false">Active</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>
      ) : (
        <div className="space-y-2">
          {users.length === 0 && (
            <p className="text-center text-foreground/20 py-8 text-sm">No users found matching your filters.</p>
          )}
          {users.map((user) => (
            <div key={user.id} className={`border rounded-2xl overflow-hidden ${
              user.suspended ? "bg-red-500/[0.03] border-red-500/10" : "bg-foreground/[0.02] border-foreground/[0.06]"
            }`}>
              <button
                onPointerDown={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
                className="w-full flex items-center gap-4 p-4 hover:bg-foreground/[0.02] transition-colors"
                data-testid={`user-row-${user.id}`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold ${
                  user.suspended ? "bg-red-500/20 text-red-400" : "bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-foreground/60"
                }`}>
                  {user.suspended ? <Ban size={16} /> : (user.firstName?.[0] || user.email?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex-1 text-left min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm font-semibold truncate ${user.suspended ? "text-foreground/40 line-through" : "text-foreground/90"}`}>{user.displayName || user.firstName || user.email || `User ${user.id}`}</p>
                    {user.suspended && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/15 uppercase">Suspended</span>}
                    {user.waitlistStatus === "DIRECT LOGIN" && (
                      <span title="Signed in via OAuth but has no waitlist entry" className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-yellow-500/10 text-yellow-400 border border-yellow-500/15 uppercase cursor-help" data-testid={`badge-direct-login-${user.id}`}>Direct Login</span>
                    )}
                    {user.waitlistStatus === "PENDING" && (
                      <span title="On the waitlist — not yet approved" className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-foreground/5 text-foreground/30 border border-foreground/10 uppercase cursor-help" data-testid={`badge-pending-${user.id}`}>Pending</span>
                    )}
                    {user.waitlistStatus === "REJECTED" && (
                      <span title="Waitlist application was rejected" className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/15 uppercase cursor-help" data-testid={`badge-rejected-${user.id}`}>Rejected</span>
                    )}
                  </div>
                  <p className="text-xs text-foreground/30 truncate">{user.email}</p>
                </div>
                <span className={`text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide ${
                  user.subscription === "growth" ? "bg-purple-500/10 text-purple-400 border border-purple-500/15"
                  : user.subscription === "solo" ? "bg-blue-500/10 text-blue-400 border border-blue-500/15"
                  : "bg-foreground/[0.04] text-foreground/30 border border-foreground/[0.06]"
                }`}>
                  {user.subscription || "free"}
                </span>
                <span className="text-xs text-foreground/20 font-mono">${(user.aiTotalCost || 0).toFixed(4)}</span>
                {expandedUser === user.id ? <ChevronDown size={16} className="text-foreground/20" /> : <ChevronRight size={16} className="text-foreground/20" />}
              </button>
              {expandedUser === user.id && (
                <div className="border-t border-foreground/[0.04] p-4 bg-foreground/[0.01]">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                    <div><span className="text-[10px] text-foreground/25 uppercase tracking-wider block">Workspaces</span><span className="text-sm font-bold text-foreground">{user.workspaceCount}</span></div>
                    <div><span className="text-[10px] text-foreground/25 uppercase tracking-wider block">Content</span><span className="text-sm font-bold text-foreground">{user.contentCount}</span></div>
                    <div><span className="text-[10px] text-foreground/25 uppercase tracking-wider block">Campaigns</span><span className="text-sm font-bold text-foreground">{user.campaignCount}</span></div>
                    <div><span className="text-[10px] text-foreground/25 uppercase tracking-wider block">AI Spend</span><span className="text-sm font-bold text-emerald-400">${(user.aiTotalCost || 0).toFixed(4)}</span></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 border border-foreground/[0.04] rounded-xl p-3 bg-foreground/[0.02]">
                    <div>
                      <span className="text-[10px] text-foreground/25 uppercase tracking-wider block mb-0.5">Overage Billing</span>
                      <span className={`text-xs font-bold ${user.overageBillingEnabled ? "text-emerald-400" : "text-foreground/30"}`} data-testid={`text-overage-enabled-${user.id}`}>
                        {user.overageBillingEnabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-foreground/25 uppercase tracking-wider block mb-0.5">Overage Balance</span>
                      <span className="text-xs font-bold text-emerald-400" data-testid={`text-overage-balance-${user.id}`}>
                        ${(user.aiUsageBalance || 0).toFixed(4)}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-foreground/25 uppercase tracking-wider block mb-0.5">Card Saved</span>
                      <span className={`text-xs font-bold ${user.stripePaymentMethodId ? "text-emerald-400" : "text-foreground/30"}`} data-testid={`text-has-card-${user.id}`}>
                        {user.stripePaymentMethodId ? "Yes" : "No"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] text-foreground/25 uppercase tracking-wider block mb-0.5">Charge Failed</span>
                      <span className={`text-xs font-bold ${user.overageChargeFailedAt ? "text-red-400" : "text-foreground/30"}`} data-testid={`text-charge-failed-${user.id}`}>
                        {user.overageChargeFailedAt ? new Date(user.overageChargeFailedAt).toLocaleDateString() : "—"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap items-center">
                    <span className="text-xs text-foreground/25 mr-1">Plan:</span>
                    {["free", "solo", "growth"].map((plan) => (
                      <button
                        key={plan}
                        onPointerDown={() => updateSubscription(user.id, plan)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          user.subscription === plan
                            ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                            : "bg-foreground/[0.03] text-foreground/30 border border-foreground/[0.06] hover:text-foreground/60"
                        }`}
                        data-testid={`button-plan-${plan}-${user.id}`}
                      >
                        {plan}
                      </button>
                    ))}
                    <div className="w-px h-5 bg-foreground/[0.06] mx-1" />
                    <button
                      onPointerDown={() => toggleSuspend(user.id, user.suspended)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        user.suspended
                          ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20"
                          : "bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                      }`}
                      data-testid={`button-suspend-${user.id}`}
                    >
                      {user.suspended ? <UserCheck size={12} /> : <UserX size={12} />}
                      {user.suspended ? "Unsuspend" : "Suspend"}
                    </button>
                    <button
                      onPointerDown={() => resetAiUsage(user.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
                      data-testid={`button-reset-ai-${user.id}`}
                    >
                      <RotateCcw size={12} />
                      Reset AI Usage
                    </button>
                    {user.waitlistStatus === "DIRECT LOGIN" && (
                      <>
                        <div className="w-px h-5 bg-foreground/[0.06] mx-1" />
                        <button
                          onPointerDown={() => removeUser(user.id, user.email || user.id)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
                          data-testid={`button-remove-user-${user.id}`}
                        >
                          <Trash2 size={12} />
                          Remove User
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvitesTab() {
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [plan, setPlan] = useState("solo");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresInHours, setExpiresInHours] = useState(48);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const loadInvites = useCallback(() => {
    setLoading(true);
    fetch("/api/saas-admin/invite-codes", { credentials: "include" })
      .then((r) => r.json())
      .then(setInvites)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadInvites(); }, [loadInvites]);

  const createInvite = async () => {
    setCreating(true);
    try {
      await fetch("/api/saas-admin/invite-codes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan, maxUses, expiresInHours }),
        credentials: "include",
      });
      loadInvites();
    } finally {
      setCreating(false);
    }
  };

  const deactivate = async (id: number) => {
    await fetch(`/api/saas-admin/invite-codes/${id}`, { method: "DELETE", credentials: "include" });
    loadInvites();
  };

  const copyLink = async (code: string, id: number) => {
    const url = `${window.location.origin}/signup?invite=${code}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div data-testid="invites-tab">
      <h2 className="text-2xl font-display font-bold mb-6">Invite Codes</h2>

      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5 mb-6">
        <h3 className="text-sm font-bold text-foreground/70 mb-4">Generate New Invite</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">Plan</label>
            <select
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm focus:outline-none focus:border-blue-500/40"
              data-testid="select-invite-plan"
            >
              <option value="solo">Solo - $199.99/mo</option>
              <option value="growth">Growth - $399.99/mo</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">Max Uses</label>
            <input
              type="number"
              value={maxUses}
              onChange={(e) => setMaxUses(Number(e.target.value))}
              min={1}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm focus:outline-none focus:border-blue-500/40"
              data-testid="input-max-uses"
            />
          </div>
          <div>
            <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">Expires In (hours)</label>
            <input
              type="number"
              value={expiresInHours}
              onChange={(e) => setExpiresInHours(Number(e.target.value))}
              min={1}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm focus:outline-none focus:border-blue-500/40"
              data-testid="input-expires-hours"
            />
          </div>
        </div>
        <button
          onPointerDown={createInvite}
          disabled={creating}
          className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground font-bold rounded-xl text-sm transition-colors disabled:opacity-50"
          data-testid="button-generate-invite"
        >
          {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          Generate Invite Link
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>
      ) : invites.length === 0 ? (
        <p className="text-foreground/25 text-sm text-center py-12">No invite codes yet.</p>
      ) : (
        <div className="space-y-2">
          {invites.map((inv) => {
            const expired = new Date(inv.expiresAt) < new Date();
            const fullyUsed = inv.usedCount >= inv.maxUses;
            const inactive = !inv.isActive || expired || fullyUsed;
            return (
              <div key={inv.id} className={`flex items-center gap-4 p-4 rounded-2xl border ${inactive ? "bg-foreground/[0.01] border-foreground/[0.03] opacity-50" : "bg-foreground/[0.02] border-foreground/[0.06]"}`} data-testid={`invite-row-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <code className="text-xs font-mono text-foreground/60 truncate">{inv.code}</code>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                      inv.plan === "growth" ? "bg-purple-500/10 text-purple-400" : "bg-blue-500/10 text-blue-400"
                    }`}>{inv.plan}</span>
                  </div>
                  <div className="flex gap-4 text-[10px] text-foreground/20">
                    <span>{inv.usedCount}/{inv.maxUses} uses</span>
                    <span>{expired ? "Expired" : `Expires ${new Date(inv.expiresAt).toLocaleDateString()}`}</span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    onPointerDown={() => copyLink(inv.code, inv.id)}
                    className="p-2 rounded-lg hover:bg-foreground/[0.05] text-foreground/25 hover:text-foreground/60 transition-colors"
                    data-testid={`button-copy-invite-${inv.id}`}
                  >
                    {copiedId === inv.id ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
                  </button>
                  {!inactive && (
                    <button
                      onPointerDown={() => deactivate(inv.id)}
                      className="p-2 rounded-lg hover:bg-red-500/10 text-foreground/25 hover:text-red-400 transition-colors"
                      data-testid={`button-deactivate-invite-${inv.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AiPricingTab() {
  const [configs, setConfigs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const defaultServices = [
    { service: "gemini-flash-text", model: "gemini-2.5-flash", inputCostPer1k: 0.00015, outputCostPer1k: 0.0006, perCallCost: null, marginPercent: 20 },
    { service: "gemini-flash-image", model: "gemini-2.5-flash-image", inputCostPer1k: 0.00015, outputCostPer1k: 0.0006, perCallCost: 0.02, marginPercent: 20 },
    { service: "imagen", model: "imagen-3", inputCostPer1k: 0, outputCostPer1k: 0, perCallCost: 0.04, marginPercent: 25 },
    { service: "veo", model: "veo-2", inputCostPer1k: 0, outputCostPer1k: 0, perCallCost: 0.15, marginPercent: 25 },
  ];

  const loadConfigs = useCallback(() => {
    setLoading(true);
    fetch("/api/saas-admin/ai-pricing", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        const merged = defaultServices.map((def) => {
          const existing = data.find((c: any) => c.service === def.service);
          return existing || def;
        });
        setConfigs(merged);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const updateConfig = async (config: any) => {
    setSaving(config.service);
    try {
      await fetch("/api/saas-admin/ai-pricing", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(config),
        credentials: "include",
      });
      loadConfigs();
    } finally {
      setSaving(null);
    }
  };

  const updateField = (service: string, field: string, value: number | null) => {
    setConfigs((prev) =>
      prev.map((c) => (c.service === service ? { ...c, [field]: value } : c))
    );
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>;

  return (
    <div data-testid="ai-pricing-tab">
      <h2 className="text-2xl font-display font-bold mb-2">AI Service Pricing</h2>
      <p className="text-foreground/30 text-sm mb-6">Set base costs and your margin percentage for each AI service. Customers are charged base cost + margin.</p>

      <div className="space-y-4">
        {configs.map((config) => (
          <div key={config.service} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5">
            <div className="flex items-center justify-between gap-4 mb-4">
              <div>
                <h3 className="text-sm font-bold text-foreground">{config.service}</h3>
                <p className="text-[10px] text-foreground/25 font-mono">{config.model}</p>
              </div>
              <button
                onPointerDown={() => updateConfig(config)}
                disabled={saving === config.service}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600/20 text-blue-400 rounded-lg text-xs font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                data-testid={`button-save-pricing-${config.service}`}
              >
                {saving === config.service ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Save
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1">Input $/1K tokens</label>
                <input
                  type="number"
                  step="0.0001"
                  value={config.inputCostPer1k}
                  onChange={(e) => updateField(config.service, "inputCostPer1k", Number(e.target.value))}
                  className="w-full px-3 py-2 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs font-mono focus:outline-none focus:border-blue-500/40"
                />
              </div>
              <div>
                <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1">Output $/1K tokens</label>
                <input
                  type="number"
                  step="0.0001"
                  value={config.outputCostPer1k}
                  onChange={(e) => updateField(config.service, "outputCostPer1k", Number(e.target.value))}
                  className="w-full px-3 py-2 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs font-mono focus:outline-none focus:border-blue-500/40"
                />
              </div>
              <div>
                <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1">Per-Call Cost</label>
                <input
                  type="number"
                  step="0.001"
                  value={config.perCallCost ?? ""}
                  onChange={(e) => updateField(config.service, "perCallCost", e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-3 py-2 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs font-mono focus:outline-none focus:border-blue-500/40"
                  placeholder="N/A"
                />
              </div>
              <div>
                <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1">Margin %</label>
                <input
                  type="number"
                  step="1"
                  value={config.marginPercent}
                  onChange={(e) => updateField(config.service, "marginPercent", Number(e.target.value))}
                  className="w-full px-3 py-2 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs font-mono focus:outline-none focus:border-blue-500/40"
                />
                {config.marginPercent > 50 && (
                  <span className="text-[10px] text-yellow-400 mt-1 block" data-testid="high-margin-warning">⚠ High margin ({config.marginPercent}%)</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AiUsageTab() {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [userLogs, setUserLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [period, setPeriod] = useState<"month" | "all">("month");
  const [expandedSessions, setExpandedSessions] = useState<Record<number, any[] | null>>({});
  const [loadingSteps, setLoadingSteps] = useState<Record<number, boolean>>({});
  const [expandedRawMeta, setExpandedRawMeta] = useState<Record<string, boolean>>({});

  const loadUsers = (p: "month" | "all", preserveSelection?: boolean) => {
    setLoading(true);
    fetch(`/api/saas-admin/users?period=${p}`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setUsers((data.users || data).sort((a: any, b: any) => (b.aiTotalCost || 0) - (a.aiTotalCost || 0)));
        if (!preserveSelection) {
          setSelectedUser(null);
          setUserLogs([]);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUsers(period);
  }, []);

  const handlePeriodChange = (p: "month" | "all") => {
    setPeriod(p);
    loadUsers(p, !!selectedUser);
    if (selectedUser) {
      loadUserLogs(selectedUser, p);
    }
  };

  const loadUserLogs = async (userId: string, p?: "month" | "all") => {
    setSelectedUser(userId);
    setLoadingLogs(true);
    try {
      const activePeriod = p ?? period;
      const res = await fetch(`/api/saas-admin/ai-usage/user/${userId}?period=${activePeriod}`, { credentials: "include" });
      const data = await res.json();
      setUserLogs(data.logs || []);
    } finally {
      setLoadingLogs(false);
    }
  };

  const toggleSessionSteps = async (logId: number, sessionId: string) => {
    if (expandedSessions[logId] !== undefined) {
      setExpandedSessions(prev => { const n = { ...prev }; delete n[logId]; return n; });
      return;
    }
    setLoadingSteps(prev => ({ ...prev, [logId]: true }));
    try {
      const res = await fetch(`/api/saas-admin/ai-usage/session/${sessionId}/steps`, { credentials: "include" });
      const steps = await res.json();
      setExpandedSessions(prev => ({ ...prev, [logId]: steps }));
    } catch {
      setExpandedSessions(prev => ({ ...prev, [logId]: [] }));
    } finally {
      setLoadingSteps(prev => { const n = { ...prev }; delete n[logId]; return n; });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>;

  return (
    <div data-testid="ai-usage-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-2xl font-display font-bold">AI Usage by User</h2>
        <div className="flex items-center gap-2">
          <button
            data-testid="button-reset-all-balances"
            onClick={async () => {
              if (!confirm("Reset ALL user and workspace AI balances to zero? This cannot be undone.")) return;
              try {
                const res = await fetch("/api/saas-admin/reset-all-ai-balances", { method: "POST", credentials: "include" });
                const data = await res.json();
                if (data.success) alert("All AI balances reset to zero.");
                else alert("Failed: " + (data.message || "unknown error"));
              } catch (e: any) { alert("Error: " + e.message); }
            }}
            className="px-3 py-1.5 rounded-md text-xs font-semibold bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors border border-red-500/20"
          >
            Reset All Balances
          </button>
          <div className="flex items-center gap-1 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg p-0.5">
            <button
              data-testid="button-period-month"
              onClick={() => handlePeriodChange("month")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${period === "month" ? "bg-blue-500/20 text-blue-400" : "text-foreground/40 hover:text-foreground/60"}`}
            >
              This Month
            </button>
            <button
              data-testid="button-period-all"
              onClick={() => handlePeriodChange("all")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${period === "all" ? "bg-blue-500/20 text-blue-400" : "text-foreground/40 hover:text-foreground/60"}`}
            >
              All Time
            </button>
          </div>
        </div>
      </div>
      <p className="text-foreground/30 text-sm mb-6">
        Track AI costs per user, broken down by service and model.{" "}
        <span className="text-foreground/20">{period === "month" ? "Showing current month only." : "Showing all-time totals."}</span>
      </p>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-2">
          {users.map((user) => (
            <button
              key={user.id}
              onPointerDown={() => loadUserLogs(user.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                (user as any).isSystemEntry
                  ? selectedUser === user.id
                    ? "bg-purple-500/10 border border-purple-500/30"
                    : "bg-foreground/[0.02] border border-purple-500/10 hover:bg-foreground/[0.04]"
                  : selectedUser === user.id
                    ? "bg-blue-500/10 border border-blue-500/20"
                    : "bg-foreground/[0.02] border border-foreground/[0.04] hover:bg-foreground/[0.04]"
              }`}
              data-testid={`button-user-usage-${user.id}`}
            >
              <div className="flex-1 min-w-0 text-left">
                <p className={`text-sm font-semibold truncate ${(user as any).isSystemEntry ? "text-purple-400/80" : "text-foreground/80"}`}>
                  {user.displayName || user.firstName || user.email || `User ${user.id}`}
                </p>
                <p className="text-[10px] text-foreground/25">{user.aiLogCount || 0} calls{(user as any).isSystemEntry ? " · scheduler / background" : ""}</p>
              </div>
              <span className={`text-xs font-mono ${(user as any).isSystemEntry ? "text-purple-400" : "text-emerald-400"}`}>${(user.aiTotalCost || 0).toFixed(4)}</span>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {!selectedUser ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <TrendingUp size={24} className="text-foreground/10 mb-3" />
              <p className="text-foreground/25 text-sm">Select a user to view their AI usage breakdown.</p>
            </div>
          ) : loadingLogs ? (
            <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>
          ) : userLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <p className="text-foreground/25 text-sm">No AI usage recorded for this user.</p>
            </div>
          ) : (
            <>
            <div className="space-y-1 mb-3">
              {(() => {
                const totalBilled = userLogs.reduce((s: number, l: any) => s + (l.totalCost || 0), 0);
                const totalTrue = userLogs.reduce((s: number, l: any) => s + (l.trueCost || l.baseCost || 0), 0);
                const totalThinking = userLogs.reduce((s: number, l: any) => s + (l.thinkingTokens || 0), 0);
                return (
                  <div className="flex items-center gap-4 p-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl" data-testid="usage-summary">
                    <div className="flex-1 flex gap-6 text-xs">
                      <div><span className="text-foreground/30">Billed:</span> <span className="font-mono text-emerald-400">${totalBilled.toFixed(4)}</span></div>
                      <div><span className="text-foreground/30">True Cost:</span> <span className="font-mono text-amber-400">${totalTrue.toFixed(4)}</span></div>
                      <div><span className="text-foreground/30">Margin:</span> <span className={`font-mono ${(totalBilled - totalTrue) < 0 ? 'text-red-400' : 'text-foreground/50'}`}>${(totalBilled - totalTrue).toFixed(4)}</span></div>
                      {totalThinking > 0 && <div><span className="text-foreground/30">Thinking:</span> <span className="font-mono text-purple-400">{(totalThinking / 1000).toFixed(1)}K</span></div>}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {userLogs.map((log: any) => {
                const isAgentSession = log.metadata?.agent && log.metadata?.sessionId;
                const sessionId = log.metadata?.sessionId;
                const isExpanded = expandedSessions[log.id] !== undefined;
                const steps = expandedSessions[log.id] || [];
                const isFetchingSteps = !!loadingSteps[log.id];
                return (
                  <div key={log.id} className="rounded-xl overflow-hidden border border-foreground/[0.06]">
                    <div
                      className={`flex items-center gap-4 p-3 transition-colors ${isAgentSession ? "cursor-pointer hover:bg-foreground/[0.05]" : ""} bg-foreground/[0.02]`}
                      onClick={() => isAgentSession && toggleSessionSteps(log.id, sessionId)}
                      data-testid={`usage-log-row-${log.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {isAgentSession && (
                            <span className="text-[10px] text-blue-400/70 font-mono">
                              {isExpanded ? "▾" : "▸"}
                            </span>
                          )}
                          <span className="text-xs font-bold text-foreground/70">{log.service}</span>
                          {log.metadata?.agent && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 font-mono">{log.metadata.agent}</span>}
                          <span className="text-[10px] text-foreground/20 font-mono">{log.model}</span>
                          {log.metadata?.iterations && <span className="text-[10px] text-foreground/15">{log.metadata.iterations} steps</span>}
                        </div>
                        <div className="flex gap-3 text-[10px] text-foreground/20">
                          {log.inputTokens > 0 && <span>{log.inputTokens.toLocaleString()} in</span>}
                          {log.outputTokens > 0 && <span>{log.outputTokens.toLocaleString()} out</span>}
                          {(log.thinkingTokens || 0) > 0 && <span className="text-purple-400">{log.thinkingTokens.toLocaleString()} think</span>}
                          <span>{new Date(log.createdAt).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] font-mono text-amber-400/60">${(log.trueCost || log.baseCost || 0).toFixed(6)}</p>
                        <p className="text-xs font-mono text-emerald-400">${(log.totalCost || 0).toFixed(6)}</p>
                      </div>
                    </div>

                    {isAgentSession && isExpanded && (
                      <div className="border-t border-foreground/[0.06] bg-foreground/[0.01]">
                        {isFetchingSteps ? (
                          <div className="flex items-center gap-2 p-4 text-xs text-foreground/30">
                            <Loader2 className="animate-spin" size={12} /> Loading steps...
                          </div>
                        ) : steps.length === 0 ? (
                          <p className="p-4 text-xs text-foreground/20 italic">No step data yet — run the agent in production to populate.</p>
                        ) : (
                          <div className="divide-y divide-foreground/[0.04]">
                            {steps.map((step: any) => {
                              const rawKey = `${log.id}-${step.stepNumber}`;
                              const rawOpen = !!expandedRawMeta[rawKey];
                              return (
                                <div key={step.id} className="px-4 py-2.5 text-[10px]" data-testid={`step-row-${step.id}`}>
                                  <div className="flex items-start gap-3">
                                    <div className="flex-1 min-w-0 space-y-1">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-mono text-foreground/40">Step {step.stepNumber}</span>
                                        <span className="font-bold text-blue-400/70">{step.agentName}</span>
                                        {step.wasSummarized && (
                                          <span className="px-1 py-0.5 rounded bg-yellow-500/15 text-yellow-400 font-mono">summarized</span>
                                        )}
                                        {step.toolCalled ? (
                                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-mono">{step.toolCalled}</span>
                                        ) : (
                                          <span className="text-foreground/25 italic">text response</span>
                                        )}
                                      </div>
                                      <div className="flex gap-3 text-foreground/30 flex-wrap">
                                        <span>in: <span className="text-foreground/50 font-mono">{(step.inputTokens || 0).toLocaleString()}</span></span>
                                        <span>out: <span className="text-foreground/50 font-mono">{(step.outputTokens || 0).toLocaleString()}</span></span>
                                        {(step.thinkingTokens || 0) > 0
                                          ? <span>think: <span className="text-purple-400 font-mono">{step.thinkingTokens.toLocaleString()}</span></span>
                                          : <span className="text-foreground/15">think: 0</span>
                                        }
                                        <span>ctx: <span className="text-foreground/50 font-mono">{step.contentsLength}</span></span>
                                      </div>
                                    </div>
                                    <div className="text-right shrink-0 space-y-0.5">
                                      <p className="font-mono text-amber-400/60">step: ${(step.stepCostUsd || 0).toFixed(6)}</p>
                                      <p className="font-mono text-foreground/30">Σ ${(step.cumulativeCostUsd || 0).toFixed(6)}</p>
                                    </div>
                                  </div>
                                  {step.rawUsageMetadata && (
                                    <div className="mt-1.5">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); setExpandedRawMeta(prev => ({ ...prev, [rawKey]: !prev[rawKey] })); }}
                                        className="text-foreground/20 hover:text-foreground/50 transition-colors"
                                        data-testid={`raw-meta-toggle-${step.id}`}
                                      >
                                        {rawOpen ? "▾ hide raw" : "▸ raw metadata"}
                                      </button>
                                      {rawOpen && (
                                        <pre className="mt-1 p-2 rounded bg-foreground/[0.04] text-foreground/40 overflow-x-auto text-[9px] leading-relaxed max-h-48">
                                          {JSON.stringify(step.rawUsageMetadata, null, 2)}
                                        </pre>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface PlatformConfig {
  platform: string;
  label: string;
  subtitle: string;
  color: string;
  colorClasses: string;
  defaultScopes: string;
  type?: "oauth" | "apikey" | "apikey_phone";
  fields?: { id: string; secret: string; extra?: string };
}

const PLATFORMS: PlatformConfig[] = [
  {
    platform: "linkedin",
    label: "LinkedIn",
    subtitle: "Professional Network",
    color: "#0A66C2",
    colorClasses: "from-[#0A66C2]/20 to-transparent border-[#0A66C2]/20 text-[#0A66C2]",
    defaultScopes: "openid profile email w_member_social",
  },
  {
    platform: "meta",
    label: "Meta",
    subtitle: "Facebook / Instagram",
    color: "#1877F2",
    colorClasses: "from-[#1877F2]/20 to-transparent border-[#1877F2]/20 text-[#1877F2]",
    defaultScopes: "pages_show_list,pages_read_engagement,pages_manage_posts,pages_read_user_content,pages_manage_metadata,read_insights,ads_management,ads_read,leads_retrieval,business_management",
  },
  {
    platform: "instagram_business",
    label: "Instagram Business",
    subtitle: "Instagram Business Login",
    color: "#E1306C",
    colorClasses: "from-[#E1306C]/20 to-transparent border-[#E1306C]/20 text-[#E1306C]",
    defaultScopes: "instagram_business_basic,instagram_business_content_publish,instagram_business_manage_insights",
  },
  {
    platform: "x",
    label: "X",
    subtitle: "Twitter",
    color: "#000000",
    colorClasses: "from-white/10 to-transparent border-foreground/10 text-foreground",
    defaultScopes: "tweet.read tweet.write users.read offline.access",
  },
  {
    platform: "google",
    label: "Google",
    subtitle: "Google Ads & Business",
    color: "#4285F4",
    colorClasses: "from-[#4285F4]/20 to-transparent border-[#4285F4]/20 text-[#4285F4]",
    defaultScopes: "https://www.googleapis.com/auth/adwords https://www.googleapis.com/auth/business.manage",
  },
  {
    platform: "google_calendar",
    label: "Google Calendar",
    subtitle: "Calendar & Booking",
    color: "#4285F4",
    colorClasses: "from-[#4285F4]/20 to-transparent border-[#4285F4]/20 text-[#4285F4]",
    defaultScopes: "https://www.googleapis.com/auth/calendar",
    type: "oauth",
    fields: { id: "Client ID", secret: "Client Secret" },
  },
  {
    platform: "sendgrid",
    label: "SendGrid",
    subtitle: "Email Delivery",
    color: "#1A82E2",
    colorClasses: "from-[#1A82E2]/20 to-transparent border-[#1A82E2]/20 text-[#1A82E2]",
    defaultScopes: "",
    type: "apikey",
    fields: { id: "API Key Name", secret: "API Key" },
  },
  {
    platform: "twilio",
    label: "Twilio",
    subtitle: "SMS & Voice",
    color: "#F22F46",
    colorClasses: "from-[#F22F46]/20 to-transparent border-[#F22F46]/20 text-[#F22F46]",
    defaultScopes: "",
    type: "apikey_phone",
    fields: { id: "Account SID", secret: "Auth Token", extra: "Phone Number" },
  },
  {
    platform: "d-id",
    label: "D-ID",
    subtitle: "AI Video Generation",
    color: "#7C3AED",
    colorClasses: "from-[#7C3AED]/20 to-transparent border-[#7C3AED]/20 text-[#7C3AED]",
    defaultScopes: "",
    type: "apikey",
    fields: { id: "Label", secret: "API Key" },
  },
  {
    platform: "apollo",
    label: "Apollo.io",
    subtitle: "Company & Contact Intelligence (Radar)",
    color: "#FF6B35",
    colorClasses: "from-[#FF6B35]/20 to-transparent border-[#FF6B35]/20 text-[#FF6B35]",
    defaultScopes: "",
    type: "apikey",
    fields: { id: "Label", secret: "API Key" },
  },
  {
    platform: "google_places",
    label: "Google Places",
    subtitle: "Local Business Discovery (Radar)",
    color: "#34A853",
    colorClasses: "from-[#34A853]/20 to-transparent border-[#34A853]/20 text-[#34A853]",
    defaultScopes: "",
    type: "apikey",
    fields: { id: "Label", secret: "API Key" },
  },
];

interface CredentialState {
  clientId: string;
  clientSecret: string;
  redirectUri: string;
  scopes: string;
  isActive: boolean;
  configured: boolean;
}

function ApiConnectionsTab() {
  const [credentials, setCredentials] = useState<Record<string, CredentialState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const getDefaults = useCallback((platform: string): CredentialState => {
    const p = PLATFORMS.find((pl) => pl.platform === platform)!;
    const FIXED_REDIRECT_URIS: Record<string, string> = {
      instagram_business: "https://drpr.ai/api/oauth/instagram_business/callback",
    };
    const defaultRedirectUri =
      p.type === "apikey" || p.type === "apikey_phone"
        ? ""
        : FIXED_REDIRECT_URIS[platform] ?? `${window.location.origin}/api/oauth/${platform}/callback`;
    return {
      clientId: "",
      clientSecret: "",
      redirectUri: defaultRedirectUri,
      scopes: p.defaultScopes,
      isActive: false,
      configured: false,
    };
  }, []);

  const loadCredentials = useCallback(() => {
    setLoading(true);
    fetch("/api/saas-admin/platform-credentials", { credentials: "include" })
      .then((r) => r.json())
      .then((data: any[]) => {
        const map: Record<string, CredentialState> = {};
        PLATFORMS.forEach((p) => {
          const existing = data.find((d: any) => d.platform === p.platform);
          if (existing) {
            map[p.platform] = {
              clientId: existing.clientId || "",
              clientSecret: existing.clientSecret || "",
              redirectUri: existing.redirectUri || (p.type && p.type !== "oauth" ? "" : `${window.location.origin}/api/oauth/${p.platform}/callback`),
              scopes: existing.scopes || p.defaultScopes,
              isActive: existing.isActive ?? false,
              configured: true,
            };
          } else {
            map[p.platform] = getDefaults(p.platform);
          }
        });
        setCredentials(map);
      })
      .catch(() => {
        const map: Record<string, CredentialState> = {};
        PLATFORMS.forEach((p) => {
          map[p.platform] = getDefaults(p.platform);
        });
        setCredentials(map);
      })
      .finally(() => setLoading(false));
  }, [getDefaults]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const updateField = (platform: string, field: keyof CredentialState, value: string | boolean) => {
    setCredentials((prev) => ({
      ...prev,
      [platform]: { ...prev[platform], [field]: value },
    }));
  };

  const saveCredentials = async (platform: string) => {
    setSaving(platform);
    try {
      const cred = credentials[platform];
      await fetch("/api/saas-admin/platform-credentials", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform,
          clientId: cred.clientId,
          clientSecret: cred.clientSecret,
          redirectUri: cred.redirectUri,
          scopes: cred.scopes,
          isActive: cred.isActive,
        }),
        credentials: "include",
      });
      loadCredentials();
    } finally {
      setSaving(null);
    }
  };

  const deleteCredentials = async (platform: string) => {
    setDeleting(platform);
    try {
      await fetch(`/api/saas-admin/platform-credentials/${platform}`, {
        method: "DELETE",
        credentials: "include",
      });
      setCredentials((prev) => ({
        ...prev,
        [platform]: getDefaults(platform),
      }));
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-foreground/20" size={24} />
      </div>
    );
  }

  return (
    <div data-testid="api-connections-tab">
      <h2 className="text-2xl font-display font-bold mb-2">API Connections</h2>
      <p className="text-foreground/30 text-sm mb-6">
        Configure OAuth credentials for each social platform. These credentials enable your customers to connect their social accounts through secure OAuth flows.
      </p>

      <div className="space-y-4">
        {PLATFORMS.map((p) => {
          const cred = credentials[p.platform] || getDefaults(p.platform);
          return (
            <div
              key={p.platform}
              className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5"
              data-testid={`platform-card-${p.platform}`}
            >
              <div className="flex items-center justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: p.color + "20" }}
                  >
                    <Link2 size={18} style={{ color: p.color === "#000000" ? "#ffffff" : p.color }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-foreground">{p.label}</h3>
                    <p className="text-[10px] text-foreground/25">{p.subtitle}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-[10px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wide ${
                      cred.configured
                        ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/15"
                        : "bg-foreground/[0.04] text-foreground/30 border border-foreground/[0.06]"
                    }`}
                    data-testid={`status-${p.platform}`}
                  >
                    {cred.configured ? "Configured" : "Not Configured"}
                  </span>
                  <button
                    onPointerDown={() => updateField(p.platform, "isActive", !cred.isActive)}
                    className={`relative w-10 h-5 rounded-full transition-colors ${
                      cred.isActive ? "bg-blue-500" : "bg-foreground/[0.08]"
                    }`}
                    data-testid={`toggle-active-${p.platform}`}
                  >
                    <span
                      className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        cred.isActive ? "left-5" : "left-0.5"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">{p.fields?.id || "Client ID"}</label>
                  <input
                    type="text"
                    value={cred.clientId}
                    onChange={(e) => updateField(p.platform, "clientId", e.target.value)}
                    className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm focus:outline-none focus:border-blue-500/40"
                    placeholder={`Enter ${(p.fields?.id || "client ID").toLowerCase()}`}
                    data-testid={`input-client-id-${p.platform}`}
                  />
                </div>
                <div>
                  <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">{p.fields?.secret || "Client Secret"}</label>
                  <input
                    type="password"
                    value={cred.clientSecret}
                    onChange={(e) => updateField(p.platform, "clientSecret", e.target.value)}
                    className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm focus:outline-none focus:border-blue-500/40"
                    placeholder={`Enter ${(p.fields?.secret || "client secret").toLowerCase()}`}
                    data-testid={`input-client-secret-${p.platform}`}
                  />
                </div>
                {p.type !== "apikey" && (
                  <div>
                    <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">{p.type === "apikey_phone" ? (p.fields?.extra || "Phone Number") : "Redirect URI"}</label>
                    <input
                      type="text"
                      value={cred.redirectUri}
                      onChange={(e) => updateField(p.platform, "redirectUri", e.target.value)}
                      className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm font-mono text-xs focus:outline-none focus:border-blue-500/40"
                      placeholder={p.type === "apikey_phone" ? `Enter ${(p.fields?.extra || "phone number").toLowerCase()}` : undefined}
                      data-testid={`input-redirect-uri-${p.platform}`}
                    />
                  </div>
                )}
                {(!p.type || p.type === "oauth") && (
                  <div>
                    <label className="block text-[10px] text-foreground/25 uppercase tracking-wider mb-1.5">Scopes</label>
                    <input
                      type="text"
                      value={cred.scopes}
                      onChange={(e) => updateField(p.platform, "scopes", e.target.value)}
                      className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground text-sm font-mono text-xs focus:outline-none focus:border-blue-500/40"
                      data-testid={`input-scopes-${p.platform}`}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 flex-wrap">
                <button
                  onPointerDown={() => saveCredentials(p.platform)}
                  disabled={saving === p.platform}
                  className="flex items-center gap-1.5 px-4 py-2 bg-blue-600/20 text-blue-400 rounded-xl text-xs font-medium hover:bg-blue-600/30 transition-colors disabled:opacity-50"
                  data-testid={`button-save-credentials-${p.platform}`}
                >
                  {saving === p.platform ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                  Save Credentials
                </button>
                {cred.configured && (
                  <button
                    onPointerDown={() => deleteCredentials(p.platform)}
                    disabled={deleting === p.platform}
                    className="flex items-center gap-1.5 px-4 py-2 bg-red-500/10 text-red-400 rounded-xl text-xs font-medium hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    data-testid={`button-delete-credentials-${p.platform}`}
                  >
                    {deleting === p.platform ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    Delete
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

interface SaasPackage {
  id: number;
  name: string;
  description: string | null;
  price: string;
  billingCycle: string;
  features: string[];
  maxUsers: number;
  maxWorkspaces: number;
  aiCallsIncluded: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function AiControlsTab() {
  const [subTab, setSubTab] = useState<"pricing" | "usage" | "guardrails" | "providers" | "calculator" | "spy" | "feature-flags" | "cost-ws">("usage");

  return (
    <div className="space-y-6" data-testid="ai-controls-tab">
      <div className="flex gap-2 border-b border-foreground/[0.06] pb-4 overflow-x-auto">
        {[
          { id: "usage", label: "Usage", icon: TrendingUp },
          { id: "spy", label: "API Spy", icon: Activity },
          { id: "pricing", label: "Retail Pricing", icon: DollarSign },
          { id: "guardrails", label: "Guardrails", icon: Shield },
          { id: "providers", label: "Providers", icon: Link2 },
          { id: "calculator", label: "Margin Calculator", icon: BarChart3 },
          { id: "feature-flags", label: "Kill Switches", icon: Zap },
          { id: "cost-ws", label: "Cost by WS", icon: Layers },
        ].map((t) => (
          <button
            key={t.id}
            onPointerDown={() => setSubTab(t.id as any)}
            data-testid={`subtab-${t.id}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
              subTab === t.id
                ? "bg-blue-500/10 text-blue-400 border border-blue-500/20"
                : "text-foreground/40 hover:text-foreground/70 hover:bg-foreground/5 border border-transparent"
            }`}
          >
            <t.icon size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {subTab === "usage" && <AiUsageTab />}
      {subTab === "spy" && <AiSpyTab />}
      {subTab === "pricing" && <AiPricingTab />}
      {subTab === "guardrails" && <AiGuardrailsTab />}
      {subTab === "providers" && <AiProvidersTab />}
      {subTab === "calculator" && <AiCalculatorTab />}
      {subTab === "feature-flags" && <FeatureFlagsTab />}
      {subTab === "cost-ws" && <WorkspaceCostTab />}
    </div>
  );
}

function FeatureFlagsTab() {
  const [flags, setFlags] = useState<Array<{
    name: string;
    enabled: boolean;
    description: string;
    updated_at: string | null;
    updated_by: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/saas-admin/feature-flags", { credentials: "include" })
      .then(r => r.json())
      .then(data => { setFlags(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const toggle = async (flag: { name: string; enabled: boolean }) => {
    setToggling(prev => new Set(prev).add(flag.name));
    setError(null);
    try {
      const res = await fetch(`/api/saas-admin/feature-flags/${flag.name}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: !flag.enabled }),
      });
      if (res.ok) {
        const updated = await res.json();
        setFlags(prev => prev.map(f => f.name === flag.name ? { ...f, enabled: updated.enabled } : f));
      } else {
        setError(`Failed to toggle ${flag.name}`);
      }
    } catch {
      setError(`Error toggling ${flag.name}`);
    } finally {
      setToggling(prev => { const next = new Set(prev); next.delete(flag.name); return next; });
    }
  };

  return (
    <div className="space-y-4" data-testid="feature-flags-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Platform Kill Switches</h3>
          <p className="text-xs text-foreground/40 mt-0.5">Toggle features without redeploying</p>
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {loading ? (
        <div className="flex items-center gap-2 text-foreground/40 text-sm py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading flags…
        </div>
      ) : flags.length === 0 ? (
        <p className="text-sm text-foreground/40 py-8 text-center">No feature flags found.</p>
      ) : (
        <div className="rounded-xl border border-foreground/[0.06] overflow-hidden">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-foreground/[0.06] bg-foreground/[0.02]">
                <th className="text-left px-4 py-2.5 text-foreground/40 font-medium">Feature</th>
                <th className="text-left px-4 py-2.5 text-foreground/40 font-medium">Status</th>
                <th className="text-left px-4 py-2.5 text-foreground/40 font-medium">Description</th>
                <th className="text-left px-4 py-2.5 text-foreground/40 font-medium">Last Changed</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {flags.map(flag => (
                <tr key={flag.name} className="border-b border-foreground/[0.04] last:border-0 hover:bg-foreground/[0.02] transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-foreground/80">{flag.name}</td>
                  <td className="px-4 py-3">
                    {flag.enabled
                      ? <span className="px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 border border-green-500/20 text-xs font-medium">Enabled</span>
                      : <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/20 text-xs font-medium">Disabled</span>
                    }
                  </td>
                  <td className="px-4 py-3 text-foreground/50">{flag.description || "—"}</td>
                  <td className="px-4 py-3 text-foreground/40">
                    {flag.updated_at ? new Date(flag.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onPointerDown={() => toggle(flag)}
                      disabled={toggling.has(flag.name)}
                      className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 ${
                        flag.enabled
                          ? "bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20"
                          : "bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20"
                      }`}
                    >
                      {toggling.has(flag.name) ? <Loader2 size={12} className="animate-spin inline" /> : flag.enabled ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function WorkspaceCostTab() {
  const [rows, setRows] = useState<Array<{
    workspaceId: number;
    workspaceName: string | null;
    totalCost: number;
    totalTokens: number;
    callCount: number;
    lastCallAt: string | null;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/saas-admin/ai-cost-per-workspace?days=${days}`, { credentials: "include" })
      .then(r => r.json())
      .then(data => { setRows(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [days]);

  return (
    <div className="space-y-4" data-testid="workspace-cost-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-foreground">AI Cost by Workspace</h3>
          <p className="text-xs text-foreground/50 mt-0.5">Top 20 workspaces by Gemini spend (true Google cost)</p>
        </div>
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="text-xs bg-background border border-foreground/10 rounded-lg px-3 py-1.5 text-foreground/70"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-foreground/40 text-sm py-8 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading...
        </div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-foreground/40 text-center py-8">No AI usage recorded in this period</p>
      ) : (
        <div className="rounded-xl border border-foreground/[0.06] overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-foreground/[0.06] bg-foreground/[0.02]">
                <th className="text-left px-4 py-3 text-xs font-semibold text-foreground/40 uppercase tracking-wider">Workspace</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground/40 uppercase tracking-wider">Cost (USD)</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground/40 uppercase tracking-wider">Tokens</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground/40 uppercase tracking-wider">Calls</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-foreground/40 uppercase tracking-wider">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.workspaceId} className={`border-b border-foreground/[0.04] ${i % 2 === 0 ? "" : "bg-foreground/[0.01]"}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{r.workspaceName ?? `Workspace #${r.workspaceId}`}</div>
                    <div className="text-xs text-foreground/40">ID {r.workspaceId}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-green-400">${Number(r.totalCost).toFixed(4)}</td>
                  <td className="px-4 py-3 text-right text-foreground/70">{Number(r.totalTokens).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-foreground/70">{Number(r.callCount).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right text-foreground/50 text-xs">{r.lastCallAt ? new Date(r.lastCallAt).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AiSpyTab() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/saas-admin/ai-spy?days=${days}`, { credentials: "include" });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, [days]);

  useEffect(() => { loadData(); }, [loadData]);

  const fmtCost = (n: number) => `$${n.toFixed(4)}`;
  const fmtPct = (n: number) => `${n.toFixed(1)}%`;

  if (loading) return (
    <div className="flex items-center justify-center py-16 text-foreground/40">
      <Loader2 className="animate-spin mr-2" size={18} />
      Loading API Spy data…
    </div>
  );
  if (!data) return <div className="text-foreground/40 text-sm py-8">Failed to load.</div>;

  const { todaySummary, dailySummary, hourlyToday, workspaceToday, endpointBreakdown = [], serviceBreakdown = [] } = data;

  const maxHourlyCost = Math.max(...hourlyToday.map((h: any) => h.googleCost), 0.000001);
  const maxDailyCost = Math.max(...dailySummary.map((d: any) => d.googleCost), 0.000001);

  return (
    <div className="space-y-6" data-testid="ai-spy-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-foreground">API Spy — Live Gemini Spend</h3>
          <p className="text-xs text-foreground/40 mt-0.5">Tracks every Gemini call. System = background/scheduler calls (logged under system user).</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="spy-days-select"
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="text-xs bg-background/50 border border-foreground/[0.08] rounded-lg px-2 py-1.5 text-foreground/70"
          >
            {[1, 3, 7, 14, 30].map(d => <option key={d} value={d}>Last {d} day{d > 1 ? "s" : ""}</option>)}
          </select>
          <button
            data-testid="spy-refresh"
            onPointerDown={loadData}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-foreground/[0.08] text-xs text-foreground/50 hover:text-foreground/80 hover:bg-foreground/5 transition-all"
          >
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      {todaySummary.systemAlertThreshold && (
        <div className="flex items-start gap-3 px-4 py-3 rounded-xl border border-orange-500/30 bg-orange-500/10" data-testid="spy-system-alert">
          <AlertTriangle size={16} className="text-orange-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-orange-300">System Spend Alert</p>
            <p className="text-xs text-orange-300/70 mt-0.5">
              System background spend is {fmtPct(todaySummary.systemSpendPercent)} of today's total Google cost — above the 10% threshold.
              Review background jobs or scheduled tasks.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Today's Calls", value: todaySummary.calls.toLocaleString(), testId: "spy-today-calls" },
          { label: "Today's Google Cost", value: fmtCost(todaySummary.googleCost), testId: "spy-today-cost" },
          { label: "User Calls", value: todaySummary.userCalls.toLocaleString(), testId: "spy-today-user-calls" },
          { label: "System Calls", value: `${todaySummary.systemCalls.toLocaleString()} (${fmtPct(todaySummary.systemCallPercent)} of calls)`, testId: "spy-today-system-calls" },
        ].map(stat => (
          <div key={stat.label} className="bg-foreground/[0.03] border border-foreground/[0.06] rounded-xl p-4" data-testid={stat.testId}>
            <p className="text-xs text-foreground/40 mb-1">{stat.label}</p>
            <p className="text-lg font-semibold text-foreground">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4 space-y-2" data-testid="spy-logged-vs-system-donut">
          <h4 className="text-sm font-medium text-foreground/70 mb-3">Today: Logged vs System</h4>
          <div className="flex items-center gap-4">
            <div className="relative w-24 h-24 shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="currentColor" strokeWidth="3.5" className="text-foreground/[0.05]" />
                {todaySummary.calls > 0 && (
                  <circle
                    cx="18" cy="18" r="15.9155" fill="none"
                    stroke="#f97316" strokeWidth="3.5"
                    strokeDasharray={`${todaySummary.systemCallPercent} ${100 - todaySummary.systemCallPercent}`}
                    strokeLinecap="round"
                  />
                )}
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-foreground">{fmtPct(todaySummary.systemCallPercent)}</span>
              </div>
            </div>
            <div className="space-y-2 text-xs text-foreground/60">
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-blue-400 shrink-0" />
                <span>Logged (user): {todaySummary.userCalls.toLocaleString()} calls</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full bg-orange-400 shrink-0" />
                <span>System (background): {todaySummary.systemCalls.toLocaleString()} calls</span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4" data-testid="spy-workspace-today">
          <h4 className="text-sm font-medium text-foreground/70 mb-3">Top Workspaces Today (by cost)</h4>
          {workspaceToday.length === 0 ? (
            <p className="text-xs text-foreground/30 py-4 text-center">No workspace calls today yet.</p>
          ) : (
            <div className="overflow-x-auto max-h-52">
              <table className="w-full text-xs text-foreground/60">
                <thead>
                  <tr className="border-b border-foreground/[0.05]">
                    <th className="text-left py-1 font-medium">Workspace</th>
                    <th className="text-right py-1 font-medium">Calls</th>
                    <th className="text-right py-1 font-medium">Input Tok</th>
                    <th className="text-right py-1 font-medium">Output Tok</th>
                    <th className="text-right py-1 font-medium">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {workspaceToday.slice(0, 10).map((ws: any, i: number) => (
                    <tr key={ws.workspaceId ?? "null"} className="border-b border-foreground/[0.03] hover:bg-foreground/[0.02]" data-testid={`spy-workspace-row-${i}`}>
                      <td className="py-1.5 text-foreground/70">
                        {ws.workspaceId ? `WS #${ws.workspaceId}` : "— no workspace"}
                      </td>
                      <td className="text-right py-1.5">{ws.calls}</td>
                      <td className="text-right py-1.5">{(ws.inputTokens || 0).toLocaleString()}</td>
                      <td className="text-right py-1.5">{(ws.outputTokens || 0).toLocaleString()}</td>
                      <td className="text-right py-1.5 font-mono text-blue-400">{fmtCost(ws.googleCost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4" data-testid="spy-hourly-chart">
        <h4 className="text-sm font-medium text-foreground/70 mb-4">Hourly Calls Today</h4>
        <div className="flex items-end gap-0.5 h-24">
          {hourlyToday.map((h: any) => {
            const heightPct = h.googleCost > 0 ? (h.googleCost / maxHourlyCost) * 100 : 0;
            const systemPct = h.calls > 0 ? (h.systemCalls / h.calls) * 100 : 0;
            return (
              <div
                key={h.hour}
                className="flex-1 flex flex-col items-center justify-end group relative"
                data-testid={`spy-hour-bar-${h.hour}`}
                title={`${h.hour}:00 — ${h.calls} calls, ${fmtCost(h.googleCost)} (${fmtPct(systemPct)} system)`}
              >
                <div
                  className="w-full rounded-t transition-all bg-blue-500/50 hover:bg-blue-400/70"
                  style={{ height: `${Math.max(heightPct, h.calls > 0 ? 4 : 0)}%` }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[10px] text-foreground/30 mt-1">
          <span>0:00</span><span>6:00</span><span>12:00</span><span>18:00</span><span>23:00</span>
        </div>
      </div>

      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4" data-testid="spy-daily-summary">
        <h4 className="text-sm font-medium text-foreground/70 mb-4">{days}-Day Summary</h4>
        {dailySummary.length === 0 ? (
          <p className="text-xs text-foreground/30 py-4 text-center">No data for this period.</p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-end gap-0.5 h-20 mb-1">
              {dailySummary.map((d: any, i: number) => {
                const heightPct = d.googleCost > 0 ? (d.googleCost / maxDailyCost) * 100 : 0;
                return (
                  <div
                    key={d.date}
                    className="flex-1 flex flex-col items-center justify-end"
                    title={`${d.date} — ${d.calls} calls, ${fmtCost(d.googleCost)}`}
                    data-testid={`spy-daily-bar-${i}`}
                  >
                    <div
                      className="w-full rounded-t bg-blue-500/40 hover:bg-blue-400/60 transition-all"
                      style={{ height: `${Math.max(heightPct, d.calls > 0 ? 6 : 0)}%` }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-foreground/60">
                <thead>
                  <tr className="border-b border-foreground/[0.05]">
                    <th className="text-left py-1 font-medium">Date</th>
                    <th className="text-right py-1 font-medium">Calls</th>
                    <th className="text-right py-1 font-medium">Input Tok</th>
                    <th className="text-right py-1 font-medium">Output Tok</th>
                    <th className="text-right py-1 font-medium">Google Cost</th>
                    <th className="text-right py-1 font-medium">System%</th>
                  </tr>
                </thead>
                <tbody>
                  {dailySummary.map((d: any) => {
                    const sysPct = d.calls > 0 ? (d.systemCalls / d.calls) * 100 : 0;
                    return (
                      <tr key={d.date} className="border-b border-foreground/[0.03] hover:bg-foreground/[0.02]" data-testid={`spy-daily-row-${d.date}`}>
                        <td className="py-1.5 font-mono">{d.date}</td>
                        <td className="text-right py-1.5">{d.calls.toLocaleString()}</td>
                        <td className="text-right py-1.5">{(d.inputTokens || 0).toLocaleString()}</td>
                        <td className="text-right py-1.5">{(d.outputTokens || 0).toLocaleString()}</td>
                        <td className="text-right py-1.5 font-mono text-blue-400">{fmtCost(d.googleCost)}</td>
                        <td className={`text-right py-1.5 ${sysPct > 10 ? "text-orange-400 font-semibold" : ""}`}>{fmtPct(sysPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4" data-testid="spy-service-breakdown">
        <h4 className="text-sm font-medium text-foreground/70 mb-4">Cost by Service ({days}-day window)</h4>
        {serviceBreakdown.length === 0 ? (
          <p className="text-xs text-foreground/30 py-4 text-center">No service data for this period.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-foreground/60">
              <thead>
                <tr className="border-b border-foreground/[0.05]">
                  <th className="text-left py-1 font-medium">Service</th>
                  <th className="text-right py-1 font-medium">Calls</th>
                  <th className="text-right py-1 font-medium">Input Tok</th>
                  <th className="text-right py-1 font-medium">Output Tok</th>
                  <th className="text-right py-1 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {serviceBreakdown.map((s: any, i: number) => (
                  <tr key={s.service} className="border-b border-foreground/[0.03] hover:bg-foreground/[0.02]" data-testid={`spy-service-row-${i}`}>
                    <td className="py-1.5 font-mono text-foreground/70">{s.service}</td>
                    <td className="text-right py-1.5">{s.calls.toLocaleString()}</td>
                    <td className="text-right py-1.5">{(s.inputTokens || 0).toLocaleString()}</td>
                    <td className="text-right py-1.5">{(s.outputTokens || 0).toLocaleString()}</td>
                    <td className="text-right py-1.5 font-mono text-blue-400">{fmtCost(s.googleCost)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-foreground/[0.08]">
                  <td className="py-1.5 font-medium text-foreground/70">Total</td>
                  <td className="text-right py-1.5 font-medium">{serviceBreakdown.reduce((s: number, r: any) => s + r.calls, 0).toLocaleString()}</td>
                  <td className="text-right py-1.5 font-medium">{serviceBreakdown.reduce((s: number, r: any) => s + (r.inputTokens || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-1.5 font-medium">{serviceBreakdown.reduce((s: number, r: any) => s + (r.outputTokens || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-1.5 font-mono font-medium text-blue-400">{fmtCost(serviceBreakdown.reduce((s: number, r: any) => s + r.googleCost, 0))}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-4" data-testid="spy-endpoint-breakdown">
        <h4 className="text-sm font-medium text-foreground/70 mb-4">Top Endpoints by Cost ({days}-day window)</h4>
        {endpointBreakdown.length === 0 ? (
          <p className="text-xs text-foreground/30 py-4 text-center">No endpoint data — add callerTag to Gemini calls</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-foreground/60">
              <thead>
                <tr className="border-b border-foreground/[0.05]">
                  <th className="text-left py-1 font-medium">Endpoint</th>
                  <th className="text-right py-1 font-medium">Calls</th>
                  <th className="text-right py-1 font-medium">Cost</th>
                </tr>
              </thead>
              <tbody>
                {endpointBreakdown.slice(0, 15).map((ep: any, i: number) => (
                  <tr key={ep.endpoint} className="border-b border-foreground/[0.03] hover:bg-foreground/[0.02]" data-testid={`spy-endpoint-row-${i}`}>
                    <td className="py-1.5 font-mono text-foreground/70">{ep.endpoint}</td>
                    <td className="text-right py-1.5">{ep.calls.toLocaleString()}</td>
                    <td className="text-right py-1.5 font-mono text-blue-400">{fmtCost(ep.googleCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function AiProvidersTab() {
  const [providers, setProviders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: "",
    baseUrl: "",
    apiKey: "",
    modelId: "",
    providerType: "openai",
    isActive: true,
  });

  const loadProviders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/ai-providers", { credentials: "include" });
      if (res.ok) setProviders(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const url = editingProvider ? `/api/saas-admin/ai-providers/${editingProvider.id}` : "/api/saas-admin/ai-providers";
      const method = editingProvider ? "PUT" : "POST";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
        credentials: "include",
      });
      if (res.ok) {
        setShowForm(false);
        setEditingProvider(null);
        loadProviders();
      }
    } finally { setSaving(false); }
  };

  const deleteProvider = async (id: number) => {
    if (!confirm("Are you sure?")) return;
    await fetch(`/api/saas-admin/ai-providers/${id}`, { method: "DELETE", credentials: "include" });
    loadProviders();
  };

  const templates: Record<string, any> = {
    gemini: { name: "Gemini (Google)", baseUrl: "https://generativelanguage.googleapis.com", providerType: "google", modelId: "gemini-1.5-pro" },
    openai: { name: "OpenAI", baseUrl: "https://api.openai.com/v1", providerType: "openai", modelId: "gpt-4o" },
    anthropic: { name: "Anthropic", baseUrl: "https://api.anthropic.com/v1", providerType: "anthropic", modelId: "claude-3-5-sonnet-latest" },
    openrouter: { name: "OpenRouter", baseUrl: "https://openrouter.ai/api/v1", providerType: "openai", modelId: "meta-llama/llama-3-70b" },
    mistral: { name: "Mistral", baseUrl: "https://api.mistral.ai/v1", providerType: "openai", modelId: "mistral-large-latest" },
    groq: { name: "Groq", baseUrl: "https://api.groq.com/openai/v1", providerType: "openai", modelId: "llama3-70b-8192" },
  };

  const applyTemplate = (key: string) => {
    const t = templates[key];
    setForm({ ...form, ...t });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">AI Providers</h3>
        <button
          onPointerDown={() => {
            setForm({ name: "", baseUrl: "", apiKey: "", modelId: "", providerType: "openai", isActive: true });
            setEditingProvider(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold"
        >
          <Plus size={16} /> Add Provider
        </button>
      </div>

      {showForm && (
        <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-6 space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="font-bold">{editingProvider ? "Edit Provider" : "Add Provider"}</h4>
            <div className="flex gap-2">
              {!editingProvider && Object.keys(templates).map(k => (
                <button key={k} onPointerDown={() => applyTemplate(k)} className="px-2 py-1 bg-foreground/5 rounded text-[10px] uppercase font-bold hover:bg-foreground/10 transition-colors">
                  {k}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] uppercase font-bold text-foreground/30 block mb-1">Name</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm" placeholder="My Provider" />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] uppercase font-bold text-foreground/30 block mb-1">Type</label>
              <select value={form.providerType} onChange={e => setForm({ ...form, providerType: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm appearance-none">
                <option value="openai">OpenAI Compatible</option>
                <option value="google">Google Gemini</option>
                <option value="anthropic">Anthropic</option>
              </select>
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] uppercase font-bold text-foreground/30 block mb-1">Base URL</label>
              <input value={form.baseUrl} onChange={e => setForm({ ...form, baseUrl: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" placeholder="https://..." />
            </div>
            <div className="col-span-2 sm:col-span-1">
              <label className="text-[10px] uppercase font-bold text-foreground/30 block mb-1">Default Model ID</label>
              <input value={form.modelId} onChange={e => setForm({ ...form, modelId: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" placeholder="gpt-4o" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase font-bold text-foreground/30 block mb-1">API Key</label>
              <input type="password" value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" placeholder="sk-..." />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <button onPointerDown={() => setShowForm(false)} className="px-4 py-2 text-sm text-foreground/40">Cancel</button>
            <button onPointerDown={handleSubmit} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold flex items-center gap-2">
              {saving && <Loader2 size={14} className="animate-spin" />}
              {editingProvider ? "Update" : "Create"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-foreground/10" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {providers.map(p => (
            <div key={p.id} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-sm">{p.name}</h4>
                  <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${p.isActive ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}>
                    {p.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="text-[10px] text-foreground/30 font-mono mt-1">{p.modelId}</p>
                <p className="text-[10px] text-foreground/20 truncate max-w-[200px]">{p.baseUrl}</p>
              </div>
              <div className="flex gap-2">
                <button
                  onPointerDown={() => {
                    setEditingProvider(p);
                    setForm({ ...p, apiKey: "" }); // Don't show existing key for security
                    setShowForm(true);
                  }}
                  className="p-2 hover:bg-foreground/5 rounded-lg text-foreground/40 transition-colors"
                >
                  <Pencil size={14} />
                </button>
                <button onPointerDown={() => deleteProvider(p.id)} className="p-2 hover:bg-red-500/10 rounded-lg text-foreground/40 hover:text-red-400 transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {providers.length === 0 && <div className="col-span-2 text-center py-10 text-foreground/20 text-sm italic">No custom AI providers configured.</div>}
        </div>
      )}
    </div>
  );
}

function AiCalculatorTab() {
  const [tokens, setTokens] = useState(1000);
  const [inputCost, setInputCost] = useState(0.00015);
  const [outputCost, setOutputCost] = useState(0.0006);
  const [margin, setMargin] = useState(20);

  const baseCost = (tokens / 1000) * (inputCost + outputCost);
  const marginAmt = baseCost * (margin / 100);
  const totalBilled = baseCost + marginAmt;

  return (
    <div className="space-y-6">
      <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-6">
        <h3 className="font-display font-bold text-lg mb-4">Live Margin Calculator</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-bold text-foreground/30 block">Tokens</label>
            <input type="number" value={tokens} onChange={e => setTokens(Number(e.target.value))} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm" />
            <input type="range" min={100} max={100000} step={100} value={tokens} onChange={e => setTokens(Number(e.target.value))} className="w-full accent-blue-600" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-bold text-foreground/30 block">Input $/1k</label>
            <input type="number" step="0.00001" value={inputCost} onChange={e => setInputCost(Number(e.target.value))} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-bold text-foreground/30 block">Output $/1k</label>
            <input type="number" step="0.00001" value={outputCost} onChange={e => setOutputCost(Number(e.target.value))} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] uppercase font-bold text-foreground/30 block">Margin %</label>
            <input type="number" value={margin} onChange={e => setMargin(Number(e.target.value))} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2 text-sm font-mono" />
            <input type="range" min={0} max={500} value={margin} onChange={e => setMargin(Number(e.target.value))} className="w-full accent-blue-600" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
          <div className="bg-foreground/[0.04] p-5 rounded-2xl border border-foreground/5">
            <p className="text-[10px] uppercase font-bold text-foreground/25 mb-1">Base Cost</p>
            <p className="text-2xl font-display font-bold text-foreground">${baseCost.toFixed(4)}</p>
          </div>
          <div className="bg-blue-500/5 p-5 rounded-2xl border border-blue-500/10">
            <p className="text-[10px] uppercase font-bold text-blue-400/50 mb-1">Drpr Margin</p>
            <p className="text-2xl font-display font-bold text-blue-400">${marginAmt.toFixed(4)}</p>
          </div>
          <div className="bg-emerald-500/5 p-5 rounded-2xl border border-emerald-500/10">
            <p className="text-[10px] uppercase font-bold text-emerald-400/50 mb-1">Total Billed</p>
            <p className="text-2xl font-display font-bold text-emerald-400">${totalBilled.toFixed(4)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function parseFeatureFlags(features: string[] = []) {
  const flags = {
    trialCredits: 0,
    trialDays: 0,
    peteEnabled: false,
    rogersEnabled: false,
    videoEnabled: false,
    mcpEnabled: false,
    prioritySupport: false,
  };

  features.forEach(f => {
    const [key, val] = f.split(":");
    if (key === "trial_credits") flags.trialCredits = parseInt(val) || 0;
    else if (key === "trial_days") flags.trialDays = parseInt(val) || 0;
    else if (key === "pete" && val === "enabled") flags.peteEnabled = true;
    else if (key === "rogers" && val === "enabled") flags.rogersEnabled = true;
    else if (key === "video" && val === "enabled") flags.videoEnabled = true;
    else if (key === "mcp" && val === "enabled") flags.mcpEnabled = true;
    else if (key === "priority_support" && val === "enabled") flags.prioritySupport = true;
  });

  return flags;
}

function serializeFeatureFlags(flags: any) {
  const features: string[] = [];
  if (flags.trialCredits > 0) features.push(`trial_credits:${flags.trialCredits}`);
  if (flags.trialDays > 0) features.push(`trial_days:${flags.trialDays}`);
  if (flags.peteEnabled) features.push("pete:enabled");
  if (flags.rogersEnabled) features.push("rogers:enabled");
  if (flags.videoEnabled) features.push("video:enabled");
  if (flags.mcpEnabled) features.push("mcp:enabled");
  if (flags.prioritySupport) features.push("priority_support:enabled");
  return features;
}

function PackagesTab() {
  const [packages, setPackages] = useState<SaasPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", price: "", billingCycle: "monthly",
    features: [] as string[], maxUsers: 1, maxWorkspaces: 1,
    aiCallsIncluded: 100, isActive: true, stripePriceId: "",
    featureFlags: parseFeatureFlags([]),
  });

  const [promoCodes, setPromoCodes] = useState<any[]>([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoForm, setPromoForm] = useState({
    code: "", planId: "", bonusCredits: 0, discountPercent: 0,
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    maxUses: 100,
  });

  const fetchPackages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/packages", { credentials: "include" });
      if (res.ok) setPackages(await res.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchPromoCodes = useCallback(async () => {
    setPromoLoading(true);
    try {
      const res = await fetch("/api/saas-admin/invite-codes", { credentials: "include" });
      if (res.ok) setPromoCodes(await res.json());
    } catch { /* ignore */ } finally { setPromoLoading(false); }
  }, []);

  useEffect(() => { 
    fetchPackages();
    fetchPromoCodes();
  }, [fetchPackages, fetchPromoCodes]);

  function resetForm() {
    setForm({ 
      name: "", description: "", price: "", billingCycle: "monthly", 
      features: [], maxUsers: 1, maxWorkspaces: 1, aiCallsIncluded: 100, 
      isActive: true, stripePriceId: "",
      featureFlags: parseFeatureFlags([]),
    });
    setEditingId(null);
    setShowForm(false);
  }

  function startEdit(pkg: SaasPackage) {
    setForm({
      name: pkg.name, description: pkg.description || "", price: pkg.price,
      billingCycle: pkg.billingCycle, features: pkg.features || [],
      maxUsers: pkg.maxUsers, maxWorkspaces: pkg.maxWorkspaces,
      aiCallsIncluded: pkg.aiCallsIncluded, isActive: pkg.isActive,
      stripePriceId: (pkg as any).stripePriceId || "",
      featureFlags: parseFeatureFlags(pkg.features || []),
    });
    setEditingId(pkg.id);
    setShowForm(true);
  }

  async function handleSubmit() {
    if (!form.name.trim() || !form.price.trim()) return;
    setSaving(true);
    try {
      const url = editingId ? `/api/saas-admin/packages/${editingId}` : "/api/saas-admin/packages";
      const method = editingId ? "PUT" : "POST";
      
      const submissionData = {
        ...form,
        features: serializeFeatureFlags(form.featureFlags),
        maxUsers: Number(form.maxUsers),
        maxWorkspaces: Number(form.maxWorkspaces),
        aiCallsIncluded: Number(form.aiCallsIncluded)
      };

      const res = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submissionData),
      });
      if (res.ok) { resetForm(); fetchPackages(); }
    } catch { /* ignore */ } finally { setSaving(false); }
  }

  async function handleDelete(id: number) {
    if (!confirm("Delete this package?")) return;
    await fetch(`/api/saas-admin/packages/${id}`, { method: "DELETE", credentials: "include" });
    fetchPackages();
  }

  async function handlePromoSubmit() {
    if (!promoForm.code.trim()) return;
    setPromoSaving(true);
    try {
      const res = await fetch("/api/saas-admin/invite-codes", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code: promoForm.code,
          plan: promoForm.planId,
          maxUses: Number(promoForm.maxUses),
          expiresInHours: Math.ceil((new Date(promoForm.expiresAt).getTime() - Date.now()) / (1000 * 60 * 60)),
          // Added these fields assuming API support or future-proofing
          bonus_credits: Number(promoForm.bonusCredits),
          discount: Number(promoForm.discountPercent),
        }),
      });
      if (res.ok) {
        setShowPromoForm(false);
        fetchPromoCodes();
        setPromoForm({
          code: "", planId: "", bonusCredits: 0, discountPercent: 0,
          expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          maxUses: 100,
        });
      }
    } catch { /* ignore */ } finally { setPromoSaving(false); }
  }

  async function handlePromoDelete(id: number) {
    if (!confirm("Delete this promo code?")) return;
    await fetch(`/api/saas-admin/invite-codes/${id}`, { method: "DELETE", credentials: "include" });
    fetchPromoCodes();
  }

  return (
    <div className="space-y-6" data-testid="packages-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground">Packages</h2>
          <p className="text-sm text-foreground/30 mt-1">Manage subscription packages and pricing tiers.</p>
        </div>
        {!showForm && (
          <button
            onPointerDown={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground rounded-xl text-sm font-bold transition-all"
            data-testid="button-create-package"
          >
            <Plus size={16} />
            Create Package
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5" data-testid="package-form">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-display font-bold text-foreground">{editingId ? "Edit Package" : "New Package"}</h3>
            <button onPointerDown={resetForm} className="text-foreground/30 hover:text-foreground/60 transition-colors" data-testid="button-cancel-package">
              <X size={18} />
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Name *</label>
              <input
                data-testid="input-package-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Starter, Pro, Enterprise"
                className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-foreground/20"
              />
            </div>
            <div>
              <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Price *</label>
              <div className="flex items-center gap-2">
                <DollarSign size={14} className="text-foreground/20 shrink-0" />
                <input
                  data-testid="input-package-price"
                  value={form.price}
                  onChange={(e) => setForm({ ...form, price: e.target.value })}
                  placeholder="49.99"
                  className="flex-1 bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-foreground/20"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Description</label>
            <textarea
              data-testid="input-package-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Brief description of this package..."
              rows={2}
              className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground resize-none placeholder:text-foreground/20"
            />
          </div>

          <div>
            <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Billing Cycle</label>
            <div className="flex gap-3" data-testid="select-billing-cycle">
              {["monthly", "yearly"].map((cycle) => (
                <label key={cycle} className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${form.billingCycle === cycle ? "bg-blue-600/20 border-blue-500/30 text-blue-400" : "bg-foreground/[0.02] border-foreground/[0.06] text-foreground/50"}`}>
                  <input type="radio" name="billingCycle" value={cycle} checked={form.billingCycle === cycle} onChange={(e) => setForm({ ...form, billingCycle: e.target.value })} className="accent-blue-500" />
                  <span className="text-sm font-medium capitalize">{cycle}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-bold text-foreground/80 border-b border-foreground/10 pb-2">Feature Flags</h4>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="text-[10px] text-foreground/40 uppercase tracking-widest font-bold block mb-1.5">Trial AI Credits on Signup</label>
                  <input
                    type="number"
                    value={form.featureFlags.trialCredits}
                    onChange={(e) => setForm({ ...form, featureFlags: { ...form.featureFlags, trialCredits: parseInt(e.target.value) || 0 }})}
                    className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                    data-testid="input-trial-credits"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground/40 uppercase tracking-widest font-bold block mb-1.5">Trial Duration (days)</label>
                  <input
                    type="number"
                    value={form.featureFlags.trialDays}
                    onChange={(e) => setForm({ ...form, featureFlags: { ...form.featureFlags, trialDays: parseInt(e.target.value) || 0 }})}
                    className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                    data-testid="input-trial-days"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground/40 uppercase tracking-widest font-bold block mb-1.5">Max Workspaces</label>
                  <input
                    type="number"
                    value={form.maxWorkspaces}
                    onChange={(e) => setForm({ ...form, maxWorkspaces: parseInt(e.target.value) || 1 })}
                    className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                    data-testid="input-max-workspaces"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground/40 uppercase tracking-widest font-bold block mb-1.5">Max Users</label>
                  <input
                    type="number"
                    value={form.maxUsers}
                    onChange={(e) => setForm({ ...form, maxUsers: parseInt(e.target.value) || 1 })}
                    className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                    data-testid="input-max-users"
                  />
                </div>
                <div>
                  <label className="text-[10px] text-foreground/40 uppercase tracking-widest font-bold block mb-1.5">AI Calls Included</label>
                  <input
                    type="number"
                    value={form.aiCallsIncluded}
                    onChange={(e) => setForm({ ...form, aiCallsIncluded: parseInt(e.target.value) || 0 })}
                    className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-2.5 text-sm focus:border-blue-500 outline-none text-foreground"
                    data-testid="input-ai-calls"
                  />
                </div>
              </div>

              <div className="space-y-3">
                {[
                  { key: "peteEnabled", label: "Pete / Ad Manager" },
                  { key: "rogersEnabled", label: "Rogers / Lead Desk" },
                  { key: "videoEnabled", label: "Video Generation" },
                  { key: "mcpEnabled", label: "External Agent API / MCP" },
                  { key: "prioritySupport", label: "Priority Support" },
                ].map((toggle) => (
                  <div key={toggle.key} className="flex items-center justify-between p-3 rounded-xl bg-foreground/[0.02] border border-foreground/[0.06]">
                    <span className="text-sm font-medium text-foreground/70">{toggle.label}</span>
                    <button
                      onPointerDown={() => setForm({ 
                        ...form, 
                        featureFlags: { ...form.featureFlags, [toggle.key]: !(form.featureFlags as any)[toggle.key] }
                      })}
                      className={`relative w-10 h-5 rounded-full transition-colors ${(form.featureFlags as any)[toggle.key] ? "bg-blue-600" : "bg-foreground/10"}`}
                      data-testid={`toggle-feature-${toggle.key}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${(form.featureFlags as any)[toggle.key] ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Stripe Price ID</label>
            <input
              data-testid="input-stripe-price-id"
              value={form.stripePriceId}
              onChange={(e) => setForm({ ...form, stripePriceId: e.target.value })}
              placeholder="price_xxxxxxxxxxxxxxxxxxxxxxxx"
              className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-foreground/20 font-mono"
            />
            <p className="text-[10px] text-foreground/25 mt-1.5">From your Stripe dashboard — enables checkout for this package. Leave blank to hide from the subscription page.</p>
          </div>

          <div className="flex items-center justify-between p-4 rounded-xl bg-foreground/[0.02] border border-foreground/[0.06]">
            <div>
              <p className="text-sm font-medium text-foreground">Active</p>
              <p className="text-[10px] text-foreground/25">Inactive packages won't be shown to customers</p>
            </div>
            <button
              data-testid="toggle-package-active"
              onPointerDown={() => setForm({ ...form, isActive: !form.isActive })}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.isActive ? "bg-blue-600" : "bg-foreground/10"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.isActive ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          <button
            onPointerDown={handleSubmit}
            disabled={saving || !form.name.trim() || !form.price.trim()}
            className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 text-foreground font-bold rounded-xl transition-all disabled:opacity-50 text-sm"
            data-testid="button-save-package"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {editingId ? "Update Package" : "Create Package"}
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-foreground/30">
          <Loader2 size={24} className="animate-spin" />
        </div>
      ) : packages.length === 0 ? (
        <div className="text-center py-16 text-foreground/20">
          <Package size={40} className="mx-auto mb-4 opacity-30" />
          <p className="text-sm">No packages yet. Create your first subscription package.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {packages.map((pkg) => (
            <div
              key={pkg.id}
              className={`bg-foreground/[0.03] border rounded-2xl p-6 transition-all ${pkg.isActive ? "border-foreground/10" : "border-foreground/[0.04] opacity-60"}`}
              data-testid={`card-package-${pkg.id}`}
            >
              <div className="flex items-start justify-between gap-2 mb-4">
                <div>
                  <h3 className="font-display font-bold text-foreground text-lg">{pkg.name}</h3>
                  {pkg.description && <p className="text-xs text-foreground/30 mt-1">{pkg.description}</p>}
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${pkg.isActive ? "bg-green-500/10 text-green-400" : "bg-foreground/5 text-foreground/30"}`} data-testid={`badge-status-${pkg.id}`}>
                  {pkg.isActive ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="flex items-baseline gap-1 mb-4">
                <span className="text-3xl font-bold text-foreground">${pkg.price}</span>
                <span className="text-sm text-foreground/30">/{pkg.billingCycle === "yearly" ? "yr" : "mo"}</span>
              </div>

              <div className="space-y-2 mb-5">
                <div className="flex items-center gap-2 text-xs text-foreground/40">
                  <Users size={12} className="shrink-0" />
                  <span>{pkg.maxUsers} user{pkg.maxUsers !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground/40">
                  <Package size={12} className="shrink-0" />
                  <span>{pkg.maxWorkspaces} workspace{pkg.maxWorkspaces !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-foreground/40">
                  <Zap size={12} className="shrink-0" />
                  <span>{pkg.aiCallsIncluded.toLocaleString()} AI calls</span>
                </div>
                <div className="flex items-center gap-2 text-xs" data-testid={`badge-stripe-${pkg.id}`}>
                  {(pkg as any).stripePriceId ? (
                    <>
                      <Check size={12} className="text-emerald-400 shrink-0" />
                      <span className="text-emerald-400/70 font-mono truncate">{(pkg as any).stripePriceId}</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={12} className="text-amber-400/60 shrink-0" />
                      <span className="text-foreground/25">No Stripe Price ID — checkout disabled</span>
                    </>
                  )}
                </div>
              </div>

              {pkg.features && pkg.features.length > 0 && (
                <div className="border-t border-foreground/[0.06] pt-4 mb-5">
                  <p className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold mb-2">Features</p>
                  <div className="flex flex-wrap gap-1.5">
                    {pkg.features.map((f) => (
                      <span key={f} className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-medium">
                        {f.replace(":enabled", "").replace("_", " ")}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button
                  onPointerDown={() => startEdit(pkg)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-foreground/5 text-foreground/40 border border-foreground/10 hover:border-blue-500/30 hover:text-blue-400 transition-all text-xs font-medium"
                  data-testid={`button-edit-package-${pkg.id}`}
                >
                  <Pencil size={12} />
                  Edit
                </button>
                <button
                  onPointerDown={() => handleDelete(pkg.id)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-foreground/5 text-foreground/40 border border-foreground/10 hover:border-red-500/30 hover:text-red-400 transition-all text-xs font-medium"
                  data-testid={`button-delete-package-${pkg.id}`}
                >
                  <Trash2 size={12} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Promo Codes Section */}
      <div className="pt-10 border-t border-foreground/[0.06]">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
          <div>
            <h2 className="text-xl font-display font-bold text-foreground">Promo Codes</h2>
            <p className="text-sm text-foreground/30 mt-1">Create invite codes with specific plans and bonuses.</p>
          </div>
          {!showPromoForm && (
            <button
              onPointerDown={() => setShowPromoForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-foreground/5 border border-foreground/10 hover:bg-foreground/10 text-foreground rounded-xl text-sm font-bold transition-all"
              data-testid="button-create-promo"
            >
              <Plus size={16} />
              Create Promo Code
            </button>
          )}
        </div>

        {showPromoForm && (
          <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5 mb-8" data-testid="promo-form">
            <div className="flex items-center justify-between gap-4">
              <h3 className="font-display font-bold text-foreground">New Promo Code</h3>
              <button onPointerDown={() => setShowPromoForm(false)} className="text-foreground/30 hover:text-foreground/60 transition-colors" data-testid="button-cancel-promo">
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Code</label>
                <input
                  data-testid="input-promo-code"
                  value={promoForm.code}
                  onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SUMMER2024"
                  className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
                />
              </div>
              <div>
                <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Plan</label>
                <select
                  data-testid="select-promo-plan"
                  value={promoForm.planId}
                  onChange={(e) => setPromoForm({ ...promoForm, planId: e.target.value })}
                  className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground appearance-none"
                >
                  <option value="">Select a package...</option>
                  {packages.map(p => (
                    <option key={p.id} value={p.name.toLowerCase()}>{p.name} (${p.price})</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Bonus Credits</label>
                <input
                  type="number"
                  data-testid="input-promo-bonus"
                  value={promoForm.bonusCredits}
                  onChange={(e) => setPromoForm({ ...promoForm, bonusCredits: parseInt(e.target.value) || 0 })}
                  className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
                />
              </div>
              <div>
                <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Discount %</label>
                <input
                  type="number"
                  data-testid="input-promo-discount"
                  value={promoForm.discountPercent}
                  onChange={(e) => setPromoForm({ ...promoForm, discountPercent: parseInt(e.target.value) || 0 })}
                  className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
                />
              </div>
              <div>
                <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Expires At</label>
                <input
                  type="date"
                  data-testid="input-promo-expiry"
                  value={promoForm.expiresAt}
                  onChange={(e) => setPromoForm({ ...promoForm, expiresAt: e.target.value })}
                  className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Max Uses</label>
              <input
                type="number"
                data-testid="input-promo-max-uses"
                value={promoForm.maxUses}
                onChange={(e) => setPromoForm({ ...promoForm, maxUses: parseInt(e.target.value) || 100 })}
                className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground"
              />
            </div>

            <button
              onPointerDown={handlePromoSubmit}
              disabled={promoSaving || !promoForm.code.trim()}
              className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 text-foreground font-bold rounded-xl transition-all disabled:opacity-50 text-sm"
              data-testid="button-save-promo"
            >
              {promoSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Create Promo Code
            </button>
          </div>
        )}

        {promoLoading ? (
          <div className="flex items-center justify-center py-12 text-foreground/30">
            <Loader2 size={24} className="animate-spin" />
          </div>
        ) : promoCodes.length === 0 ? (
          <div className="bg-foreground/[0.02] border border-dashed border-foreground/10 rounded-2xl py-12 text-center text-foreground/20">
            <p className="text-sm">No promo codes yet.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="text-left border-b border-foreground/10">
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Code</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Plan</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Bonus</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Discount</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Uses</th>
                  <th className="px-4 py-3 text-[10px] uppercase tracking-widest font-bold text-foreground/40">Expires</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-foreground/[0.04]">
                {promoCodes.map((code) => (
                  <tr key={code.id} className="hover:bg-foreground/[0.01] transition-colors">
                    <td className="px-4 py-4 text-sm font-mono font-bold text-blue-400">{code.code}</td>
                    <td className="px-4 py-4 text-sm text-foreground/60 capitalize">{code.plan}</td>
                    <td className="px-4 py-4 text-sm text-foreground/60">{code.bonus_credits || 0}</td>
                    <td className="px-4 py-4 text-sm text-foreground/60">{code.discount || 0}%</td>
                    <td className="px-4 py-4 text-sm text-foreground/60">
                      <span className={code.usedCount >= code.maxUses ? "text-red-400" : ""}>
                        {code.usedCount} / {code.maxUses}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground/60">
                      {new Date(code.expiresAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onPointerDown={() => handlePromoDelete(code.id)}
                        className="p-2 rounded-lg text-foreground/20 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        data-testid={`button-delete-promo-${code.id}`}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

interface SaasInvoice {
  id: number;
  userId: number;
  workspaceId: number | null;
  packageId: number | null;
  amount: string;
  status: string;
  description: string | null;
  stripePaymentId: string | null;
  createdAt: string;
  paidAt: string | null;
  userName: string;
  userEmail: string;
}

function BillingTab() {
  const [subTab, setSubTab] = useState<"stripe" | "invoices" | "monitor" | "config">("stripe");

  return (
    <div className="space-y-6" data-testid="billing-tab">
      <div>
        <h2 className="text-xl font-display font-bold text-foreground">Billing</h2>
        <p className="text-sm text-foreground/30 mt-1">Stripe metrics, invoices, credit monitor, and billing config.</p>
      </div>
      <div className="flex gap-2 flex-wrap border-b border-foreground/[0.06] pb-4">
        {([
          { id: "stripe", label: "Stripe" },
          { id: "invoices", label: "Invoices" },
          { id: "monitor", label: "Credit Monitor" },
          { id: "config", label: "Config" },
        ] as const).map(tab => (
          <button
            key={tab.id}
            onPointerDown={() => setSubTab(tab.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${subTab === tab.id ? "bg-blue-500/20 text-blue-400 border border-blue-500/30" : "bg-foreground/[0.03] text-foreground/40 border border-foreground/[0.06] hover:text-foreground/60"}`}
            data-testid={`billing-subtab-${tab.id}`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {subTab === "stripe" && <BillingStripeSubTab />}
      {subTab === "invoices" && <BillingInvoicesSubTab />}
      {subTab === "monitor" && <BillingMonitorSubTab />}
      {subTab === "config" && <BillingConfigSubTab />}
    </div>
  );
}

function BillingStripeSubTab() {
  const [stripeMetrics, setStripeMetrics] = useState<{ activeSubscribers: number; mrr: number; recentPayments: any[]; arpu?: number; churnedThisMonth?: number; newSubscribersThisMonth?: number; churnedMRR?: number; newMRR?: number; netNewMRR?: number; error?: string } | null>(null);
  const [stripeLoading, setStripeLoading] = useState(true);
  const [invoices, setInvoices] = useState<SaasInvoice[]>([]);

  const fetchStripeMetrics = useCallback(async () => {
    setStripeLoading(true);
    try {
      const res = await fetch("/api/saas-admin/stripe-metrics", { credentials: "include" });
      if (res.ok) setStripeMetrics(await res.json());
    } catch { /* ignore */ } finally { setStripeLoading(false); }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await fetch("/api/saas-admin/invoices", { credentials: "include" });
      if (res.ok) setInvoices(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStripeMetrics(); fetchInvoices(); }, [fetchStripeMetrics, fetchInvoices]);

  const totalRevenue = invoices.filter((i) => i.status === "paid").reduce((sum, i) => sum + parseFloat(i.amount || "0"), 0);
  const pendingCount = invoices.filter((i) => i.status === "pending").length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-cyan-500/10 flex items-center justify-center"><TrendingUp size={14} className="text-cyan-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">MRR</span></div>
          <p className="text-2xl font-bold text-cyan-400" data-testid="text-mrr">${stripeMetrics?.mrr.toFixed(2) ?? "—"}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-indigo-500/10 flex items-center justify-center"><Users size={14} className="text-indigo-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Subscribers</span></div>
          <p className="text-2xl font-bold text-indigo-400" data-testid="text-active-subscribers">{stripeMetrics?.activeSubscribers ?? "—"}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center"><DollarSign size={14} className="text-emerald-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Invoiced Rev.</span></div>
          <p className="text-2xl font-bold text-emerald-400">${totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-yellow-500/10 flex items-center justify-center"><Clock size={14} className="text-yellow-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Pending</span></div>
          <p className="text-2xl font-bold text-yellow-400">{pendingCount}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-violet-500/10 flex items-center justify-center"><DollarSign size={14} className="text-violet-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">ARPU</span></div>
          <p className="text-2xl font-bold text-violet-400">${stripeMetrics?.arpu?.toFixed(2) ?? '—'}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-red-500/10 flex items-center justify-center"><TrendingDown size={14} className="text-red-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Churned (30d)</span></div>
          <p className="text-2xl font-bold text-red-400">{stripeMetrics?.churnedThisMonth ?? '—'}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-green-500/10 flex items-center justify-center"><UserPlus size={14} className="text-green-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">New Subs (30d)</span></div>
          <p className="text-2xl font-bold text-green-400">{stripeMetrics?.newSubscribersThisMonth ?? '—'}</p>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3"><div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center"><TrendingUp size={14} className="text-emerald-400" /></div><span className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Net New MRR</span></div>
          <p className={`text-2xl font-bold ${ (stripeMetrics?.netNewMRR ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400' }`}>${stripeMetrics?.netNewMRR?.toFixed(2) ?? '—'}</p>
        </div>
      </div>
      {stripeLoading ? (
        <div className="flex items-center gap-2 py-6 justify-center text-foreground/30"><Loader2 size={16} className="animate-spin" /><span className="text-xs">Loading Stripe data...</span></div>
      ) : stripeMetrics?.error ? (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-xs"><AlertCircle size={14} /><span>Stripe issue: {stripeMetrics.error}</span></div>
      ) : stripeMetrics && stripeMetrics.recentPayments.length > 0 ? (
        <div className="bg-foreground/[0.03] border border-foreground/[0.06] rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-foreground/[0.06]"><p className="text-[10px] text-foreground/25 uppercase tracking-widest font-bold">Recent Payments</p></div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="stripe-payments-table">
              <thead><tr className="border-b border-foreground/[0.06]">
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-2">Date</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-2">Customer</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-2">Amount</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-2">Status</th>
              </tr></thead>
              <tbody>
                {stripeMetrics.recentPayments.map((p: any) => (
                  <tr key={p.id} className="border-b border-foreground/[0.04] last:border-b-0" data-testid={`row-payment-${p.id}`}>
                    <td className="px-5 py-3 text-foreground/40 text-xs">{new Date(p.created).toLocaleDateString()}</td>
                    <td className="px-5 py-3 text-foreground/60 text-xs">{p.customerEmail || "-"}</td>
                    <td className="px-5 py-3 text-foreground font-medium">${p.amount.toFixed(2)} <span className="text-foreground/25 uppercase text-[10px]">{p.currency}</span></td>
                    <td className="px-5 py-3"><span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${p.status === "succeeded" ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>{p.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : <p className="text-xs text-foreground/20 py-6 text-center">No recent Stripe payments.</p>}
    </div>
  );
}

function BillingInvoicesSubTab() {
  const [invoices, setInvoices] = useState<SaasInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [form, setForm] = useState({ userId: "", amount: "", description: "", status: "pending" });

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/invoices", { credentials: "include" });
      if (res.ok) setInvoices(await res.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await fetch("/api/saas-admin/users", { credentials: "include" });
      if (res.ok) { const data = await res.json(); setUsers(data.users || data); }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchInvoices(); fetchUsers(); }, [fetchInvoices, fetchUsers]);

  async function handleCreate() {
    if (!form.userId || !form.amount) return;
    setSaving(true);
    try {
      const res = await fetch("/api/saas-admin/invoices", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: parseInt(form.userId), amount: form.amount, description: form.description || null, status: form.status }),
      });
      if (res.ok) { setForm({ userId: "", amount: "", description: "", status: "pending" }); setShowForm(false); fetchInvoices(); }
    } catch { /* ignore */ } finally { setSaving(false); }
  }

  async function updateStatus(id: number, status: string) {
    await fetch(`/api/saas-admin/invoices/${id}/status`, { method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ status }) });
    fetchInvoices();
  }

  const statusColors: Record<string, string> = {
    pending: "bg-yellow-500/10 text-yellow-400",
    paid: "bg-green-500/10 text-green-400",
    failed: "bg-red-500/10 text-red-400",
    refunded: "bg-purple-500/10 text-purple-400",
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        {!showForm && (
          <button onPointerDown={() => setShowForm(true)} className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground rounded-xl text-sm font-bold transition-all" data-testid="button-create-invoice">
            <Plus size={16} /> Create Invoice
          </button>
        )}
      </div>
      {showForm && (
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5" data-testid="invoice-form">
          <div className="flex items-center justify-between gap-4">
            <h3 className="font-display font-bold text-foreground">Create Manual Invoice</h3>
            <button onPointerDown={() => setShowForm(false)} className="text-foreground/30 hover:text-foreground/60" data-testid="button-cancel-invoice"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">User *</label>
              <select data-testid="select-invoice-user" value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground appearance-none">
                <option value="">Select a user...</option>
                {users.map((u: any) => (<option key={u.id} value={u.id}>{u.displayName || u.firstName || u.email || `User ${u.id}`} (ID: {u.id})</option>))}
              </select>
            </div>
            <div>
              <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Amount *</label>
              <div className="flex items-center gap-2">
                <DollarSign size={14} className="text-foreground/20 shrink-0" />
                <input data-testid="input-invoice-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="49.99" className="flex-1 bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-foreground/20" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-xs text-foreground/40 uppercase tracking-widest font-bold block mb-2">Description</label>
            <input data-testid="input-invoice-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Monthly subscription - Pro Plan" className="w-full bg-foreground/5 border border-foreground/10 rounded-xl px-4 py-3 text-sm focus:border-blue-500 outline-none text-foreground placeholder:text-foreground/20" />
          </div>
          <div className="flex gap-3">
            {["pending", "paid"].map((s) => (
              <label key={s} className={`flex items-center gap-2 px-4 py-3 rounded-xl border cursor-pointer transition-all ${form.status === s ? "bg-blue-600/20 border-blue-500/30 text-blue-400" : "bg-foreground/[0.02] border-foreground/[0.06] text-foreground/50"}`}>
                <input type="radio" name="invoiceStatus" value={s} checked={form.status === s} onChange={(e) => setForm({ ...form, status: e.target.value })} className="accent-blue-500" />
                <span className="text-sm font-medium capitalize">{s}</span>
              </label>
            ))}
          </div>
          <button onPointerDown={handleCreate} disabled={saving || !form.userId || !form.amount} className="flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-500 text-foreground font-bold rounded-xl transition-all disabled:opacity-50 text-sm" data-testid="button-save-invoice">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Create Invoice
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-foreground/30"><Loader2 size={24} className="animate-spin" /></div>
      ) : invoices.length === 0 ? (
        <div className="text-center py-16 text-foreground/20"><Receipt size={40} className="mx-auto mb-4 opacity-30" /><p className="text-sm">No invoices yet.</p></div>
      ) : (
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="invoices-table">
              <thead><tr className="border-b border-foreground/[0.06]">
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">Invoice</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">User</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">Amount</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">Status</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">Date</th>
                <th className="text-left text-[10px] text-foreground/25 uppercase tracking-widest font-bold px-5 py-3">Actions</th>
              </tr></thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-foreground/[0.04] last:border-b-0 hover:bg-foreground/[0.02]" data-testid={`row-invoice-${inv.id}`}>
                    <td className="px-5 py-4"><div className="flex items-center gap-2"><FileText size={14} className="text-foreground/20" /><div><p className="text-sm font-medium text-foreground">INV-{String(inv.id).padStart(4, "0")}</p>{inv.description && <p className="text-[10px] text-foreground/25 truncate max-w-[180px]">{inv.description}</p>}</div></div></td>
                    <td className="px-5 py-4"><p className="text-sm text-foreground/60">{inv.userName}</p>{inv.userEmail && <p className="text-[10px] text-foreground/20">{inv.userEmail}</p>}</td>
                    <td className="px-5 py-4"><span className="text-sm font-bold text-foreground">${parseFloat(inv.amount).toFixed(2)}</span></td>
                    <td className="px-5 py-4"><span className={`text-[10px] font-bold px-2 py-1 rounded-md uppercase ${statusColors[inv.status] || "bg-foreground/5 text-foreground/30"}`}>{inv.status}</span></td>
                    <td className="px-5 py-4"><p className="text-xs text-foreground/40">{new Date(inv.createdAt).toLocaleDateString()}</p></td>
                    <td className="px-5 py-4">
                      {inv.status === "pending" && (<div className="flex items-center gap-1.5">
                        <button onPointerDown={() => updateStatus(inv.id, "paid")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-500/10 text-green-400 text-[10px] font-bold uppercase hover:bg-green-500/20" data-testid={`button-mark-paid-${inv.id}`}><Check size={10} /> Paid</button>
                        <button onPointerDown={() => updateStatus(inv.id, "failed")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-red-500/10 text-red-400 text-[10px] font-bold uppercase hover:bg-red-500/20" data-testid={`button-mark-failed-${inv.id}`}><X size={10} /> Failed</button>
                      </div>)}
                      {inv.status === "paid" && (<button onPointerDown={() => updateStatus(inv.id, "refunded")} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-purple-500/10 text-purple-400 text-[10px] font-bold uppercase hover:bg-purple-500/20" data-testid={`button-mark-refunded-${inv.id}`}><ArrowUpDown size={10} /> Refund</button>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BillingMonitorSubTab() {
  return <CreditsBillingMonitor />;
}

function BillingConfigSubTab() {
  return <CreditsBillingConfig />;
}

interface AiModel {
  id: number;
  service: string;
  modelId: string;
  displayName: string;
  provider: string;
  isEnabled: boolean;
  maxTokens: number | null;
  createdAt: string;
}

interface SpendLimitRow {
  id: number;
  scope: string;
  userId: string | null;
  monthlyLimit: string;
  dailyLimit: string | null;
  alertThreshold: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UserSpendData {
  userId: string;
  userName: string;
  userEmail: string;
  aiEnabled: boolean;
  monthlySpend: number;
  dailySpend: number;
  monthlyLimit: number | null;
  dailyLimit: number | null;
  alertThreshold: number;
  hasUserLimit: boolean;
  userLimitId: number | null;
  subscription: string;
}

function SpendProgressBar({ spent, limit, threshold }: { spent: number; limit: number | null; threshold: number }) {
  if (!limit || limit <= 0) return <span className="text-[10px] text-foreground/20">No limit</span>;
  const pct = Math.min(100, (spent / limit) * 100);
  const barColor = pct >= 100 ? "bg-red-500" : pct >= 90 ? "bg-orange-500" : pct >= threshold ? "bg-yellow-500" : "bg-blue-500";
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 rounded-full bg-foreground/[0.06] overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.max(1, pct)}%` }} />
      </div>
      <span className="text-[10px] text-foreground/40 font-mono whitespace-nowrap">{pct.toFixed(0)}%</span>
    </div>
  );
}

function ThresholdBadge({ spent, limit, threshold }: { spent: number; limit: number | null; threshold: number }) {
  if (!limit || limit <= 0) return null;
  const pct = (spent / limit) * 100;
  if (pct >= 100) return <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-md bg-red-500/15 text-red-400 border border-red-500/20" data-testid="badge-limit-exceeded">Limit Reached</span>;
  if (pct >= 90) return <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-md bg-orange-500/15 text-orange-400 border border-orange-500/20" data-testid="badge-90-threshold">90% Alert</span>;
  if (pct >= 80) return <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-md bg-yellow-500/15 text-yellow-400 border border-yellow-500/20" data-testid="badge-80-threshold">80% Warning</span>;
  return null;
}

function AiGuardrailsTab() {
  const [models, setModels] = useState<AiModel[]>([]);
  const [spendLimits, setSpendLimits] = useState<SpendLimitRow[]>([]);
  const [currentSpend, setCurrentSpend] = useState({ monthly: 0, daily: 0 });
  const [loading, setLoading] = useState(true);
  const [showModelForm, setShowModelForm] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [savingLimits, setSavingLimits] = useState(false);
  const [modelForm, setModelForm] = useState({ service: "gemini", modelId: "", displayName: "", provider: "google", maxTokens: "" });
  const [limitForm, setLimitForm] = useState({ monthlyLimit: "1000", dailyLimit: "", alertThreshold: "80" });
  const [userSpendData, setUserSpendData] = useState<UserSpendData[]>([]);
  const [userSpendLoading, setUserSpendLoading] = useState(true);
  const [togglingAi, setTogglingAi] = useState<string | null>(null);
  const [editingUserLimit, setEditingUserLimit] = useState<string | null>(null);
  const [userLimitForm, setUserLimitForm] = useState({ monthlyLimit: "", dailyLimit: "", alertThreshold: "80" });
  const [savingUserLimit, setSavingUserLimit] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const USER_SPEND_PAGE_SIZE = 25;
  const [userSpendPage, setUserSpendPage] = useState(1);
  const [userSpendTotal, setUserSpendTotal] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/ai-guardrails", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setModels(data.models || []);
        setSpendLimits(data.spendLimits || []);
        setCurrentSpend(data.currentSpend || { monthly: 0, daily: 0 });
        const globalLimit = (data.spendLimits || []).find((l: SpendLimitRow) => l.scope === "global");
        if (globalLimit) {
          setLimitForm({
            monthlyLimit: globalLimit.monthlyLimit || "1000",
            dailyLimit: globalLimit.dailyLimit || "",
            alertThreshold: globalLimit.alertThreshold || "80",
          });
        }
      }
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  const fetchUserSpend = useCallback(async () => {
    setUserSpendLoading(true);
    try {
      const res = await fetch(
        `/api/saas-admin/ai-spend-limits?page=${userSpendPage}&limit=${USER_SPEND_PAGE_SIZE}${searchQuery.trim() ? `&q=${encodeURIComponent(searchQuery.trim())}` : ''}`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setUserSpendData(data.users || []);
        setUserSpendTotal(data.total || 0);
      }
    } catch { /* ignore */ } finally { setUserSpendLoading(false); }
  }, [userSpendPage, searchQuery]);

  useEffect(() => { setUserSpendPage(1); }, [searchQuery]);
  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchUserSpend(); }, [fetchUserSpend]);

  async function toggleModel(id: number, isEnabled: boolean) {
    try {
      await fetch(`/api/saas-admin/ai-models/${id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isEnabled }),
      });
      fetchData();
    } catch { /* ignore */ }
  }

  async function deleteModel(id: number) {
    if (!confirm("Delete this AI model?")) return;
    try {
      const res = await fetch(`/api/saas-admin/ai-models/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) console.error("Delete model failed:", await res.text());
    } catch (e) { console.error("Delete model error:", e); }
    fetchData();
  }

  async function addModel() {
    if (!modelForm.modelId.trim() || !modelForm.displayName.trim()) return;
    setSavingModel(true);
    try {
      const res = await fetch("/api/saas-admin/ai-models", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...modelForm, maxTokens: modelForm.maxTokens ? Number(modelForm.maxTokens) : null }),
      });
      if (res.ok) {
        setModelForm({ service: "gemini", modelId: "", displayName: "", provider: "google", maxTokens: "" });
        setShowModelForm(false);
        fetchData();
      }
    } catch { /* ignore */ } finally { setSavingModel(false); }
  }

  async function saveLimits() {
    setSavingLimits(true);
    try {
      const globalLimit = spendLimits.find((l) => l.scope === "global");
      const url = globalLimit ? `/api/saas-admin/spend-limits/${globalLimit.id}` : "/api/saas-admin/spend-limits";
      const method = globalLimit ? "PUT" : "POST";
      await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scope: "global",
          monthlyLimit: limitForm.monthlyLimit || "1000",
          dailyLimit: limitForm.dailyLimit || null,
          alertThreshold: limitForm.alertThreshold || "80",
        }),
      });
      fetchData();
      fetchUserSpend();
    } catch { /* ignore */ } finally { setSavingLimits(false); }
  }

  async function toggleUserAi(userId: string, aiEnabled: boolean) {
    setTogglingAi(userId);
    try {
      await fetch(`/api/saas-admin/users/${userId}/ai-enabled`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aiEnabled }),
      });
      fetchUserSpend();
    } catch { /* ignore */ } finally { setTogglingAi(null); }
  }

  async function saveUserLimit(userId: string) {
    setSavingUserLimit(true);
    try {
      await fetch("/api/saas-admin/ai-spend-limits", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          monthlyLimit: userLimitForm.monthlyLimit || "1000",
          dailyLimit: userLimitForm.dailyLimit || null,
          alertThreshold: userLimitForm.alertThreshold || "80",
        }),
      });
      setEditingUserLimit(null);
      fetchUserSpend();
    } catch { /* ignore */ } finally { setSavingUserLimit(false); }
  }

  const globalLimit = spendLimits.find((l) => l.scope === "global");
  const monthlyLimitNum = parseFloat(globalLimit?.monthlyLimit || "1000");
  const monthlyPercent = monthlyLimitNum > 0 ? Math.min(100, (currentSpend.monthly / monthlyLimitNum) * 100) : 0;
  const alertThresholdNum = parseFloat(globalLimit?.alertThreshold || "80");
  const isOverThreshold = monthlyPercent >= alertThresholdNum;

  const filteredUsers = userSpendData;

  const usersAtThreshold = userSpendData.filter((u) => u.monthlyLimit && u.monthlyLimit > 0 && (u.monthlySpend / u.monthlyLimit) * 100 >= 80);
  const usersDisabled = userSpendData.filter((u) => !u.aiEnabled);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="ai-guardrails-loading">
        <Loader2 className="animate-spin text-blue-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-guardrails-tab">
      <div>
        <h2 className="text-xl font-display font-bold text-foreground">AI Guardrails</h2>
        <p className="text-sm text-foreground/30 mt-1">Monitor spend, manage models, set usage limits, and control per-user AI access.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-monthly-spend">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Monthly Spend</span>
            <DollarSign size={14} className={isOverThreshold ? "text-red-400" : "text-blue-400"} />
          </div>
          <div className="text-2xl font-display font-black text-foreground">${currentSpend.monthly.toFixed(2)}</div>
          <div className="text-xs text-foreground/30 mt-1">of ${monthlyLimitNum.toFixed(2)} limit</div>
          <div className="mt-3 w-full h-2 rounded-full bg-foreground/[0.06] overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${isOverThreshold ? "bg-red-500" : "bg-blue-500"}`}
              style={{ width: `${monthlyPercent}%` }}
              data-testid="progress-monthly-spend"
            />
          </div>
          <div className="text-[10px] text-foreground/25 mt-1">{monthlyPercent.toFixed(1)}% used</div>
        </div>

        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-daily-spend">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Daily Spend</span>
            <Clock size={14} className="text-blue-400" />
          </div>
          <div className="text-2xl font-display font-black text-foreground">${currentSpend.daily.toFixed(2)}</div>
          <div className="text-xs text-foreground/30 mt-1">
            {globalLimit?.dailyLimit ? `of $${globalLimit.dailyLimit} limit` : "No daily limit set"}
          </div>
        </div>

        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-users-at-threshold">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Users at Threshold</span>
            <AlertCircle size={14} className={usersAtThreshold.length > 0 ? "text-yellow-400" : "text-emerald-400"} />
          </div>
          <div className="text-2xl font-display font-black text-foreground">{usersAtThreshold.length}</div>
          <div className="text-xs text-foreground/30 mt-1">{usersAtThreshold.length > 0 ? "Need attention" : "All within limits"}</div>
        </div>

        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-users-disabled">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">AI Disabled</span>
            <Ban size={14} className={usersDisabled.length > 0 ? "text-red-400" : "text-foreground/20"} />
          </div>
          <div className="text-2xl font-display font-black text-foreground">{usersDisabled.length}</div>
          <div className="text-xs text-foreground/30 mt-1">{usersDisabled.length > 0 ? "Users with AI off" : "All users active"}</div>
        </div>
      </div>

      <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5" data-testid="section-per-user-spend">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-display font-bold text-foreground">Per-User AI Spend & Controls</h3>
            <p className="text-xs text-foreground/30 mt-0.5">View spend, set individual limits, and toggle AI access per user.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/25" />
              <input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users..."
                className="pl-9 pr-3 py-2 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 w-48"
                data-testid="input-search-users-spend"
              />
            </div>
          </div>
        </div>

        {userSpendLoading ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="animate-spin text-foreground/20" size={20} /></div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-10 text-foreground/20 text-sm" data-testid="text-no-users-spend">No users found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-user-spend">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">User</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Plan</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Monthly Spend</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Progress</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Status</th>
                  <th className="text-center py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">AI Access</th>
                  <th className="text-right py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Limit</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.userId} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors" data-testid={`row-user-spend-${u.userId}`}>
                    <td className="py-3 px-3">
                      <div className="text-foreground/80 text-xs font-medium">{u.userName || "Unknown"}</div>
                      <div className="text-foreground/25 text-[10px]">{u.userEmail}</div>
                    </td>
                    <td className="py-3 px-3">
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-md bg-foreground/[0.06] text-foreground/50 border border-foreground/10">{u.subscription}</span>
                    </td>
                    <td className="py-3 px-3">
                      <div className="text-white font-mono text-xs">${u.monthlySpend.toFixed(4)}</div>
                      <div className="text-foreground/20 text-[10px]">
                        {u.monthlyLimit ? `of $${u.monthlyLimit.toFixed(2)}` : "no limit"}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <SpendProgressBar spent={u.monthlySpend} limit={u.monthlyLimit} threshold={u.alertThreshold} />
                    </td>
                    <td className="py-3 px-3">
                      <ThresholdBadge spent={u.monthlySpend} limit={u.monthlyLimit} threshold={u.alertThreshold} />
                    </td>
                    <td className="py-3 px-3 text-center">
                      <button
                        onPointerDown={() => toggleUserAi(u.userId, !u.aiEnabled)}
                        disabled={togglingAi === u.userId}
                        className={`relative w-10 h-5 rounded-full transition-colors ${u.aiEnabled ? "bg-blue-600" : "bg-red-500/30"}`}
                        data-testid={`toggle-ai-${u.userId}`}
                      >
                        {togglingAi === u.userId ? (
                          <Loader2 size={12} className="absolute top-1 left-3 animate-spin text-foreground" />
                        ) : (
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${u.aiEnabled ? "left-5" : "left-0.5"}`} />
                        )}
                      </button>
                    </td>
                    <td className="py-3 px-3 text-right">
                      {editingUserLimit === u.userId ? (
                        <div className="flex items-center gap-2 justify-end">
                          <input
                            value={userLimitForm.monthlyLimit}
                            onChange={(e) => setUserLimitForm({ ...userLimitForm, monthlyLimit: e.target.value })}
                            placeholder="Monthly $"
                            className="w-20 px-2 py-1.5 bg-foreground/[0.04] border border-foreground/10 rounded-lg text-xs text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                            data-testid={`input-user-monthly-limit-${u.userId}`}
                          />
                          <button
                            onPointerDown={() => saveUserLimit(u.userId)}
                            disabled={savingUserLimit}
                            className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-all"
                            data-testid={`button-save-user-limit-${u.userId}`}
                          >
                            {savingUserLimit ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                          </button>
                          <button
                            onPointerDown={() => setEditingUserLimit(null)}
                            className="p-1.5 rounded-lg text-foreground/30 hover:text-foreground/60 transition-all"
                            data-testid={`button-cancel-user-limit-${u.userId}`}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onPointerDown={() => {
                            setEditingUserLimit(u.userId);
                            setUserLimitForm({
                              monthlyLimit: u.hasUserLimit && u.monthlyLimit ? String(u.monthlyLimit) : "",
                              dailyLimit: u.dailyLimit ? String(u.dailyLimit) : "",
                              alertThreshold: String(u.alertThreshold),
                            });
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-foreground/30 hover:text-foreground/60 hover:bg-foreground/[0.04] transition-all ml-auto"
                          data-testid={`button-edit-limit-${u.userId}`}
                        >
                          <Pencil size={10} />
                          {u.hasUserLimit ? "Edit" : "Set"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {userSpendTotal > USER_SPEND_PAGE_SIZE && (
              <div className="flex items-center justify-between pt-4 border-t border-foreground/[0.06]" data-testid="pagination-user-spend">
                <span className="text-xs text-foreground/30">
                  {userSpendTotal} users — page {userSpendPage} of {Math.ceil(userSpendTotal / USER_SPEND_PAGE_SIZE)}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onPointerDown={() => setUserSpendPage(p => Math.max(1, p - 1))}
                    disabled={userSpendPage === 1 || userSpendLoading}
                    className="px-3 py-1.5 rounded-lg text-xs text-foreground/50 hover:text-foreground/80 bg-foreground/[0.04] border border-foreground/10 disabled:opacity-30 transition-all"
                    data-testid="button-prev-page-spend"
                  >Prev</button>
                  <button
                    onPointerDown={() => setUserSpendPage(p => Math.min(Math.ceil(userSpendTotal / USER_SPEND_PAGE_SIZE), p + 1))}
                    disabled={userSpendPage >= Math.ceil(userSpendTotal / USER_SPEND_PAGE_SIZE) || userSpendLoading}
                    className="px-3 py-1.5 rounded-lg text-xs text-foreground/50 hover:text-foreground/80 bg-foreground/[0.04] border border-foreground/10 disabled:opacity-30 transition-all"
                    data-testid="button-next-page-spend"
                  >Next</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5" data-testid="section-ai-models">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h3 className="font-display font-bold text-foreground">AI Models</h3>
            <p className="text-xs text-foreground/30 mt-0.5">Registered models and their status.</p>
          </div>
          {!showModelForm && (
            <button
              onPointerDown={() => setShowModelForm(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground rounded-xl text-sm font-bold transition-all"
              data-testid="button-add-model"
            >
              <Plus size={16} />
              Add Model
            </button>
          )}
        </div>

        {showModelForm && (
          <div className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-5 space-y-4" data-testid="model-form">
            <div className="flex items-center justify-between gap-4">
              <h4 className="text-sm font-bold text-foreground/70">New Model</h4>
              <button onPointerDown={() => setShowModelForm(false)} className="text-foreground/30 hover:text-foreground/60 transition-colors" data-testid="button-cancel-model">
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Service</label>
                <input
                  value={modelForm.service}
                  onChange={(e) => setModelForm({ ...modelForm, service: e.target.value })}
                  className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                  placeholder="gemini"
                  data-testid="input-model-service"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Model ID</label>
                <input
                  value={modelForm.modelId}
                  onChange={(e) => setModelForm({ ...modelForm, modelId: e.target.value })}
                  className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                  placeholder="gemini-2.5-flash"
                  data-testid="input-model-id"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Display Name</label>
                <input
                  value={modelForm.displayName}
                  onChange={(e) => setModelForm({ ...modelForm, displayName: e.target.value })}
                  className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                  placeholder="Gemini 2.5 Flash"
                  data-testid="input-model-display-name"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Provider</label>
                <input
                  value={modelForm.provider}
                  onChange={(e) => setModelForm({ ...modelForm, provider: e.target.value })}
                  className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                  placeholder="google"
                  data-testid="input-model-provider"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Max Tokens</label>
                <input
                  value={modelForm.maxTokens}
                  onChange={(e) => setModelForm({ ...modelForm, maxTokens: e.target.value })}
                  className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                  placeholder="8192 (optional)"
                  data-testid="input-model-max-tokens"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                onPointerDown={addModel}
                disabled={savingModel || !modelForm.modelId.trim() || !modelForm.displayName.trim()}
                className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-foreground rounded-xl text-sm font-bold transition-all"
                data-testid="button-save-model"
              >
                {savingModel ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Model
              </button>
              <button
                onPointerDown={() => setShowModelForm(false)}
                className="px-4 py-2.5 text-foreground/40 hover:text-foreground/60 rounded-xl text-sm font-medium transition-all"
                data-testid="button-cancel-model-2"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {models.length === 0 ? (
          <div className="text-center py-10 text-foreground/20 text-sm" data-testid="text-no-models">No AI models registered yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-ai-models">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Service</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Model ID</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Display Name</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Provider</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Max Tokens</th>
                  <th className="text-left py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Enabled</th>
                  <th className="text-right py-3 px-3 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {models.map((m) => (
                  <tr key={m.id} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors" data-testid={`row-model-${m.id}`}>
                    <td className="py-3 px-3 text-foreground/60">{m.service}</td>
                    <td className="py-3 px-3 font-mono text-xs text-foreground/80">{m.modelId}</td>
                    <td className="py-3 px-3 text-foreground">{m.displayName}</td>
                    <td className="py-3 px-3 text-foreground/60">{m.provider}</td>
                    <td className="py-3 px-3 text-foreground/40">{m.maxTokens ?? "—"}</td>
                    <td className="py-3 px-3">
                      <button
                        onPointerDown={() => toggleModel(m.id, !m.isEnabled)}
                        className={`relative w-10 h-5 rounded-full transition-colors ${m.isEnabled ? "bg-blue-600" : "bg-foreground/10"}`}
                        data-testid={`toggle-model-${m.id}`}
                      >
                        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${m.isEnabled ? "left-5" : "left-0.5"}`} />
                      </button>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onPointerDown={() => deleteModel(m.id)}
                        className="p-1.5 rounded-lg text-foreground/20 hover:text-red-400 hover:bg-red-500/10 transition-all"
                        data-testid={`button-delete-model-${m.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-5" data-testid="section-spend-limits">
        <div>
          <h3 className="font-display font-bold text-foreground">Global Spend Limits</h3>
          <p className="text-xs text-foreground/30 mt-0.5">Set platform-wide AI spending controls.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Monthly Limit ($)</label>
            <input
              value={limitForm.monthlyLimit}
              onChange={(e) => setLimitForm({ ...limitForm, monthlyLimit: e.target.value })}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
              placeholder="1000"
              data-testid="input-monthly-limit"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Daily Limit ($)</label>
            <input
              value={limitForm.dailyLimit}
              onChange={(e) => setLimitForm({ ...limitForm, dailyLimit: e.target.value })}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
              placeholder="Optional"
              data-testid="input-daily-limit"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Alert Threshold (%)</label>
            <input
              value={limitForm.alertThreshold}
              onChange={(e) => setLimitForm({ ...limitForm, alertThreshold: e.target.value })}
              className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
              placeholder="80"
              data-testid="input-alert-threshold"
            />
          </div>
        </div>
        <button
          onPointerDown={saveLimits}
          disabled={savingLimits}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-foreground rounded-xl text-sm font-bold transition-all"
          data-testid="button-save-limits"
        >
          {savingLimits ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save Limits
        </button>
      </div>
    </div>
  );
}

interface TicketRow {
  id: number;
  userId: string;
  workspaceId: number | null;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  assignedTo: string | null;
  createdAt: string;
  updatedAt: string;
  userName: string;
  userEmail: string;
}

interface TicketMsg {
  id: number;
  ticketId: number;
  userId: string | null;
  senderType: string;
  message: string;
  createdAt: string;
}

function SupportTab() {
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState<(TicketRow & { messages: TicketMsg[] }) | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [updatingTicket, setUpdatingTicket] = useState(false);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/tickets", { credentials: "include" });
      if (res.ok) setTickets(await res.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  async function openTicket(id: number) {
    setDetailLoading(true);
    try {
      const res = await fetch(`/api/saas-admin/tickets/${id}`, { credentials: "include" });
      if (res.ok) setSelectedTicket(await res.json());
    } catch { /* ignore */ } finally { setDetailLoading(false); }
  }

  async function sendReply() {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      const res = await fetch(`/api/saas-admin/tickets/${selectedTicket.id}/messages`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: replyText.trim() }),
      });
      if (res.ok) {
        setReplyText("");
        openTicket(selectedTicket.id);
        fetchTickets();
      }
    } catch { /* ignore */ } finally { setSending(false); }
  }

  async function updateTicketField(field: string, value: string) {
    if (!selectedTicket) return;
    setUpdatingTicket(true);
    try {
      const res = await fetch(`/api/saas-admin/tickets/${selectedTicket.id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [field]: value }),
      });
      if (res.ok) {
        openTicket(selectedTicket.id);
        fetchTickets();
      }
    } catch { /* ignore */ } finally { setUpdatingTicket(false); }
  }

  const filtered = tickets.filter((t) => {
    if (statusFilter !== "all" && t.status !== statusFilter) return false;
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    return true;
  });

  const openCount = tickets.filter((t) => t.status === "open").length;
  const inProgressCount = tickets.filter((t) => t.status === "in-progress").length;
  const avgResponseMs = tickets.length > 0
    ? tickets.reduce((sum, t) => sum + (new Date(t.updatedAt).getTime() - new Date(t.createdAt).getTime()), 0) / tickets.length
    : 0;
  const avgResponseHrs = Math.round(avgResponseMs / (1000 * 60 * 60));

  const statusColor: Record<string, string> = {
    open: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    "in-progress": "bg-blue-500/10 text-blue-400 border-blue-500/20",
    resolved: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    closed: "bg-foreground/[0.06] text-foreground/40 border-foreground/10",
  };
  const priorityColor: Record<string, string> = {
    low: "bg-foreground/[0.06] text-foreground/40 border-foreground/10",
    medium: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    high: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    urgent: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="support-loading">
        <Loader2 className="animate-spin text-blue-400" size={24} />
      </div>
    );
  }

  if (selectedTicket) {
    return (
      <div className="space-y-6" data-testid="support-detail">
        <button
          onPointerDown={() => { setSelectedTicket(null); setReplyText(""); }}
          className="flex items-center gap-2 text-sm text-foreground/40 hover:text-foreground/70 transition-colors"
          data-testid="button-back-tickets"
        >
          <ArrowLeft size={16} />
          Back to tickets
        </button>

        {detailLoading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blue-400" size={24} /></div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row gap-6">
              <div className="flex-1 space-y-4">
                <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6">
                  <h3 className="text-lg font-display font-bold text-foreground mb-1" data-testid="text-ticket-subject">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-3 flex-wrap mb-4">
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg border ${statusColor[selectedTicket.status] || statusColor.open}`} data-testid="badge-ticket-status">{selectedTicket.status}</span>
                    <span className={`text-[10px] font-bold uppercase px-2.5 py-1 rounded-lg border ${priorityColor[selectedTicket.priority] || priorityColor.medium}`} data-testid="badge-ticket-priority">{selectedTicket.priority}</span>
                    <span className="text-xs text-foreground/30">{selectedTicket.userName} ({selectedTicket.userEmail})</span>
                    <span className="text-xs text-foreground/20">{new Date(selectedTicket.createdAt).toLocaleDateString()}</span>
                  </div>
                  <p className="text-sm text-foreground/60 leading-relaxed" data-testid="text-ticket-description">{selectedTicket.description}</p>
                </div>

                <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-6 space-y-4">
                  <h4 className="text-sm font-bold text-foreground/50 uppercase tracking-wider">Conversation</h4>
                  {selectedTicket.messages.length === 0 ? (
                    <div className="text-center py-6 text-foreground/20 text-sm" data-testid="text-no-messages">No messages yet.</div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="conversation-thread">
                      {selectedTicket.messages.map((msg) => (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-xl text-sm ${msg.senderType === "admin"
                            ? "bg-blue-500/[0.08] border border-blue-500/20 ml-8"
                            : "bg-foreground/[0.04] border border-foreground/[0.06] mr-8"
                          }`}
                          data-testid={`message-${msg.id}`}
                        >
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider ${msg.senderType === "admin" ? "text-blue-400" : "text-foreground/40"}`}>
                              {msg.senderType === "admin" ? "Admin" : "User"}
                            </span>
                            <span className="text-[10px] text-foreground/20">{new Date(msg.createdAt).toLocaleString()}</span>
                          </div>
                          <p className="text-foreground/70 leading-relaxed">{msg.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <textarea
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      className="flex-1 px-4 py-3 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 resize-none"
                      rows={3}
                      data-testid="input-reply"
                    />
                    <button
                      onPointerDown={sendReply}
                      disabled={sending || !replyText.trim()}
                      className="self-end flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-foreground rounded-xl text-sm font-bold transition-all"
                      data-testid="button-send-reply"
                    >
                      {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                      Send
                    </button>
                  </div>
                </div>
              </div>

              <div className="lg:w-72 space-y-4">
                <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5 space-y-4">
                  <h4 className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Manage</h4>
                  <div>
                    <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Status</label>
                    <select
                      value={selectedTicket.status}
                      onChange={(e) => updateTicketField("status", e.target.value)}
                      disabled={updatingTicket}
                      className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground focus:outline-none focus:border-blue-500/40"
                      data-testid="select-ticket-status"
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Priority</label>
                    <select
                      value={selectedTicket.priority}
                      onChange={(e) => updateTicketField("priority", e.target.value)}
                      disabled={updatingTicket}
                      className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground focus:outline-none focus:border-blue-500/40"
                      data-testid="select-ticket-priority"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold text-foreground/30 uppercase tracking-wider block mb-1.5">Assigned To</label>
                    <input
                      value={selectedTicket.assignedTo || ""}
                      onChange={(e) => updateTicketField("assignedTo", e.target.value)}
                      placeholder="Unassigned"
                      className="w-full px-3 py-2.5 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40"
                      data-testid="input-assigned-to"
                    />
                  </div>
                  <div className="pt-2 border-t border-foreground/[0.06] space-y-2 text-xs">
                    <div className="flex justify-between gap-2"><span className="text-foreground/30">Category</span><span className="text-foreground/60">{selectedTicket.category}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-foreground/30">Created</span><span className="text-foreground/60">{new Date(selectedTicket.createdAt).toLocaleDateString()}</span></div>
                    <div className="flex justify-between gap-2"><span className="text-foreground/30">Updated</span><span className="text-foreground/60">{new Date(selectedTicket.updatedAt).toLocaleDateString()}</span></div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="support-tab">
      <div>
        <h2 className="text-xl font-display font-bold text-foreground">Support Tickets</h2>
        <p className="text-sm text-foreground/30 mt-1">Manage user support requests and conversations.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-open-tickets">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Open Tickets</span>
            <AlertCircle size={14} className="text-yellow-400" />
          </div>
          <div className="text-2xl font-display font-black text-foreground">{openCount}</div>
          <div className="text-xs text-foreground/30 mt-1">Awaiting response</div>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-in-progress">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">In Progress</span>
            <Clock size={14} className="text-blue-400" />
          </div>
          <div className="text-2xl font-display font-black text-foreground">{inProgressCount}</div>
          <div className="text-xs text-foreground/30 mt-1">Being worked on</div>
        </div>
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl p-5" data-testid="card-avg-response">
          <div className="flex items-center justify-between gap-2 mb-3">
            <span className="text-xs font-bold text-foreground/40 uppercase tracking-wider">Avg Response</span>
            <TrendingUp size={14} className="text-emerald-400" />
          </div>
          <div className="text-2xl font-display font-black text-foreground">{avgResponseHrs}h</div>
          <div className="text-xs text-foreground/30 mt-1">Average response time</div>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-foreground/30" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground focus:outline-none focus:border-blue-500/40"
            data-testid="filter-status"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <select
          value={priorityFilter}
          onChange={(e) => setPriorityFilter(e.target.value)}
          className="px-3 py-2 bg-foreground/[0.04] border border-foreground/10 rounded-xl text-sm text-foreground focus:outline-none focus:border-blue-500/40"
          data-testid="filter-priority"
        >
          <option value="all">All Priority</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="urgent">Urgent</option>
        </select>
        <span className="text-xs text-foreground/20 ml-auto">{filtered.length} ticket{filtered.length !== 1 ? "s" : ""}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-foreground/20 text-sm" data-testid="text-no-tickets">
          {tickets.length === 0 ? "No support tickets yet." : "No tickets match the selected filters."}
        </div>
      ) : (
        <div className="bg-foreground/[0.03] border border-foreground/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="table-tickets">
              <thead>
                <tr className="border-b border-foreground/[0.06]">
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Subject</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">User</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Status</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Priority</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Category</th>
                  <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Created</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr
                    key={t.id}
                    onPointerDown={() => openTicket(t.id)}
                    className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors cursor-pointer"
                    data-testid={`row-ticket-${t.id}`}
                  >
                    <td className="py-3 px-4 text-foreground font-medium max-w-xs truncate">{t.subject}</td>
                    <td className="py-3 px-4">
                      <div className="text-foreground/60 text-xs">{t.userName}</div>
                      <div className="text-foreground/25 text-[10px]">{t.userEmail}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg border ${statusColor[t.status] || statusColor.open}`}>{t.status}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg border ${priorityColor[t.priority] || priorityColor.medium}`}>{t.priority}</span>
                    </td>
                    <td className="py-3 px-4 text-foreground/40 text-xs">{t.category}</td>
                    <td className="py-3 px-4 text-foreground/30 text-xs whitespace-nowrap">{new Date(t.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function WaitlistTab() {
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [budgetInputs, setBudgetInputs] = useState<Record<number, string>>({});
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchEntries = useCallback(() => {
    setLoading(true);
    fetch("/api/saas-admin/waitlist", { credentials: "include" })
      .then(r => r.json())
      .then((data: any[]) => {
        setEntries(data);
        const budgets: Record<number, string> = {};
        data.forEach((entry: any) => {
          if (entry.aiBudgetCap !== null && entry.aiBudgetCap !== undefined) {
            budgets[entry.id] = String(entry.aiBudgetCap);
          }
        });
        setBudgetInputs(prev => {
          const merged = { ...budgets };
          Object.keys(prev).forEach(k => {
            if (prev[Number(k)]) merged[Number(k)] = prev[Number(k)];
          });
          return merged;
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleApprove = async (id: number, betaAccess: boolean) => {
    setActionLoading(id);
    const budget = budgetInputs[id] ? Number(budgetInputs[id]) : undefined;
    await fetch(`/api/saas-admin/waitlist/${id}/approve`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ aiBudgetCap: budget }),
    });
    // Set beta access flag immediately after approve
    await fetch(`/api/saas-admin/waitlist/${id}/beta-access`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ betaAccess }),
    });
    setActionLoading(null);
    fetchEntries();
  };

  const handleToggleBetaAccess = async (id: number, current: boolean) => {
    setActionLoading(id);
    await fetch(`/api/saas-admin/waitlist/${id}/beta-access`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ betaAccess: !current }),
    });
    setActionLoading(null);
    fetchEntries();
  };

  const handleReject = async (id: number) => {
    setActionLoading(id);
    await fetch(`/api/saas-admin/waitlist/${id}/reject`, {
      method: "PUT",
      credentials: "include",
    });
    setActionLoading(null);
    fetchEntries();
  };

  const handleDelete = async (id: number) => {
    setActionLoading(id);
    await fetch(`/api/saas-admin/waitlist/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    setActionLoading(null);
    fetchEntries();
  };

  const handleSetBudget = async (id: number) => {
    const budget = budgetInputs[id];
    if (!budget) return;
    setActionLoading(id);
    await fetch(`/api/saas-admin/waitlist/${id}/budget`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ aiBudgetCap: Number(budget) }),
    });
    setActionLoading(null);
    fetchEntries();
  };

  const filtered = filter === "all" ? entries : entries.filter(e => e.status === filter);
  const counts = { all: entries.length, pending: entries.filter(e => e.status === "pending").length, approved: entries.filter(e => e.status === "approved").length, rejected: entries.filter(e => e.status === "rejected").length };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground" data-testid="text-waitlist-title">Waitlist Management</h2>
          <p className="text-sm text-foreground/30">Approve or reject users waiting for access</p>
        </div>
        <button onPointerDown={fetchEntries} className="flex items-center gap-2 px-4 py-2 bg-foreground/5 border border-foreground/10 rounded-xl text-sm text-foreground/50 hover:text-foreground hover:bg-foreground/10 transition-all" data-testid="button-refresh-waitlist">
          <RefreshCw size={14} />
          Refresh
        </button>
      </div>

      <div className="flex gap-2">
        {(["all", "pending", "approved", "rejected"] as const).map(f => (
          <button
            key={f}
            onPointerDown={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${filter === f ? "bg-blue-500/10 text-blue-400 border border-blue-500/20" : "text-foreground/35 hover:text-foreground/60 hover:bg-foreground/[0.03] border border-transparent"}`}
            data-testid={`button-filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)} ({counts[f]})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-foreground/20 text-sm">No waitlist entries found.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(entry => (
            <div key={entry.id} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-xl p-5 hover:border-foreground/10 transition-all" data-testid={`waitlist-entry-${entry.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-1">
                    <span className="text-sm font-bold text-foreground" data-testid={`text-waitlist-email-${entry.id}`}>{entry.email}</span>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-md ${
                      entry.status === "approved" ? "bg-emerald-500/10 border border-emerald-500/20 text-emerald-400" :
                      entry.status === "rejected" ? "bg-red-500/10 border border-red-500/20 text-red-400" :
                      "bg-amber-500/10 border border-amber-500/20 text-amber-400"
                    }`} data-testid={`badge-status-${entry.id}`}>{entry.status}</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-foreground/25">
                    {entry.name && <span>{entry.name}</span>}
                    {entry.company && <span>{entry.company}</span>}
                    <span>{new Date(entry.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {entry.status === "pending" && (
                    <>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-foreground/30">Budget $</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="10.00"
                          value={budgetInputs[entry.id] || ""}
                          onChange={(e) => setBudgetInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                          className="w-20 px-2 py-1.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs focus:outline-none focus:border-blue-500/40"
                          data-testid={`input-budget-${entry.id}`}
                        />
                      </div>
                      <button
                        onPointerDown={() => handleApprove(entry.id, true)}
                        disabled={actionLoading === entry.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                        data-testid={`button-approve-beta-${entry.id}`}
                        title="Approve with free beta access"
                      >
                        {actionLoading === entry.id ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                        Approve (Beta)
                      </button>
                      <button
                        onPointerDown={() => handleApprove(entry.id, false)}
                        disabled={actionLoading === entry.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                        data-testid={`button-approve-paid-${entry.id}`}
                        title="Approve — must subscribe to access"
                      >
                        {actionLoading === entry.id ? <Loader2 size={12} className="animate-spin" /> : <UserCheck size={12} />}
                        Approve (Paid)
                      </button>
                      <button
                        onPointerDown={() => handleReject(entry.id)}
                        disabled={actionLoading === entry.id}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                        data-testid={`button-reject-${entry.id}`}
                      >
                        {actionLoading === entry.id ? <Loader2 size={12} className="animate-spin" /> : <Ban size={12} />}
                        Reject
                      </button>
                    </>
                  )}
                  {entry.status === "approved" && (
                    <>
                      <button
                        onPointerDown={() => handleToggleBetaAccess(entry.id, entry.betaAccess !== false)}
                        disabled={actionLoading === entry.id}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50 border ${entry.betaAccess !== false ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20" : "bg-amber-500/10 border-amber-500/20 text-amber-400 hover:bg-amber-500/20"}`}
                        data-testid={`button-toggle-beta-${entry.id}`}
                        title={entry.betaAccess !== false ? "Currently: Free Beta — click to require payment" : "Currently: Must Pay — click to grant free access"}
                      >
                        {actionLoading === entry.id ? <Loader2 size={12} className="animate-spin" /> : null}
                        {entry.betaAccess !== false ? "Beta (Free)" : "Must Subscribe"}
                      </button>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-foreground/30">Budget $</span>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="10.00"
                          value={budgetInputs[entry.id] || ""}
                          onChange={(e) => setBudgetInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                          className="w-20 px-2 py-1.5 bg-foreground/[0.04] border border-foreground/[0.08] rounded-lg text-foreground text-xs focus:outline-none focus:border-blue-500/40"
                          data-testid={`input-budget-approved-${entry.id}`}
                        />
                        <button
                          onPointerDown={() => handleSetBudget(entry.id)}
                          disabled={actionLoading === entry.id || !budgetInputs[entry.id]}
                          className="flex items-center gap-1 px-2 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 hover:bg-blue-500/20 rounded-lg text-xs font-medium transition-all disabled:opacity-50"
                          data-testid={`button-set-budget-${entry.id}`}
                        >
                          <Save size={12} />
                        </button>
                      </div>
                    </>
                  )}
                  <button
                    onPointerDown={() => handleDelete(entry.id)}
                    disabled={actionLoading === entry.id}
                    className="flex items-center gap-1 px-2 py-1.5 text-foreground/20 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-xs transition-all disabled:opacity-50"
                    data-testid={`button-delete-${entry.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StaffTab() {
  const [staff, setStaff] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ username: "", password: "", displayName: "", email: "", role: "support", permissions: {} as Record<string, boolean> });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchStaff = useCallback(async () => {
    try {
      const res = await fetch("/api/saas-admin/staff", { credentials: "include" });
      if (res.ok) setStaff(await res.json());
    } catch (e) { console.warn('[saas-admin] fetchStaff failed:', e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStaff(); }, [fetchStaff]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        const body: any = {};
        if (form.displayName) body.displayName = form.displayName;
        if (form.email !== undefined) body.email = form.email;
        if (form.role) body.role = form.role;
        if (form.password) body.password = form.password;
        if (form.permissions) body.permissions = form.permissions;
        const res = await fetch(`/api/saas-admin/staff/${editingId}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body), credentials: "include",
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      } else {
        if (!form.username || !form.password) { setError("Username and password required"); setSaving(false); return; }
        const res = await fetch("/api/saas-admin/staff", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(form), credentials: "include",
        });
        if (!res.ok) { const d = await res.json(); throw new Error(d.message); }
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ username: "", password: "", displayName: "", email: "", role: "support", permissions: {} });
      fetchStaff();
    } catch (e: any) { setError(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Remove this staff member?")) return;
    try {
      await fetch(`/api/saas-admin/staff/${id}`, { method: "DELETE", credentials: "include" });
      fetchStaff();
    } catch (e) { console.warn('[saas-admin] handleDelete failed:', e); }
  };

  const handleToggleActive = async (s: any) => {
    try {
      await fetch(`/api/saas-admin/staff/${s.id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isActive: !s.isActive }), credentials: "include",
      });
      fetchStaff();
    } catch (e) { console.warn('[saas-admin] handleToggleActive failed:', e); }
  };

  const startEdit = (s: any) => {
    setEditingId(s.id);
    setForm({ username: s.username, password: "", displayName: s.displayName || "", email: s.email || "", role: s.role, permissions: s.permissions || {} });
    setShowForm(true);
    setError("");
  };

  const togglePermission = (tabId: string) => {
    setForm(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [tabId]: prev.permissions[tabId] === false ? true : false
      }
    }));
  };

  const roleColors: Record<string, string> = {
    owner: "text-amber-400 bg-amber-500/10 border-amber-500/20",
    admin: "text-blue-400 bg-blue-500/10 border-blue-500/20",
    developer: "text-green-400 bg-green-500/10 border-green-500/20",
    support: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

  return (
    <div className="space-y-6" data-testid="staff-tab">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground">Staff Management</h2>
          <p className="text-foreground/30 text-sm mt-1">Manage team members and their access roles</p>
        </div>
        <button
          onPointerDown={() => { setShowForm(true); setEditingId(null); setForm({ username: "", password: "", displayName: "", email: "", role: "support", permissions: {} }); setError(""); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground text-sm font-semibold rounded-xl transition-colors"
          data-testid="button-add-staff"
        >
          <Plus size={16} /> Add Staff
        </button>
      </div>

      {showForm && (
        <div className="bg-foreground/[0.03] border border-foreground/[0.08] rounded-2xl p-6" data-testid="staff-form">
          <h3 className="text-lg font-display font-bold text-foreground mb-4">{editingId ? "Edit Staff Member" : "Add Staff Member"}</h3>
          {error && (
            <div className="flex items-center gap-2 px-4 py-3 mb-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              <AlertCircle size={16} /> <span>{error}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editingId && (
              <div>
                <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Username</label>
                <input
                  type="text" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })}
                  placeholder="Enter username" data-testid="input-staff-username"
                  className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 text-sm"
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">{editingId ? "New Password (leave blank to keep)" : "Password"}</label>
              <input
                type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={editingId ? "Leave blank to keep current" : "Enter password"} data-testid="input-staff-password"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Display Name</label>
              <input
                type="text" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                placeholder="Full name" data-testid="input-staff-display-name"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Email</label>
              <input
                type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="staff@company.com" data-testid="input-staff-email"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground placeholder-foreground/20 focus:outline-none focus:border-blue-500/40 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground/40 uppercase tracking-wider mb-2">Role</label>
              <select
                value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="select-staff-role"
                className="w-full px-4 py-3 bg-foreground/[0.04] border border-foreground/[0.08] rounded-xl text-foreground focus:outline-none focus:border-blue-500/40 text-sm"
              >
                <option value="owner" className="bg-[#0a0a12]">Owner</option>
                <option value="admin" className="bg-[#0a0a12]">Admin</option>
                <option value="developer" className="bg-[#0a0a12]">Developer</option>
                <option value="support" className="bg-[#0a0a12]">Support</option>
              </select>
            </div>
          </div>

          <div className="mt-6 border-t border-foreground/[0.08] pt-6">
            <h4 className="text-xs font-bold text-foreground/40 uppercase tracking-wider mb-4">Tab Permissions</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
              {[
                { id: "overview", label: "Overview" },
                { id: "packages", label: "Packages" },
                { id: "billing", label: "Billing" },
                { id: "users", label: "Users" },
                { id: "support", label: "Support" },
                { id: "waitlist", label: "Waitlist" },
                { id: "ai-controls", label: "AI Controls" },
                { id: "api-connections", label: "API Connections" },
                { id: "website", label: "Website" },
              ].map((tab) => (
                <label key={tab.id} className="flex items-center gap-2 p-2 rounded-lg bg-foreground/[0.02] border border-foreground/[0.06] hover:bg-foreground/[0.04] transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.permissions[tab.id] !== false}
                    onChange={() => togglePermission(tab.id)}
                    className="rounded border-foreground/20 text-blue-500 focus:ring-blue-500/20"
                    data-testid={`checkbox-permission-${tab.id}`}
                  />
                  <span className="text-xs text-foreground/70">{tab.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6">
            <button onPointerDown={handleSave} disabled={saving} data-testid="button-save-staff"
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground text-sm font-semibold rounded-xl transition-colors disabled:opacity-50">
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              <span>{editingId ? "Update" : "Create"}</span>
            </button>
            <button onPointerDown={() => { setShowForm(false); setEditingId(null); setError(""); }} data-testid="button-cancel-staff"
              className="px-5 py-2.5 bg-foreground/[0.04] hover:bg-foreground/[0.08] text-foreground/60 text-sm font-semibold rounded-xl transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-foreground/[0.03] border border-foreground/[0.06] rounded-2xl overflow-hidden">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead>
            <tr className="border-b border-foreground/[0.06]">
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Name</th>
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Username</th>
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Email</th>
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Role</th>
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Status</th>
              <th className="text-left py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Last Login</th>
              <th className="text-right py-3 px-4 text-[10px] font-bold text-foreground/30 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((s) => (
              <tr key={s.id} className="border-b border-foreground/[0.04] hover:bg-foreground/[0.02] transition-colors" data-testid={`row-staff-${s.id}`}>
                <td className="py-3 px-4 text-foreground font-medium">{s.displayName || s.username}</td>
                <td className="py-3 px-4 text-foreground/50">{s.username}</td>
                <td className="py-3 px-4 text-foreground/40 text-xs">{s.email || "-"}</td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg border ${roleColors[s.role] || roleColors.support}`}>{s.role}</span>
                </td>
                <td className="py-3 px-4">
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-lg border ${s.isActive ? "text-green-400 bg-green-500/10 border-green-500/20" : "text-red-400 bg-red-500/10 border-red-500/20"}`}>
                    {s.isActive ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="py-3 px-4 text-foreground/30 text-xs">{s.lastLoginAt ? new Date(s.lastLoginAt).toLocaleString() : "Never"}</td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onPointerDown={() => startEdit(s)} className="p-1.5 rounded-lg hover:bg-foreground/[0.06] text-foreground/40 hover:text-foreground transition-colors" data-testid={`button-edit-staff-${s.id}`}>
                      <Pencil size={14} />
                    </button>
                    <button onPointerDown={() => handleToggleActive(s)} className={`p-1.5 rounded-lg hover:bg-foreground/[0.06] transition-colors ${s.isActive ? "text-green-400 hover:text-green-300" : "text-red-400 hover:text-red-300"}`}
                      data-testid={`button-toggle-staff-${s.id}`}>
                      {s.isActive ? <Check size={14} /> : <X size={14} />}
                    </button>
                    {s.role !== "owner" && (
                      <button onPointerDown={() => handleDelete(s.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-foreground/40 hover:text-red-400 transition-colors" data-testid={`button-delete-staff-${s.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {staff.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-foreground/20">No staff members yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const CREDIT_ACTIONS = [
  { key: "radar_scan", label: "Radar Scan" },
  { key: "content_batch", label: "Content Batch" },
  { key: "strategy", label: "Strategy" },
  { key: "image", label: "Image Generation" },
  { key: "video", label: "Video Generation" },
  { key: "pitch", label: "Peggy Pitch" },
  { key: "lead_email", label: "Lead Nurture Email" },
  { key: "ad_campaign", label: "Ad Campaign" },
];

const DEFAULT_WEIGHTS: Record<string, number> = {
  radar_scan: 10, content_batch: 3, strategy: 5, image: 15,
  video: 40, pitch: 3, lead_email: 1, ad_campaign: 4,
};

function CreditsBillingMonitor() {
  const [monitor, setMonitor] = useState<any[]>([]);
  const [monitorLoading, setMonitorLoading] = useState(true);
  const [discountEdits, setDiscountEdits] = useState<Record<string, number>>({});
  const [discountSaving, setDiscountSaving] = useState<string | null>(null);

  const fetchMonitor = useCallback(async () => {
    setMonitorLoading(true);
    try {
      const res = await fetch("/api/saas-admin/billing-monitor", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const monitorUsers = data.users || data;
        setMonitor(monitorUsers);
        const edits: Record<string, number> = {};
        monitorUsers.forEach((u: any) => { edits[u.userId] = u.discountPercent; });
        setDiscountEdits(edits);
      }
    } finally { setMonitorLoading(false); }
  }, []);

  useEffect(() => { fetchMonitor(); }, [fetchMonitor]);

  async function saveDiscount(userId: string) {
    setDiscountSaving(userId);
    try {
      await fetch(`/api/saas-admin/users/${userId}/discount`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ discountPercent: discountEdits[userId] ?? 0 }),
      });
      await fetchMonitor();
    } finally { setDiscountSaving(null); }
  }

  function exportCsv() {
    const headers = ["User", "Plan", "Credits Used", "Credits Left", "Overage Billed", "AI Cost", "Margin", "Overage Cap", "Discount %"];
    const rows = monitor.map(u => [
      u.email || u.userId, u.plan, u.creditsUsed, u.creditBalance,
      `$${u.overageBilled.toFixed(2)}`, `$${u.actualAiCost.toFixed(4)}`, `$${u.margin.toFixed(2)}`,
      u.overageCap ?? "None", `${u.discountPercent}%`,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "drpr-billing-monitor.csv"; a.click();
  }

  const totals = monitor.reduce((acc, u) => ({
    creditsIssued: acc.creditsIssued + (u.creditAllowance ?? 0),
    creditsUsed: acc.creditsUsed + (u.creditsUsed ?? 0),
    overageBilled: acc.overageBilled + (u.overageBilled ?? 0),
    aiCost: acc.aiCost + (u.actualAiCost ?? 0),
    margin: acc.margin + (u.margin ?? 0),
  }), { creditsIssued: 0, creditsUsed: 0, overageBilled: 0, aiCost: 0, margin: 0 });

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Credits Issued</p><p className="text-xl font-display font-bold">{totals.creditsIssued.toLocaleString()}</p></div>
        <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Consumed</p><p className="text-xl font-display font-bold">{totals.creditsUsed.toLocaleString()}</p></div>
        <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Overage Billed</p><p className="text-xl font-display font-bold text-emerald-400">${totals.overageBilled.toFixed(2)}</p></div>
        <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">AI Cost</p><p className="text-xl font-display font-bold text-red-400">${totals.aiCost.toFixed(4)}</p></div>
        <div className="bg-card border border-border rounded-2xl p-4"><p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Net Margin</p><p className={`text-xl font-display font-bold ${totals.margin >= 0 ? "text-emerald-400" : "text-red-400"}`}>${totals.margin.toFixed(2)}</p></div>
      </div>
      <div className="bg-card border border-border rounded-2xl">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div><h3 className="font-display font-bold text-base">Billing Monitor</h3><p className="text-xs text-muted-foreground mt-0.5">Per-user credit usage, overage billing, and AI cost</p></div>
          <div className="flex items-center gap-2">
            <button onPointerDown={fetchMonitor} className="p-2 rounded-xl hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors" data-testid="button-refresh-monitor"><RefreshCw size={15} /></button>
            <button onPointerDown={exportCsv} className="flex items-center gap-2 px-3 py-2 bg-muted/30 hover:bg-muted/50 border border-border rounded-xl text-xs font-medium transition-colors" data-testid="button-export-csv"><FileText size={13} /> Export CSV</button>
          </div>
        </div>
        {monitorLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 size={24} className="animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">User</th>
                <th className="text-left py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Plan</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Credits Used</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Balance</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Overage Billed</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">AI Cost</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Margin</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Overage Cap</th>
                <th className="text-right py-3 px-4 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Discount %</th>
              </tr></thead>
              <tbody className="divide-y divide-border">
                {monitor.map(u => (
                  <tr key={u.userId} className="hover:bg-muted/10 transition-colors" data-testid={`row-billing-user-${u.userId}`}>
                    <td className="py-3 px-4"><p className="text-sm font-medium text-foreground/80 truncate max-w-[150px]">{u.displayName || u.firstName || u.email || `User ${u.userId}`}</p><p className="text-[10px] text-muted-foreground truncate max-w-[150px]">{u.email}</p></td>
                    <td className="py-3 px-4"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${u.plan === "growth" ? "bg-purple-500/10 text-purple-400" : u.plan === "solo" ? "bg-blue-500/10 text-blue-400" : "bg-muted/30 text-muted-foreground"}`}>{u.plan || "free"}</span></td>
                    <td className="py-3 px-4 text-right text-foreground/60 text-xs">{u.creditsUsed}/{u.creditAllowance}</td>
                    <td className="py-3 px-4 text-right text-xs"><span className={u.creditBalance <= 0 ? "text-red-400" : "text-emerald-400"}>{u.creditBalance}</span></td>
                    <td className="py-3 px-4 text-right text-xs text-emerald-400">${u.overageBilled.toFixed(2)}</td>
                    <td className="py-3 px-4 text-right text-xs text-red-400">${u.actualAiCost.toFixed(4)}</td>
                    <td className="py-3 px-4 text-right text-xs"><span className={u.margin >= 0 ? "text-emerald-400" : "text-red-400"}>${u.margin.toFixed(2)}</span></td>
                    <td className="py-3 px-4 text-right text-xs text-muted-foreground">{u.overageCap != null ? `$${u.overageCap}` : "None"}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <input type="number" min={0} max={100} value={discountEdits[u.userId] ?? 0} onChange={e => setDiscountEdits(d => ({ ...d, [u.userId]: Number(e.target.value) }))} className="w-14 text-xs text-center px-2 py-1 rounded-lg border border-border bg-background" data-testid={`input-discount-${u.userId}`} />
                        <span className="text-[10px] text-muted-foreground">%</span>
                        <button onPointerDown={() => saveDiscount(u.userId)} disabled={discountSaving === u.userId} className="p-1 rounded-lg hover:bg-muted/30 text-muted-foreground hover:text-foreground transition-colors" data-testid={`button-save-discount-${u.userId}`}>
                          {discountSaving === u.userId ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {monitor.length === 0 && (<tr><td colSpan={9} className="py-12 text-center text-muted-foreground text-sm">No users found</td></tr>)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CreditsBillingConfig() {
  const [weights, setWeights] = useState<Record<string, number>>(DEFAULT_WEIGHTS);
  const [overageRate, setOverageRate] = useState(0.10);
  const [planAllowances, setPlanAllowances] = useState({ solo: 500, growth: 2000 });
  const [configSaving, setConfigSaving] = useState(false);
  const [configSaved, setConfigSaved] = useState(false);

  async function saveConfig() {
    setConfigSaving(true);
    try {
      await fetch("/api/saas-admin/credit-config", {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ weights, overageRate, planAllowances }),
      });
      setConfigSaved(true);
      setTimeout(() => setConfigSaved(false), 2000);
    } finally { setConfigSaving(false); }
  }

  return (
    <div className="space-y-6">
      <div className="bg-card border border-border rounded-2xl p-6">
        <h3 className="font-display font-bold text-base mb-1">Credit Configuration</h3>
        <p className="text-xs text-muted-foreground mb-5">Set the credit cost for each AI action. Changes apply to all new operations.</p>
        <div className="grid grid-cols-2 gap-3 mb-5">
          {CREDIT_ACTIONS.map(({ key, label }) => (
            <div key={key} className="flex items-center justify-between gap-3 p-3 rounded-xl bg-muted/20 border border-border">
              <span className="text-xs text-foreground/70">{label}</span>
              <div className="flex items-center gap-1">
                <input type="number" value={weights[key] ?? DEFAULT_WEIGHTS[key]} onChange={e => setWeights(w => ({ ...w, [key]: Number(e.target.value) }))} className="w-14 text-xs text-center px-2 py-1.5 rounded-lg border border-border bg-background" min={1} data-testid={`input-credit-weight-${key}`} />
                <span className="text-[10px] text-muted-foreground">cr</span>
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="p-3 rounded-xl bg-muted/20 border border-border">
            <label className="text-[10px] text-muted-foreground block mb-1.5">Solo Allowance (cr/mo)</label>
            <input type="number" value={planAllowances.solo} onChange={e => setPlanAllowances(p => ({ ...p, solo: Number(e.target.value) }))} className="w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-background" data-testid="input-plan-allowance-solo" />
          </div>
          <div className="p-3 rounded-xl bg-muted/20 border border-border">
            <label className="text-[10px] text-muted-foreground block mb-1.5">Growth Allowance (cr/mo)</label>
            <input type="number" value={planAllowances.growth} onChange={e => setPlanAllowances(p => ({ ...p, growth: Number(e.target.value) }))} className="w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-background" data-testid="input-plan-allowance-growth" />
          </div>
          <div className="p-3 rounded-xl bg-muted/20 border border-border">
            <label className="text-[10px] text-muted-foreground block mb-1.5">Overage Rate ($/cr)</label>
            <input type="number" step="0.01" value={overageRate} onChange={e => setOverageRate(Number(e.target.value))} className="w-full text-xs px-2 py-1.5 rounded-lg border border-border bg-background" data-testid="input-overage-rate" />
          </div>
        </div>
        <button onPointerDown={saveConfig} disabled={configSaving} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-60" data-testid="button-save-credit-config">
          {configSaving ? <Loader2 size={14} className="animate-spin" /> : configSaved ? <Check size={14} /> : <Save size={14} />}
          {configSaved ? "Saved!" : "Save Configuration"}
        </button>
      </div>
    </div>
  );
}

function CreditsBillingTab() {
  return (
    <div className="space-y-8" data-testid="tab-credits-billing">
      <CreditsBillingMonitor />
      <CreditsBillingConfig />
    </div>
  );
}

function ChaosPoolsTab() {
  const [overrides, setOverrides] = useState<{ groupResults: Record<string, string>; knockoutWinners: Record<string, string> } | null>(null);
  const [loading, setLoading] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [rescoreResult, setRescoreResult] = useState<string | null>(null);
  const [groupInput, setGroupInput] = useState(""); // matchId:result per line
  const [knockoutInput, setKnockoutInput] = useState(""); // slotId:teamId per line
  const [savingGroup, setSavingGroup] = useState(false);
  const [savingKnockout, setSavingKnockout] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [resolvingBracket, setResolvingBracket] = useState(false);
  const [bracketResult, setBracketResult] = useState<{ slotsResolved: number; groupResultsCount: number; poolsUpdated: number; emailsSent: number; r32Slots: any[] } | null>(null);

  const [overview, setOverview] = useState<{
    participantCount: number;
    participantCap: number;
    totalPools: number;
    phase1Count: number;
    phase2Count: number;
  } | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [pools, setPools] = useState<any[]>([]);
  const [poolsLoading, setPoolsLoading] = useState(true);
  const [poolSearch, setPoolSearch] = useState("");

  async function fetchOverview() {
    setOverviewLoading(true);
    try {
      const r = await fetch("/api/saas-admin/pools/overview", { credentials: "include" });
      if (r.ok) setOverview(await r.json());
    } catch {} finally {
      setOverviewLoading(false);
    }
  }

  async function fetchPools() {
    setPoolsLoading(true);
    try {
      const r = await fetch("/api/saas-admin/pools/list", { credentials: "include" });
      if (r.ok) setPools(await r.json());
    } catch {} finally {
      setPoolsLoading(false);
    }
  }

  async function fetchOverrides() {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch("/api/saas-admin/pools/overrides", { credentials: "include" });
      if (!r.ok) throw new Error(await r.text());
      setOverrides(await r.json());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchOverrides(); fetchOverview(); fetchPools(); }, []);

  async function resolveBracket() {
    setResolvingBracket(true);
    setBracketResult(null);
    setError(null);
    try {
      const r = await fetch("/api/saas-admin/pools/resolve-bracket", {
        method: "POST",
        credentials: "include",
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      setBracketResult(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setResolvingBracket(false);
    }
  }

  async function saveGroupResults() {
    setSavingGroup(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const lines = groupInput.trim().split("\n").filter(Boolean);
      const results = lines.map((line) => {
        const [matchId, result] = line.split(":").map((s) => s.trim());
        return { matchId, result };
      });
      const r = await fetch("/api/saas-admin/pools/match-results", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ results }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      setSuccessMsg(`Set ${data.set} group result(s).`);
      setOverrides(data.overrides);
      setGroupInput("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingGroup(false);
    }
  }

  async function clearGroupResults() {
    setSavingGroup(true);
    setError(null);
    try {
      const r = await fetch("/api/saas-admin/pools/match-results", { method: "DELETE", credentials: "include" });
      const data = await r.json();
      setOverrides(data.overrides);
      setSuccessMsg("Group result overrides cleared.");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingGroup(false);
    }
  }

  async function saveKnockoutWinners() {
    setSavingKnockout(true);
    setError(null);
    setSuccessMsg(null);
    try {
      const lines = knockoutInput.trim().split("\n").filter(Boolean);
      const winners = lines.map((line) => {
        const [slotId, teamId] = line.split(":").map((s) => s.trim());
        return { slotId, teamId };
      });
      const r = await fetch("/api/saas-admin/pools/knockout-winners", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ winners }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      setSuccessMsg(`Set ${data.set} knockout winner(s).`);
      setOverrides(data.overrides);
      setKnockoutInput("");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingKnockout(false);
    }
  }

  async function clearKnockoutWinners() {
    setSavingKnockout(true);
    setError(null);
    try {
      const r = await fetch("/api/saas-admin/pools/knockout-winners", { method: "DELETE", credentials: "include" });
      const data = await r.json();
      setOverrides(data.overrides);
      setSuccessMsg("Knockout winner overrides cleared.");
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSavingKnockout(false);
    }
  }

  async function triggerRescore() {
    setRescoring(true);
    setError(null);
    setRescoreResult(null);
    try {
      const r = await fetch("/api/saas-admin/pools/rescore", { method: "POST", credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      setRescoreResult(`Rescore complete in ${data.durationMs}ms.`);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setRescoring(false);
    }
  }

  const groupCount = overrides ? Object.keys(overrides.groupResults).length : 0;
  const knockoutCount = overrides ? Object.keys(overrides.knockoutWinners).length : 0;

  const [scoringStatus, setScoringStatus] = useState<{
    currentlyRunning: boolean;
    lastRunAt: string | null;
    lastError: string | null;
    runCount: number;
    consecutiveErrors: number;
    feed: { degraded: boolean; degradedAt: string | null; degradedReason: string | null; lastGoodAt: string | null };
  } | null>(null);

  async function fetchScoringStatus() {
    try {
      const r = await fetch("/api/saas-admin/pools/scoring-status", { credentials: "include" });
      if (r.ok) setScoringStatus(await r.json());
    } catch {}
  }

  useEffect(() => { fetchScoringStatus(); }, []);

  const filteredPools = pools.filter((p) => {
    const q = poolSearch.toLowerCase();
    if (!q) return true;
    return (
      (p.name ?? "").toLowerCase().includes(q) ||
      (p.inviteCode ?? "").toLowerCase().includes(q) ||
      (p.commissionerName ?? "").toLowerCase().includes(q) ||
      (p.commissionerEmail ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-foreground">Chaos Pools</h2>
          <p className="text-sm text-foreground/40 mt-1">Global participation monitoring, pool directory, and Gap Day controls.</p>
        </div>
        <button
          onPointerDown={() => { fetchOverrides(); fetchScoringStatus(); fetchOverview(); fetchPools(); }}
          disabled={loading || overviewLoading || poolsLoading}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-foreground/60 hover:text-foreground hover:bg-white/10 transition-all"
          data-testid="button-refresh-overrides"
        >
          {(loading || overviewLoading || poolsLoading) ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {/* ── Global Monitoring Stats ───────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="chaos-pools-overview">
        <div className="p-5 rounded-2xl bg-blue-500/[0.07] border border-blue-500/15">
          <p className="text-[10px] font-bold text-blue-400/70 uppercase tracking-widest mb-1">Participants</p>
          {overviewLoading ? (
            <Loader2 size={18} className="animate-spin text-foreground/20 my-1" />
          ) : (
            <>
              <p className="text-2xl font-black text-foreground" data-testid="stat-participant-count">
                {overview?.participantCount?.toLocaleString() ?? "—"} <span className="text-sm font-normal text-foreground/30">/ {overview?.participantCap?.toLocaleString()}</span>
              </p>
              {overview && (
                <div className="mt-2 w-full bg-foreground/[0.06] rounded-full h-1.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${(overview.participantCount / overview.participantCap) >= 0.9 ? "bg-red-400" : "bg-blue-400"}`}
                    style={{ width: `${Math.min(100, (overview.participantCount / overview.participantCap) * 100)}%` }}
                  />
                </div>
              )}
              <p className="text-[10px] text-foreground/30 mt-1">of {overview?.participantCap?.toLocaleString()} cap</p>
            </>
          )}
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-1">Total Pools</p>
          {overviewLoading ? (
            <Loader2 size={18} className="animate-spin text-foreground/20 my-1" />
          ) : (
            <p className="text-2xl font-black text-foreground" data-testid="stat-total-pools">{overview?.totalPools ?? "—"}</p>
          )}
          <p className="text-[10px] text-foreground/30 mt-1">created</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-1">Phase 1</p>
          {overviewLoading ? (
            <Loader2 size={18} className="animate-spin text-foreground/20 my-1" />
          ) : (
            <p className="text-2xl font-black text-foreground" data-testid="stat-phase1-count">{overview?.phase1Count ?? "—"}</p>
          )}
          <p className="text-[10px] text-foreground/30 mt-1">group stage</p>
        </div>
        <div className="p-5 rounded-2xl bg-emerald-500/[0.06] border border-emerald-500/15">
          <p className="text-[10px] font-bold text-emerald-400/70 uppercase tracking-widest mb-1">Phase 2</p>
          {overviewLoading ? (
            <Loader2 size={18} className="animate-spin text-foreground/20 my-1" />
          ) : (
            <p className="text-2xl font-black text-foreground" data-testid="stat-phase2-count">{overview?.phase2Count ?? "—"}</p>
          )}
          <p className="text-[10px] text-foreground/30 mt-1">knockout bracket</p>
        </div>
      </div>

      {/* ── All Pools Table ───────────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/[0.06] overflow-hidden" data-testid="chaos-pools-table">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-bold text-foreground/70">All Pools</h3>
          <div className="relative flex-1 max-w-xs">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground/30" />
            <input
              value={poolSearch}
              onChange={(e) => setPoolSearch(e.target.value)}
              placeholder="Search pools…"
              className="w-full pl-8 pr-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-foreground placeholder:text-foreground/25 focus:outline-none focus:border-blue-500/50"
              data-testid="input-pool-search"
            />
          </div>
        </div>
        {poolsLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 size={20} className="animate-spin text-foreground/20" />
          </div>
        ) : filteredPools.length === 0 ? (
          <div className="py-10 text-center text-sm text-foreground/30">
            {poolSearch ? "No pools match your search." : "No pools created yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-white/[0.06]">
                  <th className="text-left px-5 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Pool Name</th>
                  <th className="text-left px-4 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Commissioner</th>
                  <th className="text-center px-4 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Members</th>
                  <th className="text-center px-4 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Phase</th>
                  <th className="text-left px-4 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Prize</th>
                  <th className="text-left px-4 py-2.5 text-foreground/30 font-semibold uppercase tracking-wider">Invite Code</th>
                </tr>
              </thead>
              <tbody>
                {filteredPools.map((pool: any, i: number) => (
                  <tr
                    key={pool.id}
                    className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors ${i === filteredPools.length - 1 ? "border-b-0" : ""}`}
                    data-testid={`row-pool-${pool.id}`}
                  >
                    <td className="px-5 py-3 text-foreground/80 font-medium max-w-[180px] truncate">{pool.name || <span className="text-foreground/25 italic">Unnamed</span>}</td>
                    <td className="px-4 py-3 max-w-[160px]">
                      <div className="truncate text-foreground/60">{pool.commissionerName || <span className="text-foreground/25">—</span>}</div>
                      {pool.commissionerEmail && <div className="truncate text-foreground/30 text-[10px]">{pool.commissionerEmail}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-foreground/70 font-semibold">{pool.memberCount ?? 0}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md uppercase ${pool.phase2UnlockedAt ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-white/5 text-foreground/40 border border-white/10"}`}>
                        {pool.phase2UnlockedAt ? "Phase 2" : "Phase 1"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground/50 max-w-[160px] truncate">{pool.prizeDescription || <span className="text-foreground/20 italic">—</span>}</td>
                    <td className="px-4 py-3">
                      <span className="font-mono text-blue-400/80 bg-blue-500/10 border border-blue-500/15 rounded px-2 py-0.5 text-[11px] select-all" data-testid={`text-invite-code-${pool.id}`}>{pool.inviteCode || "—"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scoring & Feed Health Status */}
      {scoringStatus && (
        <div className={`rounded-xl border p-4 space-y-2 ${scoringStatus.consecutiveErrors >= 3 || scoringStatus.feed.degraded ? "border-red-500/30 bg-red-500/8" : "border-white/10 bg-white/4"}`}>
          <div className="flex items-center gap-2 mb-1">
            <Activity size={14} className={scoringStatus.consecutiveErrors >= 3 ? "text-red-400" : "text-foreground/50"} />
            <p className="text-xs font-bold text-foreground/70 uppercase tracking-widest">Scoring &amp; Feed Health</p>
            {scoringStatus.currentlyRunning && <span className="text-[10px] text-amber-400 font-semibold animate-pulse">● RUNNING</span>}
            {scoringStatus.consecutiveErrors >= 3 && <span className="text-[10px] text-red-400 font-semibold">⚠ {scoringStatus.consecutiveErrors} CONSECUTIVE ERRORS</span>}
            {scoringStatus.feed.degraded && !scoringStatus.consecutiveErrors && <span className="text-[10px] text-amber-400 font-semibold">⚠ FEED DEGRADED</span>}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs text-foreground/60">
            <span>Last scoring run: <span className="text-foreground/80 font-mono">{scoringStatus.lastRunAt ? new Date(scoringStatus.lastRunAt).toLocaleString() : "never"}</span></span>
            <span>Total runs: <span className="text-foreground/80">{scoringStatus.runCount}</span></span>
            <span>Feed last good: <span className="text-foreground/80 font-mono">{scoringStatus.feed.lastGoodAt ? new Date(scoringStatus.feed.lastGoodAt).toLocaleString() : "n/a"}</span></span>
            <span>Feed degraded: <span className={scoringStatus.feed.degraded ? "text-red-400 font-semibold" : "text-green-400"}>{scoringStatus.feed.degraded ? "YES" : "NO"}</span></span>
          </div>
          {scoringStatus.lastError && <p className="text-xs text-red-400 mt-1 font-mono break-all">Last error: {scoringStatus.lastError}</p>}
          {scoringStatus.feed.degradedReason && <p className="text-xs text-amber-400 font-mono break-all">Feed issue: {scoringStatus.feed.degradedReason}</p>}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <AlertCircle size={16} /> {error}
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm">
          <Check size={16} /> {successMsg}
        </div>
      )}

      {/* Current Overrides Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-1">Group Results</p>
          <p className="text-2xl font-black text-foreground">{groupCount}</p>
          <p className="text-xs text-foreground/40 mt-1">manual overrides active</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
          <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-1">Knockout Winners</p>
          <p className="text-2xl font-black text-foreground">{knockoutCount}</p>
          <p className="text-xs text-foreground/40 mt-1">manual overrides active</p>
        </div>
        <div className="p-5 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex flex-col justify-between">
          <div>
            <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-1">Force Rescore</p>
            <p className="text-xs text-foreground/40 mb-3">Recalculates all pool scores across all pools immediately.</p>
          </div>
          <button
            onPointerDown={triggerRescore}
            disabled={rescoring}
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600/20 border border-blue-500/30 text-blue-400 text-sm font-bold hover:bg-blue-600/30 transition-all"
            data-testid="button-trigger-rescore"
          >
            {rescoring ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {rescoring ? "Rescoring..." : "Run Scoring Job"}
          </button>
          {rescoreResult && <p className="text-xs text-emerald-400 mt-2">{rescoreResult}</p>}
        </div>
      </div>

      {/* Gap Day — Resolve Bracket */}
      <div className="p-6 rounded-2xl bg-emerald-500/[0.05] border border-emerald-500/20">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-sm font-bold text-emerald-400 mb-1">⚽ Gap Day — Resolve Bracket</h3>
            <p className="text-xs text-foreground/40">
              Run this once all 72 group matches are final (June 28). Computes standings for all 12 groups, resolves the 32 R32 matchups to real qualified teams, stamps <code className="text-foreground/60 font-mono">phase2UnlockedAt</code> on all pools, and queues Gap Day emails to every pool member. Safe to run multiple times.
            </p>
          </div>
          <button
            onPointerDown={resolveBracket}
            disabled={resolvingBracket}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald-600/20 border border-emerald-500/40 text-emerald-400 text-sm font-bold hover:bg-emerald-600/30 transition-all disabled:opacity-50 whitespace-nowrap flex-shrink-0"
            data-testid="button-resolve-bracket"
          >
            {resolvingBracket ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
            {resolvingBracket ? "Resolving..." : "Resolve Bracket"}
          </button>
        </div>

        {bracketResult && (
          <div className="mt-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                <p className="text-lg font-black text-emerald-400">{bracketResult.slotsResolved}/16</p>
                <p className="text-[10px] text-foreground/40 mt-0.5">R32 slots resolved</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                <p className="text-lg font-black text-foreground">{bracketResult.groupResultsCount}</p>
                <p className="text-[10px] text-foreground/40 mt-0.5">group results used</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                <p className="text-lg font-black text-foreground">{bracketResult.poolsUpdated}</p>
                <p className="text-[10px] text-foreground/40 mt-0.5">pools stamped</p>
              </div>
              <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] text-center">
                <p className="text-lg font-black text-foreground">{bracketResult.emailsSent}</p>
                <p className="text-[10px] text-foreground/40 mt-0.5">emails queued</p>
              </div>
            </div>
            <div>
              <p className="text-[10px] font-bold text-foreground/30 uppercase tracking-widest mb-2">Resolved R32 Matchups</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {bracketResult.r32Slots.map((s: any) => (
                  <div key={s.slot} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-mono ${s.resolved ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-white/[0.02] border border-white/[0.06] opacity-50"}`}>
                    <span className="text-foreground/40 w-14 flex-shrink-0">{s.slot}</span>
                    {s.resolved ? (
                      <span className="text-foreground/80">
                        {s.homeTeam?.flag} {s.homeTeam?.name} <span className="text-foreground/30">vs</span> {s.awayTeam?.flag} {s.awayTeam?.name}
                      </span>
                    ) : (
                      <span className="text-foreground/30">{s.homeSlotDesc} vs {s.awaySlotDesc}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Group Results Entry */}
      <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
        <h3 className="text-sm font-bold text-foreground mb-1">Group Stage Results</h3>
        <p className="text-xs text-foreground/40 mb-4">
          Enter one result per line as <code className="text-blue-400 font-mono">matchId:result</code> where result is <code className="text-emerald-400 font-mono">H</code> (home win), <code className="text-emerald-400 font-mono">D</code> (draw), or <code className="text-emerald-400 font-mono">A</code> (away win).
          Match IDs follow the format <code className="text-foreground/60 font-mono">A-1</code>, <code className="text-foreground/60 font-mono">B-3</code>, etc. (group letter + match number 1–6).
        </p>
        <textarea
          value={groupInput}
          onChange={(e) => setGroupInput(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-foreground font-mono focus:border-blue-500 outline-none resize-none mb-3"
          rows={6}
          placeholder={"A-1:H\nA-2:D\nB-1:A\n..."}
          data-testid="input-group-results"
        />
        {overrides && groupCount > 0 && (
          <div className="mb-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs font-bold text-amber-400 mb-2">Active overrides ({groupCount}):</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(overrides.groupResults).map(([matchId, result]) => (
                <span key={matchId} className="text-[10px] font-mono bg-white/5 border border-white/10 rounded px-2 py-0.5 text-foreground/60">
                  {matchId}:{result}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <button
            onPointerDown={saveGroupResults}
            disabled={savingGroup || !groupInput.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-bold hover:bg-emerald-600/30 transition-all disabled:opacity-40"
            data-testid="button-save-group-results"
          >
            {savingGroup ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Results
          </button>
          <button
            onPointerDown={clearGroupResults}
            disabled={savingGroup || groupCount === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold hover:bg-red-500/20 transition-all disabled:opacity-40"
            data-testid="button-clear-group-results"
          >
            <Trash2 size={13} /> Clear All
          </button>
        </div>
      </div>

      {/* Knockout Winners Entry */}
      <div className="p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06]">
        <h3 className="text-sm font-bold text-foreground mb-1">Knockout Match Winners</h3>
        <p className="text-xs text-foreground/40 mb-4">
          Enter one winner per line as <code className="text-blue-400 font-mono">slotId:teamId</code>.
          Slot IDs: <code className="text-foreground/60 font-mono">R32-1</code> through <code className="text-foreground/60 font-mono">R32-16</code>, <code className="text-foreground/60 font-mono">R16-1</code> through <code className="text-foreground/60 font-mono">R16-8</code>, <code className="text-foreground/60 font-mono">QF-1</code>–<code className="text-foreground/60 font-mono">QF-4</code>, <code className="text-foreground/60 font-mono">SF-1</code>–<code className="text-foreground/60 font-mono">SF-2</code>, <code className="text-foreground/60 font-mono">THIRD-1</code>, <code className="text-foreground/60 font-mono">FINAL-1</code>.
          Team IDs match the 3-letter codes (e.g. <code className="text-foreground/60 font-mono">BRA</code>, <code className="text-foreground/60 font-mono">ESP</code>).
        </p>
        <textarea
          value={knockoutInput}
          onChange={(e) => setKnockoutInput(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-foreground font-mono focus:border-blue-500 outline-none resize-none mb-3"
          rows={6}
          placeholder={"R32-1:BRA\nR32-2:ESP\n..."}
          data-testid="input-knockout-winners"
        />
        {overrides && knockoutCount > 0 && (
          <div className="mb-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs font-bold text-amber-400 mb-2">Active overrides ({knockoutCount}):</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(overrides.knockoutWinners).map(([slotId, teamId]) => (
                <span key={slotId} className="text-[10px] font-mono bg-white/5 border border-white/10 rounded px-2 py-0.5 text-foreground/60">
                  {slotId}:{teamId}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-3">
          <button
            onPointerDown={saveKnockoutWinners}
            disabled={savingKnockout || !knockoutInput.trim()}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600/20 border border-emerald-500/30 text-emerald-400 text-sm font-bold hover:bg-emerald-600/30 transition-all disabled:opacity-40"
            data-testid="button-save-knockout-winners"
          >
            {savingKnockout ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save Winners
          </button>
          <button
            onPointerDown={clearKnockoutWinners}
            disabled={savingKnockout || knockoutCount === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-bold hover:bg-red-500/20 transition-all disabled:opacity-40"
            data-testid="button-clear-knockout-winners"
          >
            <Trash2 size={13} /> Clear All
          </button>
        </div>
      </div>

      {/* Instructions */}
      <div className="p-5 rounded-2xl bg-amber-500/[0.06] border border-amber-500/10">
        <h3 className="text-sm font-bold text-amber-400 mb-2">Gap Day Playbook (June 28, 2026)</h3>
        <ol className="text-xs text-foreground/50 space-y-1.5 list-decimal list-inside">
          <li>After all group matches are final, enter the 72 group results above (or let the football-data.org API do it automatically if <code className="text-foreground/60 font-mono">FOOTBALL_DATA_API_KEY</code> is set).</li>
          <li>Click <strong className="text-foreground/70">Run Scoring Job</strong> to immediately recalculate Phase 1 scores and unlock bracket slots with real qualified teams.</li>
          <li>The bracket picks window opens automatically once <code className="text-foreground/60 font-mono">isPhase2Unlocked()</code> returns true (i.e. UTC midnight June 28).</li>
          <li>As R32 matches are played (June 28+), enter knockout winners above. Run scoring job after each matchday to update leaderboards in real-time.</li>
          <li>Manual overrides always take precedence over the live API — use Clear All to revert once the API is catching up correctly.</li>
        </ol>
      </div>
    </div>
  );
}

function WebsiteTab() {
  const [pages, setPages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [triggeringBrief, setTriggeringBrief] = useState(false);
  const [briefResult, setBriefResult] = useState<string | null>(null);

  const triggerDailyBrief = async () => {
    setTriggeringBrief(true);
    setBriefResult(null);
    try {
      const res = await fetch("/api/saas-admin/trigger-daily-brief", { method: "POST", credentials: "include" });
      const data = await res.json();
      if (res.ok) {
        setBriefResult("Brief generation started! Check back in ~60 seconds and refresh to see the new post.");
        setTimeout(() => fetchPages(), 90000);
      } else {
        setBriefResult(`Failed: ${data.error || data.message || "Unknown error"}`);
      }
    } catch (e: any) {
      setBriefResult(`Error: ${e.message}`);
    } finally {
      setTriggeringBrief(false);
    }
  };

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/saas-admin/site-pages", { credentials: "include" });
      if (res.ok) setPages(await res.json());
    } catch (e) { console.error("Failed to fetch pages", e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchPages(); }, [fetchPages]);

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this page?")) return;
    try {
      await fetch(`/api/saas-admin/site-pages/${id}`, { method: "DELETE", credentials: "include" });
      fetchPages();
    } catch (e) { console.error("Failed to delete page", e); }
  };

  const openEditor = async (page: any = null) => {
    if (page?.id) {
      try {
        const res = await fetch(`/api/saas-admin/site-pages/${page.id}`, { credentials: "include" });
        if (res.ok) {
          setEditingPage(await res.json());
          setShowEditor(true);
          return;
        }
      } catch (e) { console.error("Failed to load page for editing", e); }
    }
    setEditingPage(page || {
      title: "",
      slug: "",
      type: "blog",
      content: "",
      coverUrl: "",
      author: "Admin",
      tags: [],
      featured: false,
      seoTitle: "",
      seoDescription: "",
      status: "draft"
    });
    setShowEditor(true);
  };

  return (
    <div className="space-y-6" data-testid="website-tab">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-display font-bold text-foreground">Website CMS</h2>
          <p className="text-sm text-foreground/30 mt-1">Manage blog posts and public site pages.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={triggerDailyBrief}
            disabled={triggeringBrief}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600/20 border border-emerald-500/30 hover:bg-emerald-600/30 text-emerald-400 rounded-xl text-sm font-bold transition-all disabled:opacity-50"
            data-testid="button-trigger-daily-brief"
          >
            {triggeringBrief ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} />}
            {triggeringBrief ? "Generating..." : "Draper's Daily Brief"}
          </button>
          <button
            onPointerDown={() => openEditor()}
            className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-foreground rounded-xl text-sm font-bold transition-all"
            data-testid="button-create-page"
          >
            <Plus size={16} />
            Create Page
          </button>
        </div>
      </div>

      {briefResult && (
        <div className={`px-4 py-3 rounded-xl text-sm border ${briefResult.startsWith("Failed") || briefResult.startsWith("Error") ? "bg-red-500/10 border-red-500/20 text-red-400" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"}`} data-testid="brief-result-message">
          {briefResult}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-foreground/20" size={24} /></div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {pages.map((page) => (
            <div key={page.id} className="bg-foreground/[0.02] border border-foreground/[0.06] rounded-2xl p-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {page.coverUrl && (
                  <img src={page.coverUrl} className="w-12 h-12 rounded-lg object-cover bg-foreground/10" alt="" />
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-bold text-foreground">{page.title}</h3>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md border ${
                      page.status === 'published' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                    }`}>
                      {page.status}
                    </span>
                    <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-md bg-blue-500/10 text-blue-400 border border-blue-500/20">
                      {page.type}
                    </span>
                  </div>
                  <p className="text-[10px] text-foreground/30 font-mono">/${page.type === 'blog' ? 'blog' : 'page'}/${page.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`/${page.type === 'blog' ? 'blog' : 'page'}/${page.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-foreground/30 hover:text-foreground/60 transition-colors">
                  <Eye size={16} />
                </a>
                <button onPointerDown={() => openEditor(page)} className="p-2 text-foreground/30 hover:text-blue-400 transition-colors">
                  <Pencil size={16} />
                </button>
                <button onPointerDown={() => handleDelete(page.id)} className="p-2 text-foreground/30 hover:text-red-400 transition-colors">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
          {pages.length === 0 && (
            <div className="text-center py-20 border border-dashed border-foreground/10 rounded-2xl">
              <p className="text-foreground/20 italic">No pages created yet.</p>
            </div>
          )}
        </div>
      )}

      {showEditor && (
        <PageEditor 
          page={editingPage} 
          onClose={() => setShowEditor(false)} 
          onSave={() => { setShowEditor(false); fetchPages(); }} 
        />
      )}
    </div>
  );
}

function PageEditor({ page, onClose, onSave }: { page: any, onClose: () => void, onSave: () => void }) {
  const [form, setForm] = useState({ ...page, tags: Array.isArray(page.tags) ? page.tags.join(", ") : "" });
  const [saving, setSaving] = useState(false);

  const handleTitleChange = (title: string) => {
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    setForm({ ...form, title, slug });
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t: string) => t.trim()).filter(Boolean),
        publishedAt: form.status === 'published' && !page.publishedAt ? new Date().toISOString() : page.publishedAt
      };
      
      const url = page.id ? `/api/saas-admin/site-pages/${page.id}` : "/api/saas-admin/site-pages";
      const method = page.id ? "PUT" : "POST";
      
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        credentials: "include"
      });
      
      if (res.ok) onSave();
    } catch (e) { console.error("Failed to save page", e); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="page-editor-modal">
      <div className="bg-[#0c0c12] border border-white/10 w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col shadow-2xl">
        <header className="p-6 border-b border-white/5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-white">{page.id ? "Edit Page" : "New Page"}</h3>
          <button onPointerDown={onClose} className="text-white/40 hover:text-white transition-colors"><X size={20} /></button>
        </header>
        
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Title</label>
                <input 
                  type="text" 
                  value={form.title} 
                  onChange={(e) => handleTitleChange(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none"
                  placeholder="Page Title"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Slug</label>
                <input 
                  type="text" 
                  value={form.slug} 
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none font-mono"
                  placeholder="url-slug"
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Type</label>
                <select 
                  value={form.type} 
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none appearance-none"
                >
                  <option value="blog">Blog Post</option>
                  <option value="page">Standard Page</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Author</label>
                <input 
                  type="text" 
                  value={form.author} 
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none"
                />
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Cover Image URL</label>
                <input 
                  type="text" 
                  value={form.coverUrl} 
                  onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Tags (comma separated)</label>
                <input 
                  type="text" 
                  value={form.tags} 
                  onChange={(e) => setForm({ ...form, tags: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none"
                  placeholder="ai, marketing, automation"
                />
              </div>
              <div className="flex items-center gap-6 pt-2">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="checkbox" 
                    checked={form.featured} 
                    onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                    className="w-4 h-4 rounded border-white/10 bg-white/5 accent-blue-500"
                  />
                  <span className="text-sm font-medium text-white/60 group-hover:text-white transition-colors">Featured Post</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="radio" 
                    name="status" 
                    checked={form.status === 'draft'} 
                    onChange={() => setForm({ ...form, status: 'draft' })}
                    className="accent-amber-500"
                  />
                  <span className="text-sm font-medium text-white/60 group-hover:text-white transition-colors">Draft</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input 
                    type="radio" 
                    name="status" 
                    checked={form.status === 'published'} 
                    onChange={() => setForm({ ...form, status: 'published' })}
                    className="accent-emerald-500"
                  />
                  <span className="text-sm font-medium text-white/60 group-hover:text-white transition-colors">Published</span>
                </label>
              </div>
            </div>
          </div>
          
          <div>
            <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">Markdown Content</label>
            <textarea 
              value={form.content} 
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none min-h-[300px] font-mono"
              placeholder="Write your content here..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-white/[0.02] border border-white/5 rounded-2xl">
            <div>
              <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">SEO Meta Title</label>
              <input 
                type="text" 
                value={form.seoTitle} 
                onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white focus:border-blue-500 outline-none"
              />
            </div>
            <div>
              <label className="text-[10px] font-bold text-white/30 uppercase tracking-widest block mb-1.5">SEO Meta Description</label>
              <textarea 
                value={form.seoDescription} 
                onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm text-white focus:border-blue-500 outline-none resize-none"
                rows={2}
              />
            </div>
          </div>
        </div>
        
        <footer className="p-6 border-t border-white/5 flex justify-end gap-3">
          <Button variant="ghost" onPointerDown={onClose} className="text-white/40 hover:text-white">Cancel</Button>
          <Button 
            onPointerDown={handleSubmit} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-500 text-white min-w-[120px]"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : page.id ? "Update Page" : "Create Page"}
          </Button>
        </footer>
      </div>
    </div>
  );
}
